/**
 * Trading View Page with Advanced Market Data and Order Placement
 */

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { addToWatchlist, removeFromWatchlist } from '../redux/slices/watchlistSlice';
import { fetchTradeBook } from '../redux/slices/orderSlice';
import { store } from '../redux/store';
import aliceBlueWS from '../services/aliceBlueWebSocket';
import api from '../services/api';
import OrderPanel from '../components/OrderPanel';
import { useTheme } from '../contexts/ThemeContext';

const TradingView = () => {
  const dispatch = useDispatch();
  const watchlist = useSelector((state) => state.watchlist.items);
  const { userId, userSession } = useSelector((state) => state.auth);
  const { tradeBook } = useSelector((state) => state.orders);
  const { theme } = useTheme();
  
  const [livePrices, setLivePrices] = useState({});
  const [wsConnected, setWsConnected] = useState(false);
  const [wsStatus, setWsStatus] = useState('Initializing...');
  const [connectionError, setConnectionError] = useState(null);
  
  // Indices data with their instrument tokens
  const indicesData = {
    'NIFTY': { exchange: 'NSE', token: '26000' },     // NIFTY 50 index
    'BANKNIFTY': { exchange: 'NSE', token: '26009' }, // BANK NIFTY index  
    'SENSEX': { exchange: 'BSE', token: '1' }         // SENSEX index
  };
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem('tradingView_activeTab') || 'NFO';
  });
  const [nfoSubTab, setNfoSubTabState] = useState(() => {
    return sessionStorage.getItem('tradingView_nfoSubTab') || '';
  });

  // BFO Sub-tab selection (persisted in sessionStorage)
  const [bfoSubTab, setBfoSubTabState] = useState(() => {
    return sessionStorage.getItem('tradingView_bfoSubTab') || '';
  });

  // MCX Sub-tab selection (persisted in sessionStorage)
  const [mcxSubTab, setMcxSubTabState] = useState(() => {
    return sessionStorage.getItem('tradingView_mcxSubTab') || '';
  });
  
  // Wrapper to persist nfoSubTab
  const setNfoSubTab = (value) => {
    setNfoSubTabState(value);
    sessionStorage.setItem('tradingView_nfoSubTab', value || '');
  };

  // Wrapper to persist bfoSubTab
  const setBfoSubTab = (value) => {
    setBfoSubTabState(value);
    sessionStorage.setItem('tradingView_bfoSubTab', value || '');
  };

  // Wrapper to persist mcxSubTab
  const setMcxSubTab = (value) => {
    setMcxSubTabState(value);
    sessionStorage.setItem('tradingView_mcxSubTab', value || '');
  };

  // MCX Option Chain state
  const [mcxUnderlyingList, setMcxUnderlyingList] = useState([]);
  const [mcxSelectedUnderlying, setMcxSelectedUnderlying] = useState('');
  const [mcxExpiryList, setMcxExpiryList] = useState([]);
  const [mcxSelectedExpiry, setMcxSelectedExpiry] = useState('');
  const [mcxSelectedInterval, setMcxSelectedInterval] = useState('10');
  const [mcxOptionChainData, setMcxOptionChainData] = useState([]);
  const [mcxLoading, setMcxLoading] = useState(false);
  const [mcxError, setMcxError] = useState(null);
  const [mcxChainVisible, setMcxChainVisible] = useState(false);
  
  const [nfoSymbolsOrder, setNfoSymbolsOrder] = useState(() => {
    const saved = localStorage.getItem('tradingView_nfoSymbolsOrder');
    return saved ? JSON.parse(saved) : [];
  }); // Custom order for NFO symbols

  // BFO symbols ordering
  const [bfoSymbolsOrder, setBfoSymbolsOrder] = useState(() => {
    const saved = localStorage.getItem('tradingView_bfoSymbolsOrder');
    return saved ? JSON.parse(saved) : [];
  }); // Custom order for BFO symbols
  const [draggedSymbolId, setDraggedSymbolId] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null); // For tracking hovered table rows
  const [hoveredPosition, setHoveredPosition] = useState(null); // For tracking hovered position rows
  
  // Master symbols state
  const [masterSymbolsByExchange, setMasterSymbolsByExchange] = useState({});
  const [selectedMasterSymbol, setSelectedMasterSymbol] = useState({});
  
  // Order form state
  const [orderFormVisible, setOrderFormVisible] = useState(false);
  const [orderData, setOrderData] = useState({
    symbol: null,
    action: 'BUY',
    quantity: '',
    price: '',
    orderType: 'LIMIT',
    validity: 'DAY',
    product: 'MIS'
  });

  // Order Panel state
  const [isOrderPanelOpen, setIsOrderPanelOpen] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState(null);

  // Exchange-specific search states
  const [exchangeSearchQueries, setExchangeSearchQueries] = useState({});
  const [exchangeSearchResults, setExchangeSearchResults] = useState({});
  
  // Symbol filter state for options trading
  const [symbolFilters, setSymbolFilters] = useState({
    symbolName: '',
    expiryDate: '',
    optionType: '',
    strikePrice: '',
    exchange: activeTab
  });

  // Update symbolFilters.exchange when activeTab changes
  useEffect(() => {
    setSymbolFilters(prev => ({
      ...prev,
      exchange: activeTab
    }));
  }, [activeTab]);
  const [filterResults, setFilterResults] = useState({ symbols: [], count: 0 });
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [isFilterCollapsed, setIsFilterCollapsed] = useState(true);
  const [availableExpiries, setAvailableExpiries] = useState([]);
  const [availableStrikes, setAvailableStrikes] = useState([]);
  const [availableSymbols, setAvailableSymbols] = useState(['NIFTY', 'BANKNIFTY', 'FINNIFTY']);
  const [filteredSymbols, setFilteredSymbols] = useState([]);
  const [editableBaseStrike, setEditableBaseStrike] = useState('');
  
  // Order entry states with localStorage persistence
  const [selectedCEStrike, setSelectedCEStrikeState] = useState(() => {
    const saved = localStorage.getItem('quickOrder_selectedCEStrike');
    return saved ? JSON.parse(saved) : null;
  });
  const [selectedPEStrike, setSelectedPEStrikeState] = useState(() => {
    const saved = localStorage.getItem('quickOrder_selectedPEStrike');
    return saved ? JSON.parse(saved) : null;
  });
  const [orderType, setOrderType] = useState(() => {
    return localStorage.getItem('quickOrder_orderType') || 'LIMIT';
  });
  const [productType, setProductType] = useState(() => {
    return localStorage.getItem('quickOrder_productType') || 'NRML';
  });
  const [ceQty, setCEQtyState] = useState(() => {
    return localStorage.getItem('quickOrder_ceQty') || '';
  });
  const [peQty, setPEQtyState] = useState(() => {
    return localStorage.getItem('quickOrder_peQty') || '';
  });
  // Entry price states - captures LTP at double-tap time
  const [ceEntryPrice, setCEEntryPriceState] = useState(() => {
    const saved = localStorage.getItem('quickOrder_ceEntryPrice');
    return saved ? parseFloat(saved) : null;
  });
  const [peEntryPrice, setPEEntryPriceState] = useState(() => {
    const saved = localStorage.getItem('quickOrder_peEntryPrice');
    return saved ? parseFloat(saved) : null;
  });
  
  // Quantity popup state
  const [qtyPopup, setQtyPopup] = useState({
    show: false,
    optionType: '', // 'CE' or 'PE'
    item: null,
    defaultQty: '',
    currentQty: ''
  });
  
  const [positions, setPositions] = useState([]);
  const [depthData, setDepthData] = useState({}); // Depth data for next expiry
  
  // Collapse state for options tables section
  const [isOptionsTableCollapsed, setIsOptionsTableCollapsed] = useState(() => {
    const saved = localStorage.getItem('tradingView_optionsTableCollapsed');
    return saved === 'true';
  });
  
  // Collapse state for exchange tabs section
  const [isExchangeTabsCollapsed, setIsExchangeTabsCollapsed] = useState(() => {
    const saved = localStorage.getItem('tradingView_exchangeTabsCollapsed');
    return saved === 'true';
  });
  
  // Bull vs Bear export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState('');
  const [lastExportSessionId, setLastExportSessionId] = useState(null);
  
  // Flip table state
  const [isFlipped, setIsFlipped] = useState(true); // Default to flipped (difference) view
  const [exportedData, setExportedData] = useState([]);
  const [isFetchingExported, setIsFetchingExported] = useState(false);
  
  // Positions table flip state
  const [isPositionsFlipped, setIsPositionsFlipped] = useState(false); // false = positions, true = tradebook
  
  // Popup notification state
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [popupType, setPopupType] = useState('success'); // 'success' or 'error'
  const [confirmationModal, setConfirmationModal] = useState({
    show: false,
    title: '',
    message: '',
    details: {},
    editablePrice: null,
    onConfirm: null,
    onCancel: null
  });
  
  // Re Trade Modal State
  const [reTradeModal, setReTradeModal] = useState({
    show: false,
    position: null,
    action: 'BUY', // 'BUY' or 'SELL'
    editablePrice: null,
    editableQty: null
  });
  
  // Direction tracking and notification states
  const [ceDirection, setCEDirection] = useState(null); // 'up', 'down', or null
  const [peDirection, setPEDirection] = useState(null); // 'up', 'down', or null
  const [showDirectionNotification, setShowDirectionNotification] = useState(false);
  const [directionNotificationData, setDirectionNotificationData] = useState(null);
  const [disableNotificationsUntil, setDisableNotificationsUntil] = useState(() => {
    const saved = localStorage.getItem('tradingView_disableNotificationsUntil');
    return saved ? parseInt(saved) : null;
  });
  const [notificationsDismissed, setNotificationsDismissed] = useState({});
  
  // MTM tracking - baseline LTP sums from when "Analyse Options Chain" was clicked
  // Persisted in localStorage and also saved to database for cross-device support
  const [baselineCESum, setBaselineCESumState] = useState(() => {
    const saved = localStorage.getItem('tradingView_baselineCESum');
    return saved ? parseFloat(saved) : 0;
  });
  const [baselinePESum, setBaselinePESumState] = useState(() => {
    const saved = localStorage.getItem('tradingView_baselinePESum');
    return saved ? parseFloat(saved) : 0;
  });
  
  // Re-Enter functionality - track exported strength values
  const [exportedCEStrength, setExportedCEStrengthState] = useState(() => {
    const saved = localStorage.getItem('tradingView_exportedCEStrength');
    return saved ? parseFloat(saved) : 0;
  });
  const [exportedPEStrength, setExportedPEStrengthState] = useState(() => {
    const saved = localStorage.getItem('tradingView_exportedPEStrength');
    return saved ? parseFloat(saved) : 0;
  });
  
  const setExportedCEStrength = (value) => {
    setExportedCEStrengthState(value);
    localStorage.setItem('tradingView_exportedCEStrength', value.toString());
  };
  
  const setExportedPEStrength = (value) => {
    setExportedPEStrengthState(value);
    localStorage.setItem('tradingView_exportedPEStrength', value.toString());
  };
  
  const [nextExpiryDate, setNextExpiryDateState] = useState(() => {
    // Restore from localStorage on mount
    const saved = localStorage.getItem('tradingView_nextExpiryDate');
    return saved || null;
  });
  const [nextExpirySymbols, setNextExpirySymbolsState] = useState(() => {
    const saved = localStorage.getItem('tradingView_nextExpirySymbols');
    return saved ? JSON.parse(saved) : [];
  });

  // Re-Enter button handler - export strength values to database
  const handleReEnter = async (optionType) => {
    try {
      const selectedStrike = optionType === 'CE' ? selectedCEStrike : selectedPEStrike;
      
      if (!selectedStrike) {
        setPopupMessage(`No ${optionType} strike selected`);
        setPopupType('error');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
        return;
      }
      
      // Calculate current MTM value
      const { ceOptions, peOptions } = getOptionsWatchlist();
      
      let currentStrength = 0;
      if (optionType === 'CE') {
        const currentCESum = ceOptions.reduce((sum, option) => {
          const ltp = getLivePrice(option.exchange, option.instrumentId);
          return sum + (ltp || 0);
        }, 0);
        currentStrength = currentCESum - baselineCESum;
      } else {
        const currentPESum = peOptions.reduce((sum, option) => {
          const ltp = getLivePrice(option.exchange, option.instrumentId);
          return sum + (ltp || 0);
        }, 0);
        currentStrength = currentPESum - baselinePESum;
      }
      
      // Apply /10 formula to match displayed strength value
      const displayStrength = currentStrength / 10;
      
      // Export to database
      const response = await api.post('/re-enter/export', {
        userId,
        optionType,
        strike: selectedStrike.strike,
        symbol: selectedStrike.tradingSymbol,
        strengthValue: displayStrength,
        timestamp: new Date().toISOString()
      });
      
      if (response.data.success) {
        // Update exported strength value
        if (optionType === 'CE') {
          setExportedCEStrength(displayStrength);
        } else {
          setExportedPEStrength(displayStrength);
        }
        
        setPopupMessage(`${optionType} Strength (${displayStrength.toFixed(2)}) exported successfully`);
        setPopupType('success');
      } else {
        setPopupMessage(`Failed to export ${optionType} strength`);
        setPopupType('error');
      }
    } catch (error) {
      console.error('Re-Enter export error:', error);
      setPopupMessage(`Error exporting strength: ${error.message}`);
      setPopupType('error');
    }
    
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 3000);
  };

  // Helper function to get dynamic button styles
  const getButtonStyle = (type) => {
    const baseStyle = "text-white w-full h-8 rounded text-[10px] disabled:opacity-50 font-semibold transition-colors";
    const color = type === 'buy' ? theme.colors.buy : theme.colors.sell;
    
    // Convert hex to rgba for hover effect
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };
    
    const rgb = hexToRgb(color);
    const hoverColor = rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8)` : color;
    
    return {
      className: baseStyle,
      style: {
        backgroundColor: color,
      },
      onMouseEnter: (e) => {
        e.target.style.backgroundColor = hoverColor;
      },
      onMouseLeave: (e) => {
        e.target.style.backgroundColor = color;
      }
    };
  };

  // Restore selectedChainInfo and related states from localStorage (persists across logout/login)
  const [selectedChainInfo, setSelectedChainInfoState] = useState(() => {
    const saved = localStorage.getItem('tradingView_selectedChainInfo');
    return saved ? JSON.parse(saved) : null;
  });

  // Wrapper to persist selectedChainInfo
  const setSelectedChainInfo = (value) => {
    setSelectedChainInfoState(value);
    if (value) {
      localStorage.setItem('tradingView_selectedChainInfo', JSON.stringify(value));
    } else {
      localStorage.removeItem('tradingView_selectedChainInfo');
    }
  };

  // Persist activeTab when it changes
  useEffect(() => {
    sessionStorage.setItem('tradingView_activeTab', activeTab);
  }, [activeTab]);
  
  // Cleanup expired notification disable timer
  useEffect(() => {
    if (disableNotificationsUntil) {
      const now = Date.now();
      if (now >= disableNotificationsUntil) {
        setDisableNotificationsUntil(null);
        localStorage.removeItem('tradingView_disableNotificationsUntil');
      }
    }
  }, [disableNotificationsUntil]);

  // Wrapper functions for Quick Order Entry state persistence
  const setSelectedCEStrike = (value) => {
    setSelectedCEStrikeState(value);
    if (value) {
      localStorage.setItem('quickOrder_selectedCEStrike', JSON.stringify(value));
      // Only set default quantity if input is completely empty
      if (!ceQty || ceQty === '') {
        const lotSize = value.lotSize || 25;
        setCEQty(lotSize.toString());
      }
    } else {
      localStorage.removeItem('quickOrder_selectedCEStrike');
    }
  };

  const setSelectedPEStrike = (value) => {
    setSelectedPEStrikeState(value);
    if (value) {
      localStorage.setItem('quickOrder_selectedPEStrike', JSON.stringify(value));
      // Only set default quantity if input is completely empty
      if (!peQty || peQty === '') {
        const lotSize = value.lotSize || 25;
        setPEQty(lotSize.toString());
      }
    } else {
      localStorage.removeItem('quickOrder_selectedPEStrike');
    }
  };

  const setCEQty = (value) => {
    // Allow free editing - no automatic validation
    setCEQtyState(value);
    localStorage.setItem('quickOrder_ceQty', value);
  };

  const setPEQty = (value) => {
    // Allow free editing - no automatic validation
    setPEQtyState(value);
    localStorage.setItem('quickOrder_peQty', value);
  };

  // Validate and auto-correct quantity based on lot size
  const handleQtyBlur = (optionType) => {
    const selectedStrike = optionType === 'CE' ? selectedCEStrike : selectedPEStrike;
    const currentQty = optionType === 'CE' ? ceQty : peQty;
    const setQty = optionType === 'CE' ? setCEQty : setPEQty;
    
    if (!selectedStrike || !currentQty) return;
    
    const enteredQty = parseInt(currentQty) || 0;
    const lotSize = selectedStrike.lotSize || 25;
    
    // If entered qty is less than lot size, set it to lot size
    if (enteredQty > 0 && enteredQty < lotSize) {
      setQty(lotSize.toString());
    }
  };

  // Wrapper functions for Entry Price state persistence
  const setCEEntryPrice = (value) => {
    setCEEntryPriceState(value);
    if (value !== null) {
      localStorage.setItem('quickOrder_ceEntryPrice', value.toString());
    } else {
      localStorage.removeItem('quickOrder_ceEntryPrice');
    }
  };

  const setPEEntryPrice = (value) => {
    setPEEntryPriceState(value);
    if (value !== null) {
      localStorage.setItem('quickOrder_peEntryPrice', value.toString());
    } else {
      localStorage.removeItem('quickOrder_peEntryPrice');
    }
  };

  // Persist orderType changes
  useEffect(() => {
    localStorage.setItem('quickOrder_orderType', orderType);
  }, [orderType]);

  // Persist productType changes
  useEffect(() => {
    localStorage.setItem('quickOrder_productType', productType);
  }, [productType]);

  // Ensure quantities are properly loaded from localStorage on mount
  useEffect(() => {
    const savedCEQty = localStorage.getItem('quickOrder_ceQty');
    const savedPEQty = localStorage.getItem('quickOrder_peQty');
    if (savedCEQty && savedCEQty !== ceQty) {
      setCEQty(savedCEQty);
    }
    if (savedPEQty && savedPEQty !== peQty) {
      setPEQty(savedPEQty);
    }
  }, []); // Run only on mount

  // Wrapper functions for MTM baseline state with localStorage persistence
  const setBaselineCESum = (value) => {
    setBaselineCESumState(value);
    if (value && value > 0) {
      localStorage.setItem('tradingView_baselineCESum', value.toString());
    } else {
      localStorage.removeItem('tradingView_baselineCESum');
    }
  };

  const setBaselinePESum = (value) => {
    setBaselinePESumState(value);
    if (value && value > 0) {
      localStorage.setItem('tradingView_baselinePESum', value.toString());
    } else {
      localStorage.removeItem('tradingView_baselinePESum');
    }
  };

  // Wrapper functions for Next Expiry state persistence
  const setNextExpiryDate = (value) => {
    setNextExpiryDateState(value);
    if (value) {
      localStorage.setItem('tradingView_nextExpiryDate', value);
    } else {
      localStorage.removeItem('tradingView_nextExpiryDate');
    }
  };

  // Quantity adjustment functions
  const adjustQuantity = (optionType, operation) => {
    const currentStrike = optionType === 'CE' ? selectedCEStrike : selectedPEStrike;
    const currentQty = optionType === 'CE' ? ceQty : peQty;
    
    if (!currentStrike) return;
    
    const lotSize = currentStrike.lotSize || 25; // Default to 25 if not available
    
    // Parse current quantity, default to 0 if empty/invalid
    const currentQtyNum = parseInt(currentQty) || 0;
    
    let newQty;
    if (operation === 'increase') {
      // Add lot size to current value (or start with lot size if 0)
      newQty = currentQtyNum === 0 ? lotSize : currentQtyNum + lotSize;
    } else if (operation === 'decrease') {
      // Subtract lot size, but don't go below lot size
      newQty = Math.max(lotSize, currentQtyNum - lotSize);
    }
    
    if (optionType === 'CE') {
      setCEQty(newQty.toString());
    } else {
      setPEQty(newQty.toString());
    }
  };

  // Check if decrease button should be disabled
  const isDecreaseDisabled = (optionType) => {
    const currentStrike = optionType === 'CE' ? selectedCEStrike : selectedPEStrike;
    const currentQty = optionType === 'CE' ? ceQty : peQty;
    
    if (!currentStrike) return true;
    
    const lotSize = currentStrike.lotSize || 25;
    const currentQtyNum = parseInt(currentQty) || 0;
    
    // Disable if current quantity is at or below lot size
    return currentQtyNum <= lotSize;
  };

  // Handle Re Order button - places same order again
  const handleReOrder = (optionType) => {
    const strikeData = optionType === 'CE' ? selectedCEStrike : selectedPEStrike;
    if (strikeData) {
      // Get the last order direction or default to BUY
      const lastDirection = localStorage.getItem(`lastDirection_${optionType}`) || 'BUY';
      handleQuickOrder(strikeData, lastDirection, optionType);
    }
  };

  // Get current direction for display
  const getCurrentDirection = (optionType) => {
    return localStorage.getItem(`lastDirection_${optionType}`) || 'BUY';
  };

  // Handle Exit position
  const handleQuickExit = (optionType) => {
    const strikeData = optionType === 'CE' ? selectedCEStrike : selectedPEStrike;
    if (strikeData) {
      const lastDirection = localStorage.getItem(`lastDirection_${optionType}`);
      // Exit is opposite of last direction
      const exitDirection = lastDirection === 'BUY' ? 'SELL' : 'BUY';
      handleQuickOrder(strikeData, exitDirection, optionType);
    }
  };

  const setNextExpirySymbols = (value) => {
    setNextExpirySymbolsState(value);
    if (value && value.length > 0) {
      localStorage.setItem('tradingView_nextExpirySymbols', JSON.stringify(value));
    } else {
      localStorage.removeItem('tradingView_nextExpirySymbols');
    }
  };

  // Re-subscribe to depth data when component mounts with saved state
  useEffect(() => {
    if (wsConnected && nextExpirySymbols.length > 0) {
      // console.log('🔄 Re-subscribing to saved depth data:', nextExpirySymbols.length, 'symbols');
      // console.log('📊 Next expiry symbols:', nextExpirySymbols);
      
      // Wait for WebSocket to be fully ready before subscribing
      setTimeout(() => {
        aliceBlueWS.subscribeDepth(nextExpirySymbols);
        
        // Log what we're subscribing to
        nextExpirySymbols.forEach(sym => {
          // console.log(`  📡 Subscribing depth: ${sym.exchange}|${sym.token} - ${sym.tradingSymbol} (${sym.type} ${sym.strike})`);
        });
      }, 500); // Wait 500ms for WebSocket to be fully ready
    } else {
      console.log('⚠️ Not subscribing to depth data:', { wsConnected, symbolsCount: nextExpirySymbols.length });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsConnected]); // Only run when wsConnected changes (on mount and reconnect)

  useEffect(() => {
    // Fetch master symbols when component mounts
    const fetchMasterSymbols = async () => {
      try {
        const response = await api.get('/master-symbols/by-exchange', {
          headers: { Authorization: `Bearer ${userSession}` }
        });

        if (response.data.success) {
          setMasterSymbolsByExchange(response.data.data || {});
          // Initialize NFO symbols order only if no saved order exists
          if (response.data.data?.NFO) {
            const savedOrder = localStorage.getItem('tradingView_nfoSymbolsOrder');
            if (!savedOrder) {
              const defaultOrder = response.data.data.NFO.map(s => s.id);
              setNfoSymbolsOrder(defaultOrder);
              localStorage.setItem('tradingView_nfoSymbolsOrder', JSON.stringify(defaultOrder));
            }
          }
        } else {
          console.error('Failed to fetch master symbols:', response.data.error);
        }
      } catch (error) {
        console.error('Error fetching master symbols:', error);
      }
    };

    // Fetch baseline sums from database for MTM calculation
    // Only if localStorage doesn't have them (for cross-device sync)
    const fetchBaselineSums = async () => {
      // Check if localStorage already has baseline values
      const localCESum = localStorage.getItem('tradingView_baselineCESum');
      const localPESum = localStorage.getItem('tradingView_baselinePESum');
      
      // If localStorage has values, use those (they're already loaded in state)
      if (localCESum && localPESum) {
        console.log('✅ Using baseline sums from localStorage:', { CE: localCESum, PE: localPESum });
        return;
      }
      
      // Otherwise, try to fetch from database
      try {
        const response = await api.get('/options-analysis/baseline-sums', {
          headers: { Authorization: `Bearer ${userSession}` }
        });

        if (response.data.success && response.data.data.hasBaseline) {
          console.log('📊 Loaded baseline sums from database:', response.data.data);
          // Use wrapper functions to persist to localStorage
          setBaselineCESum(response.data.data.baselineCESum);
          setBaselinePESum(response.data.data.baselinePESum);
        } else {
          console.log('ℹ️ No baseline data found in database or localStorage');
        }
      } catch (error) {
        console.error('Error fetching baseline sums:', error);
      }
    };

    if (userSession) {
      fetchMasterSymbols();
      fetchBaselineSums();
    }
  }, [userSession]);

  // Auto-select first sub-tab when master symbols are loaded
  useEffect(() => {
    // Auto-select first NFO sub-tab
    if (activeTab === 'NFO' && masterSymbolsByExchange['NFO']?.length > 0 && !nfoSubTab) {
      const firstSymbol = masterSymbolsByExchange['NFO'][0].symbol_name;
      setNfoSubTab(firstSymbol);
      handleSymbolNameChange(firstSymbol, 'NFO');
    }
    // Auto-select first BFO sub-tab
    if (activeTab === 'BFO' && masterSymbolsByExchange['BFO']?.length > 0 && !bfoSubTab) {
      const firstSymbol = masterSymbolsByExchange['BFO'][0].symbol_name;
      setBfoSubTab(firstSymbol);
      handleSymbolNameChange(firstSymbol, 'BFO');
    }
    // Auto-select first MCX sub-tab (kept for position tracking compatibility)
    if (activeTab === 'MCX' && masterSymbolsByExchange['MCX']?.length > 0 && !mcxSubTab) {
      const firstSymbol = masterSymbolsByExchange['MCX'][0].symbol_name;
      setMcxSubTab(firstSymbol);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, masterSymbolsByExchange]);

  // Fetch MCX underlying list when MCX tab becomes active
  useEffect(() => {
    if (activeTab === 'MCX' && userSession && mcxUnderlyingList.length === 0) {
      fetchMcxUnderlyingList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, userSession]);

  // Fetch MCX expiry list when underlying changes
  useEffect(() => {
    if (mcxSelectedUnderlying) {
      setMcxSelectedExpiry('');
      setMcxExpiryList([]);
      fetchMcxExpiryList(mcxSelectedUnderlying);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mcxSelectedUnderlying]);

  // Fetch expiries when sub-tab changes
  useEffect(() => {
    const fetchExpiriesForSubTab = async () => {
      const currentSymbol = activeTab === 'NFO' ? nfoSubTab : activeTab === 'BFO' ? bfoSubTab : '';
      const currentExchange = activeTab;
      
      if ((activeTab === 'NFO' || activeTab === 'BFO') && currentSymbol) {
        try {
          const response = await api.get(
            `/symbol-filters/expiry-dates?symbolName=${currentSymbol}&exchange=${currentExchange}`,
            { headers: { Authorization: `Bearer ${userSession}` } }
          );

          if (response.data.success) {
            setAvailableExpiries(response.data.data);
            // Update symbolFilters to include the symbol name from sub-tab
            const firstExpiryDate = response.data.data.length > 0 ? response.data.data[0].date : '';
            setSymbolFilters(prev => ({
              ...prev,
              symbolName: currentSymbol,
              // Auto-select first available expiry date as default
              expiryDate: firstExpiryDate
            }));
            
            // Auto-load strike prices for the first expiry date
            if (firstExpiryDate && currentSymbol) {
              try {
                // console.log('🎯 Auto-loading strike prices for first expiry:', { symbolName: currentSymbol, expiryDate: firstExpiryDate, exchange: currentExchange });
                const strikesResponse = await api.get(`/symbol-filters/strike-prices?symbolName=${currentSymbol}&expiryDate=${firstExpiryDate}&exchange=${currentExchange}`, {
                  headers: { Authorization: `Bearer ${userSession}` }
                });
                
                if (strikesResponse.data.success) {
                  setAvailableStrikes(strikesResponse.data.data);
                  // console.log('✅ Auto-loaded strikes for first expiry:', strikesResponse.data.data);
                }
              } catch (error) {
                console.error('❌ Error auto-loading strike prices:', error);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching expiry dates for sub-tab:', error);
        }
      }
    };

    fetchExpiriesForSubTab();
  }, [activeTab, nfoSubTab, bfoSubTab, userSession]);

  // Drag and drop handlers for NFO sub-tabs
  const handleDragStart = (e, symbolId) => {
    setDraggedSymbolId(symbolId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetSymbolId) => {
    e.preventDefault();
    
    if (draggedSymbolId === targetSymbolId) return;

    const newOrder = [...nfoSymbolsOrder];
    const draggedIndex = newOrder.indexOf(draggedSymbolId);
    const targetIndex = newOrder.indexOf(targetSymbolId);

    // Remove dragged item and insert at target position
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedSymbolId);

    setNfoSymbolsOrder(newOrder);
    // Save to localStorage to persist across sessions
    localStorage.setItem('tradingView_nfoSymbolsOrder', JSON.stringify(newOrder));
    setDraggedSymbolId(null);
  };

  const handleDragEnd = () => {
    setDraggedSymbolId(null);
  };

  useEffect(() => {
    // Connect to AliceBlue WebSocket on mount (automatically reconnect when page loads)
    setWsStatus('Connecting to WebSocket...');
    setConnectionError(null);
    
    const connectWebSocket = async () => {
      try {
        // Always attempt to connect/reconnect when page loads
        console.log('🔌 Auto-connecting WebSocket on page load...');
        
        // Disconnect first if already connected to ensure fresh connection
        if (aliceBlueWS.isConnected) {
          console.log('🔄 Disconnecting existing connection before reconnecting...');
          aliceBlueWS.disconnect();
        }
        
        await aliceBlueWS.connect();
        
        if (aliceBlueWS.isConnected) {
          setWsStatus('Connected');
          setConnectionError(null);
          setWsConnected(true);
          console.log('✅ WebSocket connected successfully (auto-connect)');
        }
      } catch (err) {
        console.error('❌ WebSocket:', err);
        
        let errorMessage = 'WebSocket failed';
        
        // Parse different error types
        if (err.message.includes('No session ID in response')) {
          errorMessage = 'AliceBlue API authentication failed - Please check API credentials';
        } else if (err.message.includes('HTTP 500')) {
          errorMessage = 'Server error - Please check backend configuration';
        } else if (err.message.includes('fetch')) {
          errorMessage = 'Backend server unavailable - Please check if server is running';
        } else if (err.message.includes('timeout')) {
          errorMessage = 'Connection timeout - Working in offline mode';
        } else {
          errorMessage = err.message + ' - Working in offline mode';
        }
        
        setConnectionError(errorMessage);
        setWsStatus('Offline Mode');
        setWsConnected(false);
        
        // Show user-friendly notification instead of just console error
        console.log('🔄 You can still use the application without real-time data');
      }
    };

    connectWebSocket();

    // Subscribe to connection status
    const connectionCallback = (connected) => {
      setWsConnected(connected);
      setWsStatus(connected ? 'Connected' : 'Disconnected');
      if (connected) {
        setConnectionError(null);
        // console.log('✅ WebSocket connected, waiting for OPEN state before subscribing...');
        
        // Wait for WebSocket to be fully in OPEN state before subscribing
        setTimeout(() => {
          if (watchlist.length > 0 && !window.isBuildingOptionsChain) {
            // Re-subscribe to watchlist when reconnected
            const symbols = watchlist.map(item => ({
              exchange: item.exchange,
              token: item.instrumentId
            }));
            // console.log('📡 Re-subscribing to', symbols.length, 'symbols');
            aliceBlueWS.subscribe(symbols);
          }
        }, 500); // Wait 500ms for WebSocket to be fully ready
      }
    };
    aliceBlueWS.addSubscriber('_connection', connectionCallback);

    // Subscribe to all price updates
    const priceCallback = (data) => {
      setLivePrices(prev => {
        const key = `${data.exchange}|${data.token}`;
        
        // Handle depth data separately
        if (data.type === 'depth') {
          setDepthData(prevDepth => {
            const existing = prevDepth[key] || {};
            // Merge new data with existing, preserving previous values if new ones are undefined
            const updated = {
              ...prevDepth,
              [key]: {
                tbq: data.tbq !== undefined ? data.tbq : existing.tbq,
                tsq: data.tsq !== undefined ? data.tsq : existing.tsq,
                lp: data.lp !== undefined ? data.lp : existing.lp
              }
            };
            
            // Log depth data updates
            if (data.tbq !== undefined || data.tsq !== undefined) {
             
            }
            
            return updated;
          });
          // Also update live prices for LTP
          if (data.lp) {
            return {
              ...prev,
              [key]: {
                ...prev[key],
                ltp: data.lp
              }
            };
          }
          return prev;
        }
        
        // Merge new data with existing data to preserve previous values
        return {
          ...prev,
          [key]: {
            ...prev[key], // Keep previous values
            ...data,      // Update with new values
            // Ensure LTP is never set to 0 or null if we have a previous value
            ltp: (data.lp && data.lp > 0) ? data.lp : (prev[key]?.ltp || data.lp),
            // Ensure percentage change is preserved if not in new data
            pc: (data.pc !== undefined && data.pc !== null) ? data.pc : (prev[key]?.pc),
            // Preserve high and low values - only update if new value exists, keep track of day's high/low
            h: (data.h !== undefined && data.h !== null) ? data.h : prev[key]?.h,
            l: (data.l !== undefined && data.l !== null) ? data.l : prev[key]?.l,
            // Log when we receive h/l data
            _hasHighLow: (data.h !== undefined || data.l !== undefined) || prev[key]?._hasHighLow
          }
        };
      });
    };
    aliceBlueWS.addSubscriber('*', priceCallback);

    return () => {
      aliceBlueWS.removeSubscriber('_connection', connectionCallback);
      aliceBlueWS.removeSubscriber('*', priceCallback);
    };
  }, [watchlist]);

  useEffect(() => {
    // Subscribe to watchlist symbols when watchlist changes or when WS connects
    // Skip auto-subscription if we're currently building options chain (batch operation)
    if (wsConnected && watchlist.length > 0 && !window.isBuildingOptionsChain) {
      console.log('📡 Subscribing to watchlist symbols:', watchlist.length, 'items');
      const symbols = watchlist.map(item => ({
        exchange: item.exchange,
        token: item.instrumentId
      }));
      aliceBlueWS.subscribe(symbols);
      
      // Also subscribe to depth data for NFO/BFO options to get TBQ/TSQ
      const depthSymbols = watchlist
        .filter(item => item.exchange === 'NFO' || item.exchange === 'BFO')
        .map(item => ({
          exchange: item.exchange,
          token: item.instrumentId,
          tradingSymbol: item.tradingSymbol
        }));
      
      if (depthSymbols.length > 0) {
        console.log('📊 Subscribing to depth data for options:', depthSymbols.length, 'symbols');
        aliceBlueWS.subscribeDepth(depthSymbols);
      }
    } else if (window.isBuildingOptionsChain) {
      // console.log('⏸️ Skipping auto-subscription - options chain is being built');
    }
    
    // Subscribe to indices data when WebSocket connects
    if (wsConnected && !window.isBuildingOptionsChain) {
      // console.log('📈 Subscribing to indices data (NIFTY, BANKNIFTY, SENSEX)');
      const indicesSymbols = Object.values(indicesData).map(index => ({
        exchange: index.exchange,
        token: index.token
      }));
      aliceBlueWS.subscribe(indicesSymbols);
    }
  }, [watchlist, wsConnected]);

  // Auto-populate header info when NFO/BFO/MCX options are in watchlist
  useEffect(() => {
    if ((activeTab === 'NFO' || activeTab === 'BFO' || activeTab === 'MCX') && !selectedChainInfo && watchlist.length > 0) {
      const options = watchlist.filter(item => item.exchange === activeTab && item.tradingSymbol);
      if (options.length > 0) {
        // Extract info from first option
        const firstOption = options[0];
        if (!firstOption.tradingSymbol) return;
        
        const symbolMatch = firstOption.tradingSymbol.match(/^([A-Z]+)/);
        // NFO: 02DEC25, BFO: 25DEC
        const expiryMatch = firstOption.tradingSymbol.match(/(\d{2}[A-Z]{3}\d{0,2})/);
        // NFO: C26000, BFO: 72800CE
        const strikeMatch = firstOption.tradingSymbol.match(/[CP](\d+)$/) || firstOption.tradingSymbol.match(/(\d{3,5})[CP]E$/);
        
        if (symbolMatch && expiryMatch && strikeMatch) {
          // Find all strikes to determine the base/center strike
          const allStrikes = options
            .filter(opt => opt.tradingSymbol)
            .map(opt => {
              let match = opt.tradingSymbol.match(/[CP](\d+)$/);
              if (!match) {
                match = opt.tradingSymbol.match(/(\d{3,5})[CP]E$/);
              }
              return match ? parseInt(match[1]) : null;
            })
            .filter(s => s !== null)
            .sort((a, b) => a - b);
          
          // Use middle strike as base strike
          const middleIndex = Math.floor(allStrikes.length / 2);
          const baseStrike = allStrikes[middleIndex] || parseInt(strikeMatch[1]);
          
          setSelectedChainInfo({
            symbolName: symbolMatch[1],
            expiryDate: expiryMatch[1],
            centerStrike: baseStrike.toLocaleString()
          });
        }
      }
    }
  }, [activeTab, watchlist, selectedChainInfo]);

  // Fetch and subscribe to next expiry depth data
  useEffect(() => {
    const fetchNextExpiryData = async () => {
      // Get current subtab based on active tab
      const currentSubTab = activeTab === 'NFO' ? nfoSubTab : activeTab === 'BFO' ? bfoSubTab : activeTab === 'MCX' ? mcxSubTab : '';
      
      // Don't fetch if we're currently building the options chain
      // Don't fetch if we're currently building the options chain
      if (window.isBuildingOptionsChain) {
        console.log('🚫 Skipping next expiry fetch - building options chain');
        return;
      }
      
      // Skip if no current subtab is selected
      if (!currentSubTab) {
        console.log('🚫 Skipping next expiry fetch - no subtab selected');
        return;
      }      // Clear previous data if chain has changed
      if (selectedChainInfo && selectedChainInfo.expiryDateRaw) {
        const currentExpiry = selectedChainInfo.expiryDateRaw || selectedChainInfo.expiryDate;
        console.log('🔄 Current chain expiry:', currentExpiry);
      }
      
      if ((activeTab !== 'NFO' && activeTab !== 'BFO' && activeTab !== 'MCX') || !selectedChainInfo || !currentSubTab) {
        console.log('⏭️ Skipping next expiry fetch:', { activeTab, selectedChainInfo: !!selectedChainInfo, currentSubTab });
        return;
      }

      try {
        console.log('🔍 Fetching next expiry for:', currentSubTab);
        console.log('🔍 Selected chain info:', selectedChainInfo);
        
        // Fetch all available expiry dates for the symbol
        const response = await api.get(
          `/symbol-filters/expiry-dates?symbolName=${currentSubTab}&exchange=${activeTab}`,
          { headers: { Authorization: `Bearer ${userSession}` } }
        );

        console.log('📅 Expiry dates response:', response.data);

        if (response.data.success && response.data.data.length > 1) {
          const expiries = response.data.data;
          const currentExpiry = selectedChainInfo.expiryDateRaw || selectedChainInfo.expiryDate; // Use raw date if available
          
          console.log('📅 All expiries:', expiries.map(e => e.date));
          console.log('📅 Looking for current expiry:', currentExpiry);
          
          // Find next expiry after current
          const currentIndex = expiries.findIndex(e => e.date === currentExpiry);
          const nextExpiry = currentIndex >= 0 && currentIndex < expiries.length - 1
            ? expiries[currentIndex + 1]
            : null;

          console.log('📆 Current expiry:', currentExpiry, 'Index:', currentIndex, 'Next:', nextExpiry);

          if (nextExpiry) {
            setNextExpiryDate(nextExpiry.formatted);
            console.log('✅ Set next expiry date to:', nextExpiry.formatted);

            // Get all strikes from current watchlist
            const currentExchange = activeTab; // Use activeTab (NFO, BFO or MCX)
            const currentSubTab = activeTab === 'NFO' ? nfoSubTab : activeTab === 'BFO' ? bfoSubTab : mcxSubTab;
            
            const optionsFromWatchlist = watchlist.filter(item =>
              item.exchange === currentExchange &&
              item.tradingSymbol.startsWith(currentSubTab)
            );

            const strikes = [...new Set(
              optionsFromWatchlist.map(opt => {
                // Use strikePrice if available from watchlist item
                if (opt.strikePrice) return opt.strikePrice.toString();
                
                // Fallback to parsing trading symbol
                // NFO: NIFTY25DEC25C26000 -> 26000
                let match = opt.tradingSymbol.match(/[CP](\d+)$/);
                if (match) return match[1];
                
                // BFO Weekly: SENSEX2610174900CE -> 74900
                if (opt.tradingSymbol.startsWith('SENSEX')) {
                  match = opt.tradingSymbol.match(/^SENSEX\d{2}[1-9OND]\d{2}(\d+)[CP]E$/);
                  if (match) return match[1];
                }
                
                // BFO Standard: SENSEX25DEC72800CE -> 72800
                match = opt.tradingSymbol.match(/(\d+)[CP]E$/);
                return match ? match[1] : null;
              }).filter(Boolean)
            )];

            // console.log(`🎯 Strikes to fetch for ${currentExchange} ${currentSubTab}:`, strikes);

            // Fetch next expiry symbols for depth subscription
            const nextExpirySymbols = [];
            for (const strike of strikes) {
              try {
                // Fetch CE option
                const ceResponse = await api.get(
                  `/symbol-filters/search?symbolName=${currentSubTab}&expiryDate=${nextExpiry.date}&optionType=CE&strikePrice=${strike}&exchange=${currentExchange}`,
                  { headers: { Authorization: `Bearer ${userSession}` } }
                );
                if (ceResponse.data.success && ceResponse.data.data.length > 0) {
                  const ceData = ceResponse.data.data[0];
                  nextExpirySymbols.push({
                    exchange: currentExchange,
                    token: ceData.instrument_id, // Use instrument_id from backend
                    strike: strike,
                    type: 'CE',
                    tradingSymbol: ceData.trading_symbol // Use trading_symbol from backend
                  });
                  // console.log(`✅ CE ${strike}: token=${ceData.instrument_id}, symbol=${ceData.trading_symbol}`);
                }

                // Fetch PE option
                const peResponse = await api.get(
                  `/symbol-filters/search?symbolName=${currentSubTab}&expiryDate=${nextExpiry.date}&optionType=PE&strikePrice=${strike}&exchange=${currentExchange}`,
                  { headers: { Authorization: `Bearer ${userSession}` } }
                );
                if (peResponse.data.success && peResponse.data.data.length > 0) {
                  const peData = peResponse.data.data[0];
                  nextExpirySymbols.push({
                    exchange: currentExchange,
                    token: peData.instrument_id, // Use instrument_id from backend
                    strike: strike,
                    type: 'PE',
                    tradingSymbol: peData.trading_symbol // Use trading_symbol from backend
                  });
                  // console.log(`✅ PE ${strike}: token=${peData.instrument_id}, symbol=${peData.trading_symbol}`);
                }
              } catch (error) {
                console.warn(`Failed to fetch next expiry for strike ${strike}:`, error);
              }
            }

            // Subscribe to depth data for next expiry symbols
            if (nextExpirySymbols.length > 0 && wsConnected) {
              console.log('📊 Subscribing to next expiry depth data:', nextExpirySymbols.length, 'symbols');
              setNextExpirySymbols(nextExpirySymbols);
              aliceBlueWS.subscribeDepth(nextExpirySymbols);
            } else {
              console.warn('⚠️ No symbols to subscribe or WS not connected:', {
                symbolsCount: nextExpirySymbols.length,
                wsConnected
              });
            }
          } else {
            console.warn('⚠️ No next expiry found');
            setNextExpiryDate(null);
          }
        } else {
          console.warn('⚠️ Not enough expiry dates or request failed');
        }
      } catch (error) {
        console.error('❌ Error fetching next expiry data:', error);
      }
    };

    fetchNextExpiryData();
  }, [activeTab, selectedChainInfo?.expiryDateRaw, nfoSubTab, bfoSubTab, wsConnected, userSession]);
  // Removed nextExpiryDate and nextExpirySymbols.length from dependencies to allow re-fetching

  // Auto-fetch exported data when in flipped mode and next expiry is available
  useEffect(() => {
    if (isFlipped && nextExpiryDate && selectedChainInfo && exportedData.length === 0) {
      console.log('🔄 Auto-fetching exported data for difference view');
      handleFetchExportedData();
    }
  }, [nextExpiryDate, isFlipped, selectedChainInfo]);
  
  // Fetch tradeBook when positions table is flipped
  useEffect(() => {
    if (isPositionsFlipped) {
      dispatch(fetchTradeBook());
    }
  }, [isPositionsFlipped, dispatch]);

  // Export Bull vs Bear data to database
  // Fetch exported data from SQL database
  const handleFetchExportedData = async () => {
    if (!selectedChainInfo || !nextExpiryDate) {
      return;
    }
    
    setIsFetchingExported(true);
    
    try {
      // Convert nextExpiryDate from "09DEC25" format to "YYYY-MM-DD"
      const convertDateFormat = (dateStr) => {
        if (!dateStr) return null;
        const match = dateStr.match(/(\d{2})([A-Z]{3})(\d{2})/);
        if (!match) return null;
        const day = match[1];
        const month = match[2];
        const year = '20' + match[3];
        const monthMap = {
          'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
          'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
          'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
        };
        return `${year}-${monthMap[month]}-${day}`;
      };
      
      const formattedExpiryDate = convertDateFormat(nextExpiryDate);
      
      if (!formattedExpiryDate) {
        console.error('Invalid expiry date format');
        setIsFetchingExported(false);
        return;
      }
      
      const response = await fetch(
        `/api/bull-bear/latest?symbolName=${encodeURIComponent(selectedChainInfo.symbolName)}&expiryDate=${encodeURIComponent(formattedExpiryDate)}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch exported data');
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        setExportedData(result.data);
        console.log(`✅ Fetched ${result.data.length} exported records from SQL`);
      } else {
        setExportedData([]);
        console.warn('No exported data found in database');
      }
    } catch (error) {
      console.error('Error fetching exported data:', error);
      setExportedData([]);
    } finally {
      setIsFetchingExported(false);
    }
  };
  
  const handleExportBullBear = async () => {
    if (!nextExpiryDate || !selectedChainInfo || ceOptions.length === 0) {
      setExportMessage('❌ No data to export. Please analyse the option chain first.');
      setTimeout(() => setExportMessage(''), 3000);
      return;
    }

    setIsExporting(true);
    setExportMessage('');

    try {
      // Convert nextExpiryDate from "09DEC25" or "24DEC" format to "YYYY-MM-DD"
      const convertDateFormat = (dateStr) => {
        if (!dateStr) return null;
        
        // Expected format: "09DEC25" (NFO)
        let match = dateStr.match(/(\d{2})([A-Z]{3})(\d{2})/);
        if (match) {
          const day = match[1];
          const month = match[2];
          const year = '20' + match[3];
          
          const monthMap = {
            'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
            'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
            'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
          };
          
          return `${year}-${monthMap[month]}-${day}`;
        }

        // Expected format: "24DEC" (BFO)
        match = dateStr.match(/(\d{2})([A-Z]{3})/);
        if (match) {
          const day = match[1];
          const monthStr = match[2];
          const year = new Date().getFullYear();
          
          const monthMap = {
            'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
            'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
            'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
          };
          
          return `${year}-${monthMap[monthStr]}-${day}`;
        }
        
        return null;
      };
      
      const formattedExpiryDate = convertDateFormat(nextExpiryDate);
      
      if (!formattedExpiryDate) {
        setExportMessage('❌ Invalid expiry date format');
        setTimeout(() => setExportMessage(''), 3000);
        setIsExporting(false);
        return;
      }
      
      const exportData = [];
      
      console.log('📊 Starting export for:', {
        symbolName: selectedChainInfo.symbolName,
        exchange: activeTab,
        nextExpiryDate,
        ceOptionsCount: ceOptions.length,
        peOptionsCount: peOptions.length,
        nextExpirySymbolsCount: nextExpirySymbols.length
      });
      
      // Process by iterating through PE options (descending order)
      peOptions.forEach((peItem, idx) => {
        const ceItem = ceOptions[idx];
        
        // Extract strike prices - support both NFO and BFO formats
        let peStrike = null;
        let peStrikeMatch = peItem.tradingSymbol.match(/[CP](\d+)$/); // NFO format
        if (peStrikeMatch) {
          peStrike = peStrikeMatch[1];
        } else {
          peStrikeMatch = peItem.tradingSymbol.match(/(\d{3,5})[CP]E$/); // BFO format
          if (peStrikeMatch) {
            peStrike = peStrikeMatch[1];
          }
        }
        
        let ceStrike = null;
        if (ceItem) {
          let ceStrikeMatch = ceItem.tradingSymbol.match(/[CP](\d+)$/); // NFO format
          if (ceStrikeMatch) {
            ceStrike = ceStrikeMatch[1];
          } else {
            ceStrikeMatch = ceItem.tradingSymbol.match(/(\d{3,5})[CP]E$/); // BFO format
            if (ceStrikeMatch) {
              ceStrike = ceStrikeMatch[1];
            }
          }
        }
        
        // Verbose logging disabled for performance
        // console.log(`Processing strikes - PE: ${peStrike}, CE: ${ceStrike}`);
        
        // Process PE strike data
        if (peStrike) {
          const nextPE = nextExpirySymbols.find(s => s.strike === peStrike && s.type === 'PE');
          const peKey = nextPE ? `${nextPE.exchange}|${nextPE.token}` : null;
          const peTBQ = (peKey && depthData[peKey]?.tbq) || 0;
          const peTSQ = (peKey && depthData[peKey]?.tsq) || 0;
          
          // Verbose logging disabled for performance
          // console.log(`PE ${peStrike}`);
          
          // Check if this strike already exists in exportData
          let existingEntry = exportData.find(item => item.strikePrice === parseInt(peStrike));
          
          if (existingEntry) {
            // Update PE data in existing entry
            existingEntry.pe = nextPE ? {
              tradingSymbol: nextPE.tradingSymbol || null,
              exchange: nextPE.exchange || null,
              instrumentId: nextPE.token || null,
              tbq: typeof peTBQ === 'number' ? peTBQ : 0,
              tsq: typeof peTSQ === 'number' ? peTSQ : 0
            } : null;
          } else if (nextPE) {
            // Create new entry with PE data
            exportData.push({
              symbolName: selectedChainInfo.symbolName,
              expiryDate: nextExpiryDate,
              strikePrice: parseInt(peStrike),
              ce: null,
              pe: {
                tradingSymbol: nextPE.tradingSymbol || null,
                exchange: nextPE.exchange || null,
                instrumentId: nextPE.token || null,
                tbq: typeof peTBQ === 'number' ? peTBQ : 0,
                tsq: typeof peTSQ === 'number' ? peTSQ : 0
              }
            });
          }
        }
        
        // Process CE strike data
        if (ceStrike) {
          const nextCE = nextExpirySymbols.find(s => s.strike === ceStrike && s.type === 'CE');
          const ceKey = nextCE ? `${nextCE.exchange}|${nextCE.token}` : null;
          const ceTBQ = (ceKey && depthData[ceKey]?.tbq) || 0;
          const ceTSQ = (ceKey && depthData[ceKey]?.tsq) || 0;
          
          // Verbose logging disabled for performance
          // console.log(`CE ${ceStrike}`);
          
          // Check if this strike already exists in exportData
          let existingEntry = exportData.find(item => item.strikePrice === parseInt(ceStrike));
          
          if (existingEntry) {
            // Update CE data in existing entry
            existingEntry.ce = nextCE ? {
              tradingSymbol: nextCE.tradingSymbol || null,
              exchange: nextCE.exchange || null,
              instrumentId: nextCE.token || null,
              tbq: typeof ceTBQ === 'number' ? ceTBQ : 0,
              tsq: typeof ceTSQ === 'number' ? ceTSQ : 0
            } : null;
          } else if (nextCE) {
            // Create new entry with CE data
            exportData.push({
              symbolName: selectedChainInfo.symbolName,
              expiryDate: nextExpiryDate,
              strikePrice: parseInt(ceStrike),
              ce: {
                tradingSymbol: nextCE.tradingSymbol || null,
                exchange: nextCE.exchange || null,
                instrumentId: nextCE.token || null,
                tbq: typeof ceTBQ === 'number' ? ceTBQ : 0,
                tsq: typeof ceTSQ === 'number' ? ceTSQ : 0
              },
              pe: null
            });
          }
        }
      });
      
      console.log('📤 Export data prepared:', {
        totalRecords: exportData.length,
        sampleRecord: exportData[0],
        symbolName: selectedChainInfo.symbolName,
        expiryDate: formattedExpiryDate
      });
      
      if (exportData.length === 0) {
        console.error('❌ No export data generated');
        setExportMessage('❌ No valid data to export');
        setTimeout(() => setExportMessage(''), 3000);
        setIsExporting(false);
        return;
      }
      
      // Send to backend
      console.log('📤 Sending to backend:', {
        symbolName: selectedChainInfo.symbolName,
        expiryDate: formattedExpiryDate,
        recordCount: exportData.length
      });
      
      const response = await api.post('/bull-bear/export', {
        symbolName: selectedChainInfo.symbolName,
        expiryDate: formattedExpiryDate,
        bullBearData: exportData
      });
      
      console.log('📥 Backend response:', response.data);
      
      if (response.data.success) {
        const result = response.data.data;
        setLastExportSessionId(result.exportSessionId);
        
        // Show success popup
        setPopupMessage(`Success`);
        setPopupType('success');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
        
        // Clear exported data and fetch fresh, then flip to difference view
        setExportedData([]);
        await handleFetchExportedData();
        setIsFlipped(true);
      } else {
        // Show error popup
        setPopupMessage('Export failed: ' + (response.data.error || 'Unknown error'));
        setPopupType('error');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
      }
    } catch (error) {
      console.error('Export error:', error);
      // Show error popup
      setPopupMessage('Export failed: ' + (error.response?.data?.error || error.message));
      setPopupType('error');
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    } finally {
      setIsExporting(false);
    }
  };

  // Handle flip table toggle
  const handleFlipTable = async () => {
    if (isFlipped) {
      // Switching to live view - just toggle the state
      setIsFlipped(false);
    } else {
      // Switching back to difference view - fetch exported data if not already loaded
      if (exportedData.length === 0) {
        await handleFetchExportedData();
      }
      setIsFlipped(true);
    }
  };

  // ==================== MCX OPTION CHAIN FUNCTIONS ====================

  const fetchMcxUnderlyingList = async () => {
    try {
      setMcxLoading(true);
      setMcxError(null);
      const response = await api.post('/option-chain/underlying', {
        exch: 'mcx_fo',
        userSession
      });
      if (response.data.success && response.data.data.result) {
        const list = response.data.data.result[0]?.list_underlying || [];
        setMcxUnderlyingList(list);
        if (list.length > 0 && !mcxSelectedUnderlying) {
          setMcxSelectedUnderlying(list[0]);
        }
      }
    } catch (error) {
      console.error('MCX underlying fetch error:', error);
      setMcxError('Failed to fetch MCX symbols');
    } finally {
      setMcxLoading(false);
    }
  };

  const fetchMcxExpiryList = async (underlying) => {
    if (!underlying) return;
    try {
      setMcxLoading(true);
      setMcxError(null);
      const response = await api.post('/option-chain/expiry', {
        underlying,
        exch: 'mcx_fo',
        userSession
      });
      if (response.data.success && response.data.data.result) {
        const list = response.data.data.result[0]?.underlying_expiry || [];
        setMcxExpiryList(list);
        if (list.length > 0) {
          setMcxSelectedExpiry(list[0]);
        }
      }
    } catch (error) {
      console.error('MCX expiry fetch error:', error);
      setMcxError('Failed to fetch MCX expiry dates');
    } finally {
      setMcxLoading(false);
    }
  };

  const handleAddMcxChain = async () => {
    if (!mcxSelectedUnderlying || !mcxSelectedExpiry) return;
    try {
      setMcxLoading(true);
      setMcxError(null);
      const response = await api.post('/option-chain/chain', {
        underlying: mcxSelectedUnderlying,
        expiry: mcxSelectedExpiry,
        interval: mcxSelectedInterval,
        exch: 'mcx_fo',
        userSession
      });
      if (response.data.success && response.data.data.result) {
        const data = response.data.data.result[0]?.data || [];
        setMcxOptionChainData(data);
        setMcxChainVisible(true);
        // Subscribe to WS for MCX tokens
        if (data.length > 0 && wsConnected) {
          const instruments = [];
          data.forEach(item => {
            if (item.CE?.token) instruments.push({ exchange: 'MCX', token: item.CE.token });
            if (item.PE?.token) instruments.push({ exchange: 'MCX', token: item.PE.token });
          });
          if (instruments.length > 0) aliceBlueWS.subscribe(instruments);
        }
      }
    } catch (error) {
      console.error('MCX chain fetch error:', error);
      setMcxError('Failed to fetch MCX option chain');
    } finally {
      setMcxLoading(false);
    }
  };

  const getMcxStrikePrice = (item) => {
    const option = item.CE || item.PE;
    if (!option) return null;
    if (option.forInsName) {
      const match = option.forInsName.match(/(\d+(?:\.\d+)?)\s+(CE|PE|CALL|PUT)/i);
      if (match) return parseFloat(match[1]);
    }
    if (option.tradingsymbol) {
      let match = option.tradingsymbol.match(/(\d+)(CE|PE)$/i);
      if (match) return parseFloat(match[1]);
      match = option.tradingsymbol.match(/[CP](\d+(?:\.\d+)?)$/i);
      if (match) return parseFloat(match[1]);
    }
    return null;
  };

  const mcxFormatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '-';
    const value = parseFloat(num);
    if (isNaN(value)) return '-';
    return value.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const mcxFormatOI = (num) => {
    if (!num) return '-';
    const value = parseFloat(num);
    if (isNaN(value)) return '-';
    if (value >= 10000000) return (value / 10000000).toFixed(2) + 'Cr';
    if (value >= 100000) return (value / 100000).toFixed(2) + 'L';
    if (value >= 1000) return (value / 1000).toFixed(2) + 'K';
    return value.toFixed(0);
  };

  const getMcxAtmStrike = () => {
    if (mcxOptionChainData.length === 0) return null;
    // Use strongest OI as proxy for ATM
    let atmStrike = null;
    let maxOI = 0;
    mcxOptionChainData.forEach(item => {
      const ceOI = parseFloat(item.CE?.oi || 0);
      const peOI = parseFloat(item.PE?.oi || 0);
      if (ceOI + peOI > maxOI) {
        maxOI = ceOI + peOI;
        atmStrike = getMcxStrikePrice(item);
      }
    });
    return atmStrike;
  };

  // ==================== END MCX OPTION CHAIN FUNCTIONS ====================

  // Handle master symbol selection
  const handleMasterSymbolChange = async (exchange, masterSymbolName) => {
    setSelectedMasterSymbol(prev => ({...prev, [exchange]: masterSymbolName}));
    
    if (!masterSymbolName) {
      setExchangeSearchResults(prev => ({...prev, [exchange]: []}));
      return;
    }

    try {
      // Search for symbols based on master symbol name
      const params = new URLSearchParams({
        q: masterSymbolName,
        exchange: exchange
      });

      const response = await api.get(`/symbols/search?${params}`);
      
      if (response.data.success) {
        setExchangeSearchResults(prev => ({
          ...prev, 
          [exchange]: response.data.data || []
        }));
      } else {
        console.error('Master symbol search failed:', response.data.error);
        setExchangeSearchResults(prev => ({...prev, [exchange]: []}));
      }
    } catch (error) {
      console.error('Master symbol search error:', error);
      setExchangeSearchResults(prev => ({...prev, [exchange]: []}));
    }
  };

  const handleSearch = async (exchange, query) => {
    if (!query.trim()) {
      setExchangeSearchResults(prev => ({...prev, [exchange]: []}));
      return;
    }

    try {
      const params = new URLSearchParams({
        q: query.trim(),
        exchange: exchange
      });

      const response = await api.get(`/symbols/search?${params}`);
      
      if (response.data.success) {
        setExchangeSearchResults(prev => ({
          ...prev,
          [exchange]: response.data.data || []
        }));
      } else {
        console.error('Search failed:', response.data.error);
        setExchangeSearchResults(prev => ({...prev, [exchange]: []}));
      }
    } catch (error) {
      console.error('Search error:', error);
      setExchangeSearchResults(prev => ({...prev, [exchange]: []}));
    }
  };

  const handleAddToWatchlist = async (symbol) => {
    try {
      // Convert API response format to watchlist format
      const watchlistSymbol = {
        exchange: symbol.exchange,
        instrumentId: symbol.instrument_id,
        tradingSymbol: symbol.tradingSymbol || symbol.trading_symbol, // Handle both formats
        instrumentName: symbol.instrument_name || '',
        // Add any other properties that might be needed
        lotSize: symbol.lot_size,
        tickSize: symbol.tick_size,
        parsed_expiry: symbol.parsed_expiry,
        parsed_strike: symbol.parsed_strike,
        parsed_option_type: symbol.parsed_option_type
      };
      
      // Check if symbol already exists in watchlist using fresh Redux state
      const currentWatchlist = store.getState().watchlist.items;
      const exists = currentWatchlist.some(item => 
        item.exchange === watchlistSymbol.exchange && 
        item.instrumentId === watchlistSymbol.instrumentId
      );
      
      if (!exists) {
        // Dispatch Redux action
        dispatch(addToWatchlist(watchlistSymbol));
        
        // Minimal delay for Redux state update
        await new Promise(resolve => setTimeout(resolve, 5));
        
        // Verify the symbol was actually added to Redux
        const updatedWatchlist = store.getState().watchlist.items;
        const wasAdded = updatedWatchlist.some(item => 
          item.exchange === watchlistSymbol.exchange && 
          item.instrumentId === watchlistSymbol.instrumentId
        );
        
        if (!wasAdded) {
          console.warn(`⚠️ Redux ADD failed for ${watchlistSymbol.tradingSymbol} - retrying...`);
          // Force add again
          dispatch(addToWatchlist(watchlistSymbol));
          await new Promise(resolve => setTimeout(resolve, 5));
        }
        
        // Only subscribe to WebSocket if not building options chain (to prevent interference)
        if (wsConnected && !window.isBuildingOptionsChain) {
          try {
            aliceBlueWS.subscribe([{
              exchange: watchlistSymbol.exchange,
              token: watchlistSymbol.instrumentId
            }]);
          } catch (wsError) {
            console.warn('WebSocket subscription error:', wsError);
            // Don't throw - continue without WS subscription
          }
        }
      }
    } catch (error) {
      console.error('Error in handleAddToWatchlist:', error);
      // Don't rethrow - allow the batch process to continue
    }
  };

  const handleRemoveFromWatchlist = async (symbol) => {
    return new Promise((resolve) => {
      dispatch(removeFromWatchlist(symbol));
      // Unsubscribe from this symbol
      if (wsConnected) {
        aliceBlueWS.unsubscribe([{
          exchange: symbol.exchange,
          token: symbol.instrumentId
        }]);
      }
      // Small delay to ensure Redux action is processed
      setTimeout(resolve, 10);
    });
  };

  const handleBuySell = (symbol, action) => {
    const ltp = getLivePrice(symbol.exchange, symbol.instrumentId);
    setOrderData({
      symbol: symbol,
      action: action,
      quantity: symbol.lotSize || '',
      price: ltp || '',
      orderType: 'LIMIT',
      validity: 'DAY',
      product: 'MIS'
    });
    setOrderFormVisible(true);
    // Scroll to top of watchlist table
    document.getElementById('order-form-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Symbol Filter Functions for Options Trading
  const handleSymbolNameChange = async (symbolName, exchange = 'NFO') => {
    setSymbolFilters(prev => ({
      symbolName,
      expiryDate: '',
      optionType: '',
      strikePrice: ''
    }));
    setAvailableExpiries([]);
    setAvailableStrikes([]);
    setFilteredSymbols([]);
    
    // CRITICAL FIX: Update sub-tab to match the selected symbolName
    // This ensures the current sub-tab matches the filter selection
    if (exchange === 'NFO' && symbolName) {
      setNfoSubTab(symbolName);
      console.log(`📍 Updated nfoSubTab to match filter selection: ${symbolName}`);
    } else if (exchange === 'BFO' && symbolName) {
      setBfoSubTab(symbolName);
      console.log(`📍 Updated bfoSubTab to match filter selection: ${symbolName}`);
    } else if (exchange === 'MCX' && symbolName) {
      setMcxSubTab(symbolName);
      console.log(`📍 Updated mcxSubTab to match filter selection: ${symbolName}`);
    }

    if (!symbolName) return;

    // Fetch available expiry dates for this symbol
    try {
      const response = await api.get(`/symbol-filters/expiry-dates?symbolName=${symbolName}&exchange=${exchange}`, {
        headers: { Authorization: `Bearer ${userSession}` }
      });

      if (response.data.success) {
        setAvailableExpiries(response.data.data);
        // Auto-select first available expiry date as default
        if (response.data.data.length > 0) {
          const firstExpiryDate = response.data.data[0].date;
          setSymbolFilters(prev => ({
            ...prev,
            expiryDate: firstExpiryDate,
            strikePrice: '' // Clear strike price when expiry changes
          }));
          
          // Auto-load strike prices for the first expiry date
          try {
            // console.log('🎯 Auto-loading strike prices for first expiry:', { symbolName, expiryDate: firstExpiryDate, exchange });
            const strikesResponse = await api.get(`/symbol-filters/strike-prices?symbolName=${symbolName}&expiryDate=${firstExpiryDate}&exchange=${exchange}`, {
              headers: { Authorization: `Bearer ${userSession}` }
            });
            
            if (strikesResponse.data.success) {
              setAvailableStrikes(strikesResponse.data.data);
              // console.log('✅ Auto-loaded strikes for first expiry:', strikesResponse.data.data);
            }
          } catch (error) {
            console.error('❌ Error auto-loading strike prices:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching expiry dates:', error);
    }
  };

  const handleExpiryChange = async (expiryDate) => {
    // console.log('📅 handleExpiryChange called with:', expiryDate);
    // console.log('📅 Current symbolFilters.symbolName:', symbolFilters.symbolName);
    
    setSymbolFilters(prev => ({
      ...prev,
      expiryDate,
      strikePrice: '' // Clear strike price when expiry changes
    }));
    setAvailableStrikes([]);
    setFilteredSymbols([]);

    // Use current symbolName from state, not the updated state (timing issue fix)
    const currentSymbolName = symbolFilters.symbolName;
    
    if (!currentSymbolName || !expiryDate) {
      console.log('⚠️ Missing symbolName or expiryDate:', { currentSymbolName, expiryDate });
      return;
    }

    // Fetch available strike prices for this symbol and expiry
    try {
      console.log('🎯 Fetching strike prices for:', { symbolName: currentSymbolName, expiryDate, exchange: activeTab });
      const response = await api.get(`/symbol-filters/strike-prices?symbolName=${currentSymbolName}&expiryDate=${expiryDate}&exchange=${activeTab}`, {
        headers: { Authorization: `Bearer ${userSession}` }
      });

      console.log('🎯 Strike prices response:', response.data);
      
      if (response.data.success) {
        setAvailableStrikes(response.data.data);
        console.log('✅ Available strikes set:', response.data.data);
      } else {
        console.error('❌ Strike prices fetch failed:', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching strike prices:', error);
    }
  };

  const handleStrikePriceChange = (strikePrice) => {
    // console.log('🎯 handleStrikePriceChange called with:', strikePrice);
    // console.log('🎯 Current symbolFilters before update:', symbolFilters);
    
    setSymbolFilters(prev => {
      const updated = { ...prev, strikePrice };
      // console.log('🎯 Updated symbolFilters will be:', updated);
      
      // Validate if all required fields will be present
      const isComplete = updated.symbolName && updated.expiryDate && strikePrice;
      // console.log('🎯 Will form be complete?', isComplete);
      
      return updated;
    });
    setFilteredSymbols([]);
  };

  const handleApplyFilters = async (customFilters = null) => {
    // Use custom filters if provided, otherwise use current state
    const filters = customFilters || symbolFilters;
    const { symbolName, expiryDate, strikePrice } = filters;

    // console.log('🔍 handleApplyFilters called with:');
    // console.log('  - customFilters:', customFilters);
    // console.log('  - current symbolFilters state:', symbolFilters);
    // console.log('  - final filters being used:', filters);
    // console.log('  - individual values:', { symbolName, expiryDate, strikePrice });
    // Verbose validation logging disabled for performance

    if (!symbolName || !expiryDate || !strikePrice) {
      console.error('❌ Validation failed - missing required fields');
      alert('Please select symbol name, expiry date, and strike price');
      return;
    }

    try {
      // Set flag to prevent interference from other operations
      window.isBuildingOptionsChain = true;
      
      const centerStrike = parseInt(strikePrice);
      
      console.log('🔍 Starting Smart Options Chain Builder - Following 8-Step Process');
      console.log('🔍 Symbol:', symbolName, 'Expiry:', expiryDate, 'Center Strike:', centerStrike);
      
      // STEP 1-4: Fetch ALL symbols for this symbol and expiry (CE and PE)
      const [ceResponse, peResponse] = await Promise.all([
        api.get(
          `/symbol-filters/search?symbolName=${symbolName}&expiryDate=${expiryDate}&optionType=CE&exchange=${activeTab}`,
          { headers: { Authorization: `Bearer ${userSession}` } }
        ).catch(err => {
          console.error('Error fetching CE:', err);
          return { data: { success: false, data: [] } };
        }),
        api.get(
          `/symbol-filters/search?symbolName=${symbolName}&expiryDate=${expiryDate}&optionType=PE&exchange=${activeTab}`,
          { headers: { Authorization: `Bearer ${userSession}` } }
        ).catch(err => {
          console.error('Error fetching PE:', err);
          return { data: { success: false, data: [] } };
        })
      ]);
      
      const allCE = ceResponse.data.success ? ceResponse.data.data : [];
      const allPE = peResponse.data.success ? peResponse.data.data : [];
      
      console.log('📦 Fetched CE options:', allCE.length, 'PE options:', allPE.length);
      
      if (allCE.length === 0 || allPE.length === 0) {
        alert('No CE or PE options found for this expiry');
        return;
      }
      
      // STEP 5-6: Extract all available strikes and find tick size
      const extractStrike = (option) => {
        // Use the backend's parsed_strike field if available (most reliable)
        if (option.parsed_strike) {
          return parseInt(option.parsed_strike);
        }
        
        // Fallback: try to parse from trading_symbol for older data
        const tradingSymbol = option.trading_symbol;
        
        // Try NFO format first: ends with C/P followed by numbers (e.g., NIFTY02DEC25C26000)
        let match = tradingSymbol?.match(/[CP](\d+)$/);
        if (match) {
          return parseInt(match[1]);
        }
        
        // Try BFO format: numbers before CE/PE at the end (e.g., SENSEX25D0486100CE)
        match = tradingSymbol?.match(/(\d+)(CE|PE)$/);
        if (match) {
          return parseInt(match[1]);
        }
        
        return null;
      };
      
      const ceStrikes = allCE.map(opt => extractStrike(opt)).filter(Boolean).sort((a, b) => a - b);
      const peStrikes = allPE.map(opt => extractStrike(opt)).filter(Boolean).sort((a, b) => a - b);
      
      // Get strikes available in BOTH CE and PE
      const commonStrikes = ceStrikes.filter(s => peStrikes.includes(s));
      const uniqueStrikes = [...new Set(commonStrikes)].sort((a, b) => a - b);
      
      console.log('🎯 Available strikes:', uniqueStrikes.length, 'Range:', uniqueStrikes[0], 'to', uniqueStrikes[uniqueStrikes.length - 1]);
      
      if (uniqueStrikes.length < 2) {
        alert('Not enough strikes available');
        return;
      }
      
      // STEP 5-6: Calculate tick size more robustly by finding the most common difference
      // This handles cases where there might be missing strikes that could skew the calculation
      const calculateTickSize = (strikes) => {
        if (strikes.length < 2) return 50; // Default fallback
        
        // Calculate all consecutive differences
        const differences = [];
        for (let i = 1; i < strikes.length; i++) {
          differences.push(strikes[i] - strikes[i - 1]);
        }
        
        // Count frequency of each difference
        const diffCounts = {};
        differences.forEach(diff => {
          diffCounts[diff] = (diffCounts[diff] || 0) + 1;
        });
        
        // Find the most common difference (most likely tick size)
        const mostCommonDiff = Object.keys(diffCounts).reduce((a, b) => 
          diffCounts[a] > diffCounts[b] ? a : b
        );
        
        // Apply symbol-specific logic as fallback
        const tickSize = parseInt(mostCommonDiff);
        
        // Validate against expected values for major indices
        if (symbolName === 'NIFTY' && tickSize !== 50) {
          console.warn(`⚠️ NIFTY tick size ${tickSize} != 50, using 50`);
          return 50;
        } else if (symbolName === 'BANKNIFTY' && tickSize !== 100) {
          console.warn(`⚠️ BANKNIFTY tick size ${tickSize} != 100, using 100`);
          return 100;
        } else if (symbolName === 'SENSEX' && tickSize !== 100) {
          console.warn(`⚠️ SENSEX tick size ${tickSize} != 100, using 100`);
          return 100;
        }
        
        console.log('📏 Tick size calculation:', {
          differences: differences.slice(0, 10), // Show first 10
          diffCounts,
          selectedTickSize: tickSize
        });
        
        return tickSize;
      };
      
      const tickSize = calculateTickSize(uniqueStrikes);
      console.log('📏 Final tick size for', symbolName + ':', tickSize);
      
      // STEP 7: Generate exactly 19 strikes (9 below + center + 9 above)
      const targetStrikes = [];
      
      // Always generate exactly 19 strikes, regardless of availability
      for (let i = -9; i <= 9; i++) {
        const strike = centerStrike + (i * tickSize);
        targetStrikes.push(strike);
      }
      
      // Filter to only include strikes that exist in our data
      const availableTargetStrikes = targetStrikes.filter(strike => uniqueStrikes.includes(strike));
      
      console.log('📊 Calculated 19 strikes:', targetStrikes);
      console.log('📊 Available in data:', availableTargetStrikes.length, '/', targetStrikes.length);
      console.log('📊 Available strikes:', availableTargetStrikes);
      
      // STEP 8: Build final symbol list with proper mapping
      const allSymbols = [];
      
      availableTargetStrikes.forEach(strike => {
        // Find CE option
        const ceOption = allCE.find(opt => extractStrike(opt) === strike);
        // Find PE option  
        const peOption = allPE.find(opt => extractStrike(opt) === strike);
        
        if (ceOption) {
          // Fix property mapping: backend returns trading_symbol, frontend expects tradingSymbol
          const normalizedCE = {
            ...ceOption,
            tradingSymbol: ceOption.trading_symbol, // Map backend property to frontend expectation
            exchange: activeTab // Use activeTab instead of hardcoded 'NFO'
          };
          allSymbols.push(normalizedCE);
          console.log(`✅ Added CE ${strike}:`, normalizedCE.tradingSymbol);
        } else {
          console.warn(`❌ CE not found for strike ${strike}`);
        }
        
        if (peOption) {
          // Fix property mapping: backend returns trading_symbol, frontend expects tradingSymbol
          const normalizedPE = {
            ...peOption,
            tradingSymbol: peOption.trading_symbol, // Map backend property to frontend expectation
            exchange: activeTab // Use activeTab instead of hardcoded 'NFO'
          };
          allSymbols.push(normalizedPE);
          console.log(`✅ Added PE ${strike}:`, normalizedPE.tradingSymbol);
        } else {
          console.warn(`❌ PE not found for strike ${strike}`);
        }
      });      console.log(`✅ Total symbols to add: ${allSymbols.length} (${targetStrikes.length} strikes × 2 types)`);

      if (allSymbols.length > 0) {
        // CRITICAL FIX: Only clear options for the CURRENT sub-tab (symbolName)
        // This allows multiple sub-tabs to maintain their own independent CE/PE data
        console.log(`🧹 Clearing existing NFO options for sub-tab: ${symbolName}`);
        
        // Remove ONLY options for the current symbolName (current sub-tab)
        const currentSubTabOptions = watchlist.filter(item => 
          item.exchange === activeTab && 
          item.tradingSymbol && 
          item.tradingSymbol.startsWith(symbolName)
        );
        
        console.log(`Found ${currentSubTabOptions.length} existing NFO options for ${symbolName} to remove`);
        
        // Remove all symbols in parallel for instant removal
        const removePromises = currentSubTabOptions.map(async (option) => {
          try {
            await handleRemoveFromWatchlist(option);
          } catch (removeError) {
            console.error(`Error removing ${option.tradingSymbol}:`, removeError);
          }
        });
        
        // Wait for all removals to complete
        await Promise.all(removePromises);
        
        // Minimal delay for Redux state stabilization
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Quick verification
        const freshWatchlistAfterRemoval = store.getState().watchlist.items;
        const remainingForSubTab = freshWatchlistAfterRemoval.filter(item => 
          item.exchange === activeTab && 
          item.tradingSymbol &&
          item.tradingSymbol.startsWith(symbolName)
        );
        console.log(`✅ Removal complete: ${currentSubTabOptions.length - remainingForSubTab.length}/${currentSubTabOptions.length} removed`);
        
        console.log(`➕ Adding ${allSymbols.length} new options to watchlist`);
        
        // Add all symbols at once for instant UI update
        let addedCount = 0;
        
        // Add all symbols in parallel for instant display
        const addPromises = allSymbols.map(async (symbol) => {
          try {
            await handleAddToWatchlist(symbol);
            addedCount++;
            console.log(`✅ Added ${symbol.tradingSymbol} (${addedCount}/${allSymbols.length})`);
          } catch (error) {
            console.error(`❌ Failed to add ${symbol.tradingSymbol}:`, error);
          }
        });
        
        // Wait for all additions to complete
        await Promise.all(addPromises);
        
        console.log(`✅ Successfully added ${addedCount} of ${allSymbols.length} options`);
        
        // Set filtered symbols immediately without waiting for database operations
        setFilteredSymbols([...allSymbols]);
        
        // Save chain info for header display (store both formatted and raw date)
        const expiryLabel = availableExpiries.find(e => e.date === expiryDate)?.formatted || expiryDate;
        setSelectedChainInfo({
          symbolName: symbolName,
          expiryDate: expiryLabel,        // Formatted date for display
          expiryDateRaw: expiryDate,       // Raw date (YYYY-MM-DD) for API comparisons
          centerStrike: centerStrike.toLocaleString()
        });
        
        console.log(`🎉 Smart Options Chain Complete: ${availableTargetStrikes.length} strikes created with ${addedCount} total options for ${symbolName}`);
        
        // Clear the building flag BEFORE batch subscription
        window.isBuildingOptionsChain = false;
        
        // Batch subscribe to WebSocket for all added symbols at once
        if (wsConnected && addedCount > 0) {
          console.log(`📡 Batch subscribing to ${addedCount} options in WebSocket...`);
          
          // Get fresh watchlist to ensure we have all just-added symbols
          const freshWatchlist = store.getState().watchlist.items;
          const justAddedSymbols = freshWatchlist.filter(item => 
            item.exchange === activeTab && 
            item.tradingSymbol?.startsWith(symbolName)
          );
          
          // Subscribe to price updates
          const symbols = justAddedSymbols.map(item => ({
            exchange: item.exchange,
            token: item.instrumentId
          }));
          
          if (symbols.length > 0) {
            aliceBlueWS.subscribe(symbols);
            console.log(`✅ Batch subscribed to ${symbols.length} price feeds`);
          }
          
          // Subscribe to depth data for options
          const depthSymbols = justAddedSymbols.map(item => ({
            exchange: item.exchange,
            token: item.instrumentId,
            tradingSymbol: item.tradingSymbol
          }));
          
          if (depthSymbols.length > 0) {
            aliceBlueWS.subscribeDepth(depthSymbols);
            console.log(`✅ Batch subscribed to ${depthSymbols.length} depth feeds`);
          }
        }
        
        // Wait longer for all Redux actions to be processed
        const timeoutId = setTimeout(() => {
          try {
            // Force immediate display update with current Redux state
            // Use store.getState() to get fresh state instead of stale closure
            const freshWatchlist = store.getState().watchlist.items;
            // CRITICAL FIX: Only display options for the current symbolName (sub-tab)
            const currentSubTabItems = freshWatchlist.filter(item => 
              item.exchange === activeTab && 
              item.tradingSymbol?.startsWith(symbolName)
            );
            setFilteredSymbols([...currentSubTabItems]);
            console.log(`📊 Immediately displaying ${currentSubTabItems.length} options for ${symbolName} from fresh Redux state`);
            console.log(`🔍 Fresh Redux state check: ${symbolName}=${currentSubTabItems.length}`);
            
            if (currentSubTabItems.length === addedCount) {
              console.log(`✅ State synchronization successful for ${symbolName}!`);
            } else {
              console.warn(`⚠️ State sync issue for ${symbolName}: Expected ${addedCount}, got ${currentSubTabItems.length}`);
              console.log('🔄 Attempting final state refresh...');
              // One more attempt with a fresh read
              const finalFreshWatchlist = store.getState().watchlist.items;
              const finalSubTabItems = finalFreshWatchlist.filter(item => 
                item.exchange === activeTab && 
                item.tradingSymbol?.startsWith(symbolName)
              );
              setFilteredSymbols([...finalSubTabItems]);
              console.log(`📊 Final display update: ${finalSubTabItems.length} options for ${symbolName} from Redux`);
            }
          } catch (timeoutError) {
            console.error('Error in state sync timeout:', timeoutError);
            // Don't crash - just log the error and continue
            window.isBuildingOptionsChain = false;
          }
        }, 2000); // Increased from 0 to 2000ms
        
        // Cleanup function in case component unmounts
        return () => clearTimeout(timeoutId);
        
        // Do NOT auto-collapse here - user may want to adjust filters
        // setIsFilterCollapsed(true);
      } else {
        alert('No symbols found with the selected criteria.');
        setFilteredSymbols([]);
        window.isBuildingOptionsChain = false;
      }
    } catch (error) {
      console.error('Error searching filtered symbols:', error);
      window.isBuildingOptionsChain = false; // Clear flag on error
      setFilteredSymbols([]); // Clear symbols on error
      alert('Error searching symbols: ' + error.message);
    }
  };
  
  const clearFilters = () => {
    setSymbolFilters({
      symbolName: '',
      expiryDate: '',
      optionType: '',
      strikePrice: ''
    });
    setAvailableExpiries([]);
    setAvailableStrikes([]);
    setFilteredSymbols([]);
  };

  const handleBaseStrikeChange = async (newBaseStrike) => {
    const strikeValue = parseInt(newBaseStrike.replace(/,/g, ''));
    
    console.log(`🎯 Header Base Strike Change Triggered: ${newBaseStrike} → ${strikeValue}`);
    console.log('📋 Current chain info:', selectedChainInfo);
    console.log('🔄 Building status:', window.isBuildingOptionsChain);
    
    if (isNaN(strikeValue)) {
      console.log('❌ Invalid strike value (NaN)');
      alert('Please enter a valid numeric strike price');
      return;
    }
    
    if (!selectedChainInfo) {
      console.log('❌ No chain info available');
      alert('Please build an options chain first');
      return;
    }

    // Guard against multiple simultaneous builds
    if (window.isBuildingOptionsChain) {
      console.log('⏸️ Already building options chain, ignoring header change');
      return;
    }

    console.log(`✅ Processing strike change: ${strikeValue}`);

    // Create the filters object to pass directly to handleApplyFilters
    const updatedFilters = {
      symbolName: selectedChainInfo.symbolName,
      expiryDate: selectedChainInfo.expiryDateRaw, // Use raw date for API
      optionType: '', // Keep empty for both CE and PE
      strikePrice: strikeValue.toString(),
      exchange: activeTab // Use activeTab instead of hardcoded 'NFO'
    };

    console.log('📝 Passing filters to handleApplyFilters:', updatedFilters);
    
    // Set building flag and add small delay to ensure state is updated
    window.isBuildingOptionsChain = true;
    console.log(`🚀 Rebuilding options chain with center strike: ${strikeValue}`);
    
    setTimeout(async () => {
      try {
        await handleApplyFilters(updatedFilters); // Pass filters directly
        setEditableBaseStrike(''); // Clear only after successful completion
        
        // Update the state to match what was used
        setSymbolFilters(updatedFilters);
      } catch (error) {
        console.error('❌ Error rebuilding options chain:', error);
        window.isBuildingOptionsChain = false;
      }
    }, 100);
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    
    if (!orderData.symbol || !orderData.quantity) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const orderPayload = {
        exchange: orderData.symbol.exchange,
        tradingSymbol: orderData.symbol.tradingSymbol,
        instrumentId: orderData.symbol.instrumentId,
        transactionType: orderData.action, // Fix: use transactionType instead of action
        quantity: parseInt(orderData.quantity),
        price: orderData.orderType === 'MARKET' ? 0 : parseFloat(orderData.price),
        orderType: orderData.orderType,
        validity: orderData.validity,
        product: orderData.product,
        orderComplexity: 'REGULAR', // Add missing field
        deviceId: '123', // Add missing field
        userId: userId // Add userId
      };

      const response = await api.post('/orders/place', orderPayload);
      
      if (response.data.success) {
        setPopupMessage(`Order placed successfully! Order ID: ${response.data.data.brokerOrderId || 'N/A'}`);
        setPopupType('success');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 2000);
        // Reset form
        setOrderFormVisible(false);
        setOrderData({
          symbol: null,
          action: 'BUY',
          quantity: '',
          price: '',
          orderType: 'LIMIT',
          validity: 'DAY',
          product: 'MIS'
        });
      } else {
        setPopupMessage('Order failed: ' + (response.data.error || 'Unknown error'));
        setPopupType('error');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
      }
    } catch (error) {
      console.error('Order placement error:', error);
      setPopupMessage('Failed to place order: ' + (error.response?.data?.error || error.message));
      setPopupType('error');
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    }
  };

  const handleCancelOrder = () => {
    setOrderFormVisible(false);
    setOrderData({
      symbol: null,
      action: 'BUY',
      quantity: '',
      price: '',
      orderType: 'LIMIT',
      validity: 'DAY',
      product: 'MIS'
    });
  };

  const getLivePrice = (exchange, token) => {
    const key = `${exchange}|${token}`;
    const priceData = livePrices[key];
    const livePrice = priceData?.ltp || priceData?.lp;
    
    // If live price exists and is greater than 0, return it
    if (livePrice && livePrice > 0) {
      return livePrice;
    }
    
    // If no LTP, check for close price from WebSocket data
    if (priceData?.c && priceData.c > 0) {
      console.log(`🔍 Using close price for ${key}:`, priceData.c);
      return priceData.c;
    }
    
    // Otherwise, try to get last traded price from watchlist item
    const watchlistItem = watchlist.find(item => 
      item.exchange === exchange && item.instrumentId === token
    );
    
    // Return close price or last price from watchlist data
    const fallbackPrice = watchlistItem?.closePrice || watchlistItem?.lastPrice || null;
    if (fallbackPrice) {
      console.log(`🔍 Using watchlist fallback for ${key}:`, fallbackPrice);
    } else {
      console.log(`⚠️ No price data available for ${key}`, { priceData, watchlistItem });
    }
    
    return fallbackPrice;
  };

  // Helper function to get high price from WebSocket data
  const getHighPrice = (exchange, token) => {
    const key = `${exchange}|${token}`;
    const priceData = livePrices[key];
    const liveHighValue = priceData?.h;
    
    // If live high price exists and is greater than 0, store and return it
    if (liveHighValue && liveHighValue > 0) {
      // Store the high value for future reference
      if (!priceData._storedHigh || liveHighValue > priceData._storedHigh) {
        priceData._storedHigh = liveHighValue;
      }
      return liveHighValue;
    }
    
    // Return previously stored high value if available
    if (priceData?._storedHigh && priceData._storedHigh > 0) {
      return priceData._storedHigh;
    }
    
    // Try to get high from watchlist item as fallback
    const watchlistItem = watchlist.find(item => 
      item.exchange === exchange && item.instrumentId === token
    );
    
    const fallbackHigh = watchlistItem?.highPrice || null;
    if (fallbackHigh && fallbackHigh > 0) {
      console.log(`🔍 Using watchlist fallback high for ${key}:`, fallbackHigh);
      return fallbackHigh;
    }
    
    // Debug logging
    if (!priceData?._highLogged) {
      console.log(`⚠️ No high price for ${key}. Available:`, {
        liveHigh: liveHighValue,
        storedHigh: priceData?._storedHigh,
        watchlistHigh: fallbackHigh,
        allFields: priceData ? Object.keys(priceData) : 'no data'
      });
      if (priceData) priceData._highLogged = true;
    }
    
    return null;
  };

  // Helper function to get low price from WebSocket data
  const getLowPrice = (exchange, token) => {
    const key = `${exchange}|${token}`;
    const priceData = livePrices[key];
    const liveLowValue = priceData?.l;
    
    // If live low price exists and is greater than 0, store and return it
    if (liveLowValue && liveLowValue > 0) {
      // Store the low value for future reference
      if (!priceData._storedLow || liveLowValue < priceData._storedLow) {
        priceData._storedLow = liveLowValue;
      }
      return liveLowValue;
    }
    
    // Return previously stored low value if available
    if (priceData?._storedLow && priceData._storedLow > 0) {
      return priceData._storedLow;
    }
    
    // Try to get low from watchlist item as fallback
    const watchlistItem = watchlist.find(item => 
      item.exchange === exchange && item.instrumentId === token
    );
    
    const fallbackLow = watchlistItem?.lowPrice || null;
    if (fallbackLow && fallbackLow > 0) {
      console.log(`🔍 Using watchlist fallback low for ${key}:`, fallbackLow);
      return fallbackLow;
    }
    
    // Debug logging
    if (!priceData?._lowLogged) {
      console.log(`⚠️ No low price for ${key}. Available:`, {
        liveLow: liveLowValue,
        storedLow: priceData?._storedLow,
        watchlistLow: fallbackLow,
        allFields: priceData ? Object.keys(priceData) : 'no data'
      });
      if (priceData) priceData._lowLogged = true;
    }
    
    return null;
  };

  const getPriceChange = (exchange, token) => {
    const key = `${exchange}|${token}`;
    const change = livePrices[key]?.pc;
    // Return the change if it exists, otherwise return null
    return (change !== undefined && change !== null) ? change : null;
  };

  // Helper function to get absolute change value
  const getChangeValue = (exchange, token) => {
    const key = `${exchange}|${token}`;
    const priceData = livePrices[key];
    
    // Debug logging to see what's available in priceData
    if (priceData && Object.keys(priceData).length > 0) {
      console.log(`🔍 Price data for ${key}:`, {
        cv: priceData.cv,
        ltp: priceData.ltp || priceData.lp,
        c: priceData.c,
        pc: priceData.pc,
        allFields: Object.keys(priceData)
      });
    }
    
    // Try to get change value directly from WebSocket (cv field)
    if (priceData?.cv !== undefined && priceData?.cv !== null && priceData.cv !== 0) {
      console.log(`✅ Found CV for ${key}:`, priceData.cv);
      return priceData.cv;
    }
    
    // Fallback: Calculate from LTP and close price
    const ltp = getLivePrice(exchange, token);
    const closePrice = priceData?.c;
    
    if (ltp && closePrice && ltp !== closePrice) {
      const calculatedChange = ltp - closePrice;
      console.log(`📊 Calculated change for ${key}: LTP=${ltp}, Close=${closePrice}, Change=${calculatedChange}`);
      return calculatedChange;
    }
    
    console.log(`⚠️ No change value available for ${key}`);
    return null;
  };

  // Handle quick order placement
  const handleQuickOrder = async (strikeData, action, optionType) => {
    if (!strikeData) return;

    try {
      const qty = optionType === 'CE' ? parseInt(ceQty) : parseInt(peQty);
      const livePrice = getLivePrice(strikeData.exchange, strikeData.instrumentId);
      const price = livePrice || 0;

      if (!qty || isNaN(qty)) {
        setPopupMessage('Please enter valid quantity');
        setPopupType('error');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
        return;
      }

      if (orderType === 'LIMIT' && (!price || price <= 0)) {
        setPopupMessage('Price not available for LIMIT order');
        setPopupType('error');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
        return;
      }

      const orderPayload = {
        exchange: strikeData.exchange,
        tradingSymbol: strikeData.tradingSymbol,
        instrumentId: strikeData.instrumentId,
        transactionType: action,
        quantity: qty,
        price: orderType === 'MARKET' ? 0 : price,
        orderType: orderType,
        validity: 'DAY',
        product: productType,
        orderComplexity: 'REGULAR',
        deviceId: '123',
        userId: userId
      };

      const response = await api.post('/orders/place', orderPayload);
      
      if (response.data.success) {
        setPopupMessage(`${action} order placed successfully!`);
        setPopupType('success');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 2000);
        // Store last direction for Re Enter functionality
        localStorage.setItem(`lastDirection_${optionType}`, action);
        // Refresh positions
        fetchPositions();
      } else {
        setPopupMessage('Order failed: ' + (response.data.error || 'Unknown error'));
        setPopupType('error');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
      }
    } catch (error) {
      console.error('Order placement error:', error);
      setPopupMessage('Failed to place order: ' + (error.response?.data?.error || error.message));
      setPopupType('error');
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    }
  };

  // Fetch positions from API (called after order placement)
  const fetchPositions = async () => {
    try {
      const response = await api.get('/portfolio/positions');
      if (response.data.success && response.data.data) {
        // Filter and update raw positions
        const filtered = response.data.data.filter(pos => {
          if (activeTab === 'NFO' && nfoSubTab) {
            return pos.exchange === 'NFO' && pos.tradingSymbol?.startsWith(nfoSubTab);
          } else if (activeTab === 'BFO' && bfoSubTab) {
            return pos.exchange === 'BFO' && pos.tradingSymbol?.startsWith(bfoSubTab);
          } else if (activeTab === 'MCX' && mcxSubTab) {
            return pos.exchange === 'MCX' && pos.tradingSymbol?.startsWith(mcxSubTab);
          }
          return pos.exchange === activeTab;
        });
        setRawPositions(filtered);
      }
    } catch (error) {
      console.error('Error fetching positions:', error);
    }
  };

  // Calculate total MTM from DayP&L
  const calculateTotalMTM = () => {
    if (positions.length === 0) return '0.00';
    const total = positions.reduce((sum, pos) => sum + parseFloat(pos.dayPnl || 0), 0);
    return total.toFixed(2);
  };

  // Handle exit position
  const handleExitPosition = async (position) => {
    console.log('🚪 Exit position called with:', position);
    console.log('🚪 Raw position data:', position.rawData);
    
    const positionQty = parseInt(position.qty);
    if (positionQty === 0) return;
    
    const exitAction = positionQty > 0 ? 'SELL' : 'BUY';
    const exitQty = Math.abs(positionQty);
    
    // Show confirmation only for LIMIT orders, skip for MARKET orders
    let confirmed = true;
    let exitPrice = 0;
    
    if (orderType === 'LIMIT') {
      const confirmExit = () => {
        return new Promise((resolve) => {
          setConfirmationModal({
            show: true,
            title: `${exitAction} Position Exit`,
            message: `${position.instrument}`,
            editablePrice: position.ltp || 0,
            details: {
              strike: position.instrument.includes('CE') || position.instrument.includes('PE') 
                ? position.instrument.match(/(\d+)(CE|PE)$/)?.[1] || ''
                : '',
              optionType: position.instrument.includes('CE') ? 'CE' 
                : position.instrument.includes('PE') ? 'PE' : '',
              ltp: `${position.ltp || ''}`,
              quantity: `${exitQty}`,
              orderType: orderType,
              product: position.rawData?.product || position.rawData?.prd || productType,
              exchange: position.rawData?.exchange || 'NFO',
              symbol: position.rawData?.tradingSymbol || position.rawData?.tradingsymbol || position.instrument
            },
            onConfirm: (modifiedPrice) => {
              exitPrice = modifiedPrice || position.ltp || 0;
              setConfirmationModal({ show: false });
              resolve(true);
            },
            onCancel: () => {
              setConfirmationModal({ show: false });
              resolve(false);
            }
          });
        });
      };
      
      confirmed = await confirmExit();
      if (!confirmed) return;
    }

    try {
      // Get instrument ID from raw position data
      const instrumentId = position.rawData?.token || 
                          position.rawData?.instrumentId || 
                          position.rawData?.instrument_token ||
                          position.rawData?.instId;
      
      const tradingSymbol = position.rawData?.tradingSymbol || 
                           position.rawData?.tradingsymbol || 
                           position.instrument;
      
      const exchange = position.rawData?.exchange || 'NFO';
      
      if (!instrumentId) {
        console.error('Missing instrumentId in position data:', position);
        setPopupMessage('Error: Could not find instrument ID for this position');
        setPopupType('error');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
        return;
      }
      
      const orderPayload = {
        exchange: exchange,
        tradingSymbol: tradingSymbol,
        instrumentId: instrumentId,
        transactionType: exitAction,
        quantity: exitQty,
        price: orderType === 'LIMIT' ? exitPrice : 0,
        orderType: orderType,
        validity: 'DAY',
        product: position.rawData?.product || position.rawData?.prd || productType,
        orderComplexity: 'REGULAR',
        deviceId: '123',
        userId: userId
      };

      console.log(`🚪 Exit order payload:`, orderPayload);

      const response = await api.post('/orders/place', orderPayload);
      
      if (response.data.success) {
        setPopupMessage(`Exit order placed: ${exitAction} ${exitQty} qty of ${position.instrument}`);
        setPopupType('success');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 2000);
        fetchPositions();
      } else {
        setPopupMessage('Exit order failed: ' + (response.data.error || 'Unknown error'));
        setPopupType('error');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
      }
    } catch (error) {
      console.error('Exit order error:', error);
      setPopupMessage('Failed to place exit order: ' + (error.response?.data?.error || error.message));
      setPopupType('error');
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    }
  };

  // Format instrument name to short version (e.g., NIFTY23DEC25C26000 -> NIFTYC26000)
  const formatInstrumentName = (instrument) => {
    if (!instrument) return '-';
    
    // NFO format: NIFTY23DEC25C26000 -> NIFTYC26000
    // Remove date pattern (e.g., 23DEC25, 02JAN26, etc.)
    let formatted = instrument.replace(/\d{2}[A-Z]{3}\d{2}/, '');
    
    // BFO format: SENSEX25D0475700CE -> SENSEX75700CE
    formatted = formatted.replace(/\d{2}D\d{2}/, '');
    
    return formatted;
  };

  // Handle Re Trade button click
  const handleReTrade = async (action) => {
    if (!reTradeModal.position) return;
    
    const pos = reTradeModal.position;
    const quantity = reTradeModal.editableQty || Math.abs(parseInt(pos.qty)) || 25; // Use editable qty or default
    
    try {
      // Get instrument ID from raw position data
      const instrumentId = pos.rawData?.token || 
                          pos.rawData?.instrumentId || 
                          pos.rawData?.instrument_token ||
                          pos.rawData?.instId;
      
      const tradingSymbol = pos.rawData?.tradingSymbol || 
                           pos.rawData?.tradingsymbol || 
                           pos.instrument;
      
      const exchange = pos.rawData?.exchange || 'NFO';
      
      if (!instrumentId) {
        console.error('Missing instrumentId in position data:', pos);
        setPopupMessage('Error: Could not find instrument ID for this position');
        setPopupType('error');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
        setReTradeModal({ show: false, position: null, action: 'BUY', editablePrice: null, editableQty: null });
        return;
      }
      
      const orderPayload = {
        exchange: exchange,
        tradingSymbol: tradingSymbol,
        instrumentId: instrumentId,
        transactionType: action,
        quantity: quantity,
        price: orderType === 'LIMIT' ? (reTradeModal.editablePrice || pos.ltp || 0) : 0,
        orderType: orderType,
        validity: 'DAY',
        product: pos.rawData?.product || pos.rawData?.prd || productType,
        orderComplexity: 'REGULAR',
        deviceId: '123',
        userId: userId
      };

      console.log(`🔄 Re-Trade order payload:`, orderPayload);

      const response = await api.post('/orders/place', orderPayload);
      
      if (response.data.success) {
        setPopupMessage(`Re-Trade order placed: ${action} ${quantity} qty of ${pos.instrument}`);
        setPopupType('success');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 2000);
        setReTradeModal({ show: false, position: null, action: 'BUY', editablePrice: null, editableQty: null });
        fetchPositions();
      } else {
        setPopupMessage('Re-Trade order failed: ' + (response.data.error || 'Unknown error'));
        setPopupType('error');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
        setReTradeModal({ show: false, position: null, action: 'BUY', editablePrice: null, editableQty: null });
      }
    } catch (error) {
      console.error('Re-Trade order error:', error);
      setPopupMessage('Failed to place re-trade order: ' + (error.response?.data?.error || error.message));
      setPopupType('error');
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
      setReTradeModal({ show: false, position: null, action: 'BUY', editablePrice: null, editableQty: null });
    }
  };

  // Store raw positions data separately
  const [rawPositions, setRawPositions] = useState([]);

  // Fetch positions from API (infrequent - only when needed)
  useEffect(() => {
    const fetchPositionsData = async () => {
      try {
        const response = await api.get('/portfolio/positions');
        console.log('📍 Positions API Response:', response.data);
        
        if (response.data.success && response.data.data) {
          console.log('📍 Raw positions count:', response.data.data.length);
          
          // Filter only Options (NFO, BFO) - exclude NSE, BSE, MCX and Futures
          const filtered = response.data.data.filter(pos => {
            console.log(`📍 Checking position: ${pos.tradingsymbol || pos.tradingSymbol}, exchange: ${pos.exchange}`);
            
            // Only include NFO and BFO exchanges
            if (pos.exchange !== 'NFO' && pos.exchange !== 'BFO') {
              console.log(`  ❌ Excluded: not NFO/BFO`);
              return false;
            }
            
            // Exclude Futures - only include Options
            const symbol = pos.tradingsymbol || pos.tradingSymbol || '';
            
            // Futures patterns: contains "FUT" or ends with monthly contract format (e.g., NIFTY30DEC25F, BANKNIFTY25DEC24F)
            const isFuture = symbol.includes('FUT') || /\d{2}[A-Z]{3}\d{2}F$/i.test(symbol);
            
            // Options patterns: ends with CE, PE, C followed by digits, or P followed by digits
            const isOption = /CE$/i.test(symbol) || /PE$/i.test(symbol) || /C\d+$/i.test(symbol) || /P\d+$/i.test(symbol);
            
            console.log(`  isFuture: ${isFuture}, isOption: ${isOption}`);
            
            // Exclude futures, include only options
            if (isFuture || !isOption) {
              console.log(`  ❌ Excluded: future or not option`);
              return false;
            }
            
            // Further filter by active tab and sub-tab
            if (activeTab === 'NFO' && nfoSubTab) {
              const match = pos.exchange === 'NFO' && (pos.tradingsymbol || pos.tradingSymbol)?.startsWith(nfoSubTab);
              console.log(`  NFO with subTab ${nfoSubTab}: ${match}`);
              return match;
            } else if (activeTab === 'BFO' && bfoSubTab) {
              const match = pos.exchange === 'BFO' && (pos.tradingsymbol || pos.tradingSymbol)?.startsWith(bfoSubTab);
              console.log(`  BFO with subTab ${bfoSubTab}: ${match}`);
              return match;
            } else if (activeTab === 'MCX' && mcxSubTab) {
              const match = pos.exchange === 'MCX' && (pos.tradingsymbol || pos.tradingSymbol)?.startsWith(mcxSubTab);
              console.log(`  MCX with subTab ${mcxSubTab}: ${match}`);
              return match;
            }
            
            const match = pos.exchange === activeTab;
            console.log(`  Basic exchange match (${activeTab}): ${match}`);
            return match;
          });
          
          console.log('📍 Filtered positions count:', filtered.length);
          setRawPositions(filtered);
        } else if (response.data.sessionExpired) {
          // Session expired - show alert and redirect to login
          alert('Your session has expired. Please login again.');
          window.location.href = '/login';
        } else if (response.data.warning) {
          console.warn('Positions API warning:', response.data.warning);
        }
      } catch (error) {
        console.error('Error fetching positions:', error);
        // Check if it's a 401 error (session expired)
        if (error.response?.status === 401) {
          alert('Your session has expired. Please login again.');
          window.location.href = '/login';
        }
      }
    };

    if (activeTab === 'NFO' || activeTab === 'BFO' || activeTab === 'MCX') {
      fetchPositionsData();
      const interval = setInterval(fetchPositionsData, 10000); // Refresh every 10 seconds
      return () => clearInterval(interval);
    }
  }, [activeTab, nfoSubTab, bfoSubTab, mcxSubTab]);

  // Update positions display with live prices (frequent - whenever prices change)
  useEffect(() => {
    if (rawPositions.length > 0) {
      // Debug: Log first position to see field names
      console.log('Raw Position Data Sample:', rawPositions[0]);
      
      const transformedPositions = rawPositions.map(pos => {
        // Get live price from WebSocket using trading symbol
        // First try to get instrument ID from position data
        const instrumentId = pos.token || pos.instrumentId || pos.instrument_token;
        const key = `${pos.exchange}|${instrumentId}`;
        const priceData = livePrices[key];
        
        // Get LTP - prioritize WebSocket live price, then use last known position LTP as fallback
        // This ensures we always show the last traded price even if WebSocket is not updating
        let ltp = 0;
        if (priceData?.ltp) {
          ltp = priceData.ltp;
        } else if (priceData?.lp) {
          ltp = priceData.lp;
        } else if (pos.ltp) {
          ltp = parseFloat(pos.ltp);
        }
        
        console.log(`Position ${pos.tradingSymbol}: key=${key}, WebSocket LTP=${priceData?.ltp}, Position LTP=${pos.ltp}, Final LTP=${ltp}`);
        
        // Get buy and sell quantities - AliceBlue uses buyQty/sellQty
        const buyQty = parseFloat(pos.buyQuantity || pos.buyQty || pos.buyqty || pos.dayBuyQuantity || 0);
        const sellQty = parseFloat(pos.sellQuantity || pos.sellQty || pos.sellqty || pos.daySellQuantity || 0);
        const netQty = parseInt(pos.netQuantity || pos.netQty || pos.netqty || (buyQty - sellQty));
        
        // Get buy and sell average prices from AliceBlue API
        // AliceBlue API fields: buyPrice (buy avg), sellPrice (sell avg), netAveragePrice (net avg)
        let buyAvg = 0;
        let sellAvg = 0;
        
        // Buy average - priority order:
        // 1. buyPrice field (actual buy average from API)
        // 2. Calculate from dayBuyPrice / buyQuantity
        // 3. Use netAveragePrice for long positions
        if (pos.buyPrice && parseFloat(pos.buyPrice) > 0) {
          buyAvg = parseFloat(pos.buyPrice);
        } else if (pos.mtmBuyPrice && parseFloat(pos.mtmBuyPrice) > 0) {
          buyAvg = parseFloat(pos.mtmBuyPrice);
        } else if (buyQty > 0 && pos.dayBuyPrice && parseFloat(pos.dayBuyPrice) > 0) {
          buyAvg = parseFloat(pos.dayBuyPrice) / buyQty;
        } else if (buyQty > 0 && pos.netAveragePrice && netQty > 0) {
          buyAvg = parseFloat(pos.netAveragePrice || pos.netAvgPrice);
        }
        
        // Sell average - priority order:
        // 1. sellPrice field (actual sell average from API)
        // 2. Calculate from daySellPrice / sellQuantity
        // 3. Use netAveragePrice for short positions
        if (pos.sellPrice && parseFloat(pos.sellPrice) > 0) {
          sellAvg = parseFloat(pos.sellPrice);
        } else if (pos.mtmSellprice && parseFloat(pos.mtmSellprice) > 0) {
          sellAvg = parseFloat(pos.mtmSellprice);
        } else if (sellQty > 0 && pos.daySellPrice && parseFloat(pos.daySellPrice) > 0) {
          sellAvg = parseFloat(pos.daySellPrice) / sellQty;
        } else if (sellQty > 0 && pos.netAveragePrice && netQty < 0) {
          sellAvg = parseFloat(pos.netAveragePrice || pos.netAvgPrice);
        }
        
        // // Debug: Log calculation details
        // console.log(`📊 Price Calc for ${pos.tradingSymbol || pos.tradingsymbol}:`, {
        //   buyQty,
        //   sellQty,
        //   netQty,
        //   buyPrice: pos.buyPrice,
        //   sellPrice: pos.sellPrice,
        //   mtmBuyPrice: pos.mtmBuyPrice,
        //   mtmSellprice: pos.mtmSellprice,
        //   dayBuyPrice: pos.dayBuyPrice,
        //   daySellPrice: pos.daySellPrice,
        //   netAveragePrice: pos.netAveragePrice || pos.netAvgPrice,
        //   calculatedBuyAvg: buyAvg,
        //   calculatedSellAvg: sellAvg
        // });
        
        // Calculate Day P&L using Excel formula
        // Formula: (SellAVG - BuyAVG) * MIN(BuyQTY, SellQTY) + Unrealized P&L
        let dayPnl = 0;
        
        // Step 1: Calculate Realized P&L for closed quantity
        const closedQty = Math.min(buyQty, sellQty);
        const realizedPnl = (sellAvg - buyAvg) * closedQty;
        
        // Step 2: Calculate Unrealized P&L for remaining open quantity
        let unrealizedPnl = 0;
        if (buyQty > sellQty) {
          // Long position: (LTP - BuyAVG) * (BuyQTY - SellQTY)
          unrealizedPnl = (ltp - buyAvg) * (buyQty - sellQty);
        } else if (sellQty > buyQty) {
          // Short position: (SellAVG - LTP) * (SellQTY - BuyQTY)
          unrealizedPnl = (sellAvg - ltp) * (sellQty - buyQty);
        }
        // else: buyQty === sellQty, unrealized = 0 (fully closed)
        
        // Step 3: Total Day P&L = Realized + Unrealized
        dayPnl = realizedPnl + unrealizedPnl;
        
        const netPnl = parseFloat(pos.unrealizedPnl || pos.netPnL || pos.pnl || pos.unrealizedpnl || 0);
        
        // Debug log to see what fields are available
        if (Object.keys(pos).length > 0) {
          console.log('Position fields for DayP&L:', {
            realizedPnl: pos.realizedPnl,
            realizedpnl: pos.realizedpnl,
            realised_pnl: pos.realised_pnl,
            dayBuyValue: pos.dayBuyValue,
            daySellValue: pos.daySellValue,
            availableFields: Object.keys(pos).filter(key => key.toLowerCase().includes('pnl') || key.toLowerCase().includes('realized'))
          });
        }
        
        // Calculate MTM based on net quantity and LTP
        let mtm = 0;
        if (netQty !== 0) {
          const avgPrice = netQty > 0 ? buyAvg : sellAvg;
          mtm = (ltp - avgPrice) * Math.abs(netQty);
        }
        
        // Product type (NRML, MIS, CNC)
        const productType = pos.product || pos.prd || 'NRML';
        
        // Extract option type from trading symbol (CE/PE)
        let optionType = '';
        if (pos.tradingSymbol || pos.tradingsymbol) {
          const symbol = pos.tradingSymbol || pos.tradingsymbol;
          if (symbol.includes('CE')) optionType = 'CE';
          else if (symbol.includes('PE')) optionType = 'PE';
        }
        
        return {
          type: productType,
          instrument: pos.displayName || pos.tradingSymbol || pos.tradingsymbol,
          optionType: optionType,
          qty: netQty,  // Keep signed quantity: positive for buy, negative for sell
          buyQty: buyQty,
          sellQty: sellQty,
          buyAvg: buyAvg ? buyAvg.toFixed(2) : '0.00',
          sellAvg: sellAvg ? sellAvg.toFixed(2) : '0.00',
          ltp: ltp > 0 ? ltp.toFixed(2) : (pos.ltp || '0.00'),
          dayPnl: dayPnl.toFixed(2),
          netPnl: netPnl.toFixed(2),
          mtm: mtm.toFixed(0),
          transaction: netQty > 0 ? 'Buy' : 'Sell',
          rawData: pos
        };
      });
      setPositions(transformedPositions);
      
      // Subscribe positions to WebSocket for live price updates
      if (transformedPositions.length > 0) {
        transformedPositions.forEach(pos => {
          if (pos.rawData && pos.rawData.token && pos.rawData.exchange) {
            const instrumentId = pos.rawData.token || pos.rawData.instrumentId;
            const exchange = pos.rawData.exchange;
            
            // Subscribe to WebSocket if not already subscribed
            if (wsConnected) {
              console.log(`Subscribing position to WebSocket: ${pos.rawData.tradingSymbol} (${exchange}|${instrumentId})`);
              // The WebSocket subscription will be handled by the existing watchlist subscription logic
              // We just need to make sure the position is in the livePrices tracking
            }
          }
        });
      }
    }
  }, [rawPositions, livePrices, wsConnected]);

  // Filter watchlist by active tab
  const getFilteredWatchlist = () => {
    if (activeTab === 'ALL') {
      return watchlist;
    }
    return watchlist.filter(item => item.exchange === activeTab);
  };

  // Split watchlist into CE and PE for NFO/BFO/MCX
  const getOptionsWatchlist = () => {
    const currentExchange = activeTab === 'NFO' ? 'NFO' : activeTab === 'BFO' ? 'BFO' : activeTab === 'MCX' ? 'MCX' : 'NFO';
    const currentSubTab = activeTab === 'NFO' ? nfoSubTab : activeTab === 'BFO' ? bfoSubTab : activeTab === 'MCX' ? mcxSubTab : '';
    
    let optionsWatchlist = watchlist.filter(item => item.exchange === currentExchange);
    
    // Filter by sub-tab if selected
    if (currentSubTab) {
      optionsWatchlist = optionsWatchlist.filter(item => 
        item.tradingSymbol.startsWith(currentSubTab)
      );
    }
    
    const ceOptions = optionsWatchlist.filter(item => {
      // Support both NFO format (C + digits) and BFO format (CE at end)
      return /C\d+$/.test(item.tradingSymbol) || /CE$/.test(item.tradingSymbol);
    }).sort((a, b) => {
      // Extract strike price for sorting - support both formats
      let strikeA = 0, strikeB = 0;
      
      // NFO format: ends with C followed by digits
      let match = a.tradingSymbol.match(/C(\d+)$/);
      if (match) {
        strikeA = parseInt(match[1]);
      } else {
        // BFO format: use parsed_strike or extract from symbol
        strikeA = a.parsed_strike || parseInt(a.tradingSymbol.match(/(\d+)CE$/)?.[1] || 0);
      }
      
      match = b.tradingSymbol.match(/C(\d+)$/);
      if (match) {
        strikeB = parseInt(match[1]);
      } else {
        // BFO format: use parsed_strike or extract from symbol
        strikeB = b.parsed_strike || parseInt(b.tradingSymbol.match(/(\d+)CE$/)?.[1] || 0);
      }
      
      return strikeA - strikeB; // Sort CE options in ascending order
    });
    
    const peOptions = optionsWatchlist.filter(item => {
      // Support both NFO format (P + digits) and BFO format (PE at end)
      return /P\d+$/.test(item.tradingSymbol) || /PE$/.test(item.tradingSymbol);
    }).sort((a, b) => {
      // Extract strike price for sorting - support both formats
      let strikeA = 0, strikeB = 0;
      
      // NFO format: ends with P followed by digits
      let match = a.tradingSymbol.match(/P(\d+)$/);
      if (match) {
        strikeA = parseInt(match[1]);
      } else {
        // BFO format: use parsed_strike or extract from symbol
        strikeA = a.parsed_strike || parseInt(a.tradingSymbol.match(/(\d+)PE$/)?.[1] || 0);
      }
      
      match = b.tradingSymbol.match(/P(\d+)$/);
      if (match) {
        strikeB = parseInt(match[1]);
      } else {
        // BFO format: use parsed_strike or extract from symbol
        strikeB = b.parsed_strike || parseInt(b.tradingSymbol.match(/(\d+)PE$/)?.[1] || 0);
      }
      
      return strikeB - strikeA; // Sort PE options in descending order
    });
    
    // Only log when building flag is not set to reduce console spam
    if (!window.isBuildingOptionsChain) {
      console.log(`📊 ${currentExchange} Options watchlist:`, {
        total: optionsWatchlist.length,
        ceOptions: ceOptions.length,
        peOptions: peOptions.length
      });
    }
    
    return { ceOptions, peOptions };
  };

  const filteredWatchlist = getFilteredWatchlist();
  const { ceOptions, peOptions } = getOptionsWatchlist();

  // Track MTM direction changes and show notifications
  useEffect(() => {
    // Calculate current MTM
    const currentCESum = ceOptions.reduce((sum, option) => {
      const ltp = getLivePrice(option.exchange, option.instrumentId);
      return sum + (ltp || 0);
    }, 0);
    
    const currentPESum = peOptions.reduce((sum, option) => {
      const ltp = getLivePrice(option.exchange, option.instrumentId);
      return sum + (ltp || 0);
    }, 0);
    
    const ceMTM = currentCESum - baselineCESum;
    const peMTM = currentPESum - baselinePESum;
    
    // Track CE direction
    if (baselineCESum > 0) {
      const newCEDirection = ceMTM > 5 ? 'up' : ceMTM < -5 ? 'down' : ceDirection;
      
      // Check if direction changed
      if (ceDirection && newCEDirection !== ceDirection && newCEDirection) {
        const now = Date.now();
        const notificationsDisabled = disableNotificationsUntil && now < disableNotificationsUntil;
        const key = `CE_${ceDirection}_to_${newCEDirection}_${Math.floor(now / 60000)}`; // Unique per minute
        
        // DISABLED: Direction notification for future use
        // if (!notificationsDisabled && !notificationsDismissed[key]) {
        //   const ceLTP = selectedCEStrike ? getLivePrice(selectedCEStrike.exchange, selectedCEStrike.instrumentId) : null;
        //   const ceStrike = selectedCEStrike?.strike || 'N/A';
        //   
        //   setDirectionNotificationData({
        //     type: newCEDirection === 'up' ? 'BUY' : 'SELL',
        //     message: newCEDirection === 'up' 
        //       ? `Market is Up trend - Please BUY`
        //       : `Market is Down trend - Please SELL`,
        //     symbol: `${ceStrike}CE`,
        //     ltp: ceLTP?.toFixed(2) || 'N/A',
        //     optionType: 'CE',
        //     strike: ceStrike,
        //     dismissKey: key
        //   });
        //   setShowDirectionNotification(true);
        // }
      }
      
      if (newCEDirection !== ceDirection) {
        setCEDirection(newCEDirection);
      }
    }
    
    // Track PE direction
    if (baselinePESum > 0) {
      const newPEDirection = peMTM > 5 ? 'up' : peMTM < -5 ? 'down' : peDirection;
      
      // Check if direction changed
      if (peDirection && newPEDirection !== peDirection && newPEDirection) {
        const now = Date.now();
        const notificationsDisabled = disableNotificationsUntil && now < disableNotificationsUntil;
        const key = `PE_${peDirection}_to_${newPEDirection}_${Math.floor(now / 60000)}`;
        
        // DISABLED: Direction notification for future use
        // if (!notificationsDisabled && !notificationsDismissed[key]) {
        //   const peLTP = selectedPEStrike ? getLivePrice(selectedPEStrike.exchange, selectedPEStrike.instrumentId) : null;
        //   const peStrike = selectedPEStrike?.strike || 'N/A';
        //   
        //   setDirectionNotificationData({
        //     type: newPEDirection === 'up' ? 'BUY' : 'SELL',
        //     message: newPEDirection === 'up' 
        //       ? `Market is Up trend - Please BUY`
        //       : `Market is Down trend - Please SELL`,
        //     symbol: `${peStrike}PE`,
        //     ltp: peLTP?.toFixed(2) || 'N/A',
        //     optionType: 'PE',
        //     strike: peStrike,
        //     dismissKey: key
        //   });
        //   setShowDirectionNotification(true);
        // }
      }
      
      if (newPEDirection !== peDirection) {
        setPEDirection(newPEDirection);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePrices, baselineCESum, baselinePESum, selectedCEStrike, selectedPEStrike, disableNotificationsUntil]);

  // Helper function to render watchlist table
  const renderWatchlistTable = (symbols, title, optionType = null) => {
    // Calculate total LTP sum
    const totalLTP = symbols.reduce((sum, item) => {
      const ltp = getLivePrice(item.exchange, item.instrumentId);
      return sum + (ltp || 0);
    }, 0);

    // Handle instant market order for hover buttons
    const handleInstantOrder = async (item, action, strikePrice, ltp) => {
      // Get quantity from Quick Order Entry based on option type
      const qty = optionType === 'CE' ? parseInt(ceQty) || 0 : parseInt(peQty) || 0;
      
      // Validation: Check if quantity is available
      if (!qty || qty <= 0) {
        setPopupMessage(`Please enter valid quantity in Quick Order Entry for ${optionType} options`);
        setPopupType('error');
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
        return;
      }
      
      // Calculate price based on order type
      const orderPrice = orderType === 'MARKET' ? 0 : (ltp || 0);
      
      // Show confirmation modal only for LIMIT orders, skip for MARKET orders
      let confirmed = true;
      let finalPrice = orderPrice;
      
      if (orderType === 'LIMIT') {
        const confirmOrder = () => {
          return new Promise((resolve) => {
            setConfirmationModal({
              show: true,
              title: `${action} Order Confirmation`,
              editablePrice: ltp || 0,
             
              details: {
                strike: strikePrice,
                optionType: optionType,
                ltp: `${ltp?.toFixed(2) || 'N/A'}`,
                quantity: `${qty} (From Quick Order Entry)`,
                orderType: orderType,
                product: productType,
                exchange: item.exchange,
                symbol: item.tradingSymbol
              },
             
              onConfirm: (modifiedPrice) => {
                finalPrice = modifiedPrice || ltp || 0;
                setConfirmationModal({ show: false });
                resolve(true);
             
              },
                onCancel: () => {
                setConfirmationModal({ show: false });
                resolve(false);
              }
            });
          });
        };
        
        confirmed = await confirmOrder();
        
        if (!confirmed) {
          return; // User cancelled
        }
      }
      
      try {
        if (!ltp || ltp <= 0) {
          setPopupMessage('Price not available for market order');
          setPopupType('error');
          setShowPopup(true);
          setTimeout(() => setShowPopup(false), 3000);
          return;
        }

        const orderPayload = {
          exchange: item.exchange,
          tradingSymbol: item.tradingSymbol,
          instrumentId: item.instrumentId,
          transactionType: action,
          quantity: qty,
          price: finalPrice,
          orderType: orderType, // Use current order type from Quick Order Entry
          validity: 'DAY',
          product: productType, // Use current product type from Quick Order Entry
          orderComplexity: 'REGULAR',
          deviceId: '123',
          userId: userId
        };

        console.log(`🚀 Instant ${action} order with Quick Order Entry settings:`, {
          symbol: item.tradingSymbol,
          strike: strikePrice,
          qty,
          ltp,
          orderType,
          productType
        });

        const response = await api.post('/orders/place', orderPayload);
        
        if (response.data.success) {
          // Show success popup
          setPopupMessage(` ${action} ${strikePrice}${optionType} executed at market price!`);
          setPopupType('success');
          setShowPopup(true);
          
          // Auto-hide popup after 3 seconds
          setTimeout(() => setShowPopup(false), 3000);
          
          console.log(`✅ Instant ${action} order successful:`, response.data);
        } else {
          throw new Error(response.data.error || 'Order failed');
        }
      } catch (error) {
        console.error('Instant order error:', error);
        // Show error popup
        setPopupMessage(`${action} order failed: ${error.response?.data?.error || error.message}`);
        setPopupType('error');
        setShowPopup(true);
        
        // Auto-hide popup after 5 seconds
        setTimeout(() => setShowPopup(false), 5000);
      }
    };

    return (
      <div>
        <div className="bg-white rounded-lg shadow-sm border">
          <div className={`px-2 font-semibold text-[10px] rounded-t-lg h-[32px] flex items-center ${optionType === 'CE' ? 'bg-gradient-to-r from-green-500 to-green-600 text-white' : optionType === 'PE' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white' : 'bg-gradient-to-r from-gray-600 to-gray-700 text-white'}`}>
            {title} ({symbols.length})
          </div>
          {symbols.length > 0 ? (
            <div>
              <table className="w-full table-fixed">
                <thead className={`${optionType === 'CE' ? 'bg-green-50' : optionType === 'PE' ? 'bg-red-50' : 'bg-gray-50'}`}>
                  <tr className="text-[10px] font-semibold ${optionType === 'CE' ? 'text-green-700' : optionType === 'PE' ? 'text-red-700' : 'text-gray-700'} h-[26px]">
                    <th className="text-center py-0 px-0.5 w-1/2">Strike</th>
                    <th className="text-center py-0 px-0.5 w-1/2">LTP</th>
                  </tr>
                </thead>
                <tbody>
                  {symbols.map((item, idx) => {
                    const ltp = getLivePrice(item.exchange, item.instrumentId);
                    const rowId = `${item.exchange}-${item.instrumentId}`;
                    
                    // Extract strike price from trading symbol
                    const extractStrikePrice = (symbol, item) => {
                      // First try to use parsed_strike if available (most reliable)
                      if (item.parsed_strike) {
                        return parseInt(item.parsed_strike);
                      }
                      
                      // Fallback: parse from trading symbol
                      // NFO format: NIFTY25NOV25C23450 -> 23450
                      let match = symbol.match(/[CP](\d+)$/);
                      if (match) {
                        return parseInt(match[1]);
                      }
                      
                      // BFO format: SENSEX25D0475700CE -> 75700
                      match = symbol.match(/(\d+)(CE|PE)$/);
                      if (match) {
                        return parseInt(match[1]);
                      }
                      
                      return '—';
                    };
                    
                    const strikePrice = extractStrikePrice(item.tradingSymbol, item);
                    const isHovered = hoveredRow === rowId;
                    
                    return (
                      <tr 
                        key={rowId} 
                        className="border-b border-gray-100 hover:bg-blue-50 hover:shadow-sm cursor-pointer transition-all duration-150 h-[26px] relative group"
                        onMouseEnter={() => setHoveredRow(rowId)}
                        onMouseLeave={() => setHoveredRow(null)}
                        onDoubleClick={() => {
                          const defaultQty = (item.lotSize || 25).toString();
                          const currentQty = optionType === 'CE' ? ceQty : peQty;
                          
                          setQtyPopup({
                            show: true,
                            optionType: optionType,
                            item: { ...item, strike: strikePrice, ltp },
                            defaultQty: currentQty || defaultQty,
                            currentQty: currentQty || defaultQty
                          });
                        }}
                      >
                        <td className="text-center py-0 px-0.5 font-semibold text-xs relative">
                          {/* Buy Button on Hover - Left side */}
                          {isHovered && (optionType === 'CE' || optionType === 'PE') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleInstantOrder(item, 'BUY', strikePrice, ltp);
                              }}
                              className="absolute left-1 top-1/2 transform -translate-y-1/2 bg-green-600 hover:bg-green-700 text-white text-[9px] font-bold rounded shadow-lg transition-all duration-200 z-10 flex items-center justify-center"
                              style={{ minWidth: '16px', height: '16px', width: '16px' }}
                              title={`Buy ${strikePrice}${optionType} at market price`}
                            >
                              B
                            </button>
                          )}
                          {strikePrice}
                        </td>
                        <td className="text-center py-0 px-0.5 relative">
                          {ltp ? (
                            <div className="font-semibold text-xs">{Math.round(ltp)}</div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                          {/* Sell Button on Hover */}
                          {isHovered && (optionType === 'CE' || optionType === 'PE') && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleInstantOrder(item, 'SELL', strikePrice, ltp);
                              }}
                              className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-red-600 hover:bg-red-700 text-white text-[9px] font-bold rounded shadow-lg transition-all duration-200 z-10 flex items-center justify-center"
                              style={{ minWidth: '16px', height: '16px', width: '16px' }}
                              title={`Sell ${strikePrice}${optionType} at market price`}
                            >
                              S
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Total Sum Row - Hidden but calculation still runs for Quick Order Entry */}
                  <tr className={`hidden border-t-2 ${optionType === 'CE' ? 'bg-green-50' : optionType === 'PE' ? 'bg-red-50' : 'bg-gray-50'}`}>
                    <td className="text-center py-0 px-0.5 font-bold text-sm">TOTAL</td>
                    <td className="text-center py-0 px-0.5">
                      <div className="font-bold text-sm text-blue-600">
                        {Math.round(totalLTP)}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No {optionType || 'symbols'} in watchlist</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Main component return
  return (
    <div className="p-2 space-y-2">
      {/* Popup Notification */}
      {showPopup && (
        <div className="fixed top-4 right-4 z-50 animate-fadeIn">
          <div className={`px-6 py-4 rounded-lg shadow-2xl border-2 ${
            popupType === 'success' 
              ? 'bg-green-50 border-green-500 text-green-800' 
              : 'bg-red-50 border-red-500 text-red-800'
          }`}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">
                {popupType === 'success' ? '✅' : '❌'}
              </span>
              <span className="font-semibold text-base">{popupMessage}</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Direction Change Notification */}
      {showDirectionNotification && directionNotificationData && (
        <div className="fixed top-20 right-4 z-50 animate-fadeIn">
          <div className={`px-6 py-4 rounded-lg shadow-2xl border-2 ${
            directionNotificationData.type === 'BUY'
              ? 'bg-green-50 border-green-500 text-green-800'
              : 'bg-red-50 border-red-500 text-red-800'
          }`}>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <span className="text-2xl">
                  {directionNotificationData.type === 'BUY' ? '📈' : '📉'}
                </span>
                <div className="flex-1">
                  <div className="font-semibold text-base">{directionNotificationData.message}</div>
                  <div className="text-sm mt-1">
                    <span className="font-bold">{directionNotificationData.symbol}</span>
                    {' - LTP: '}
                    <span className="font-bold">{directionNotificationData.ltp}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-300">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        const twoHoursFromNow = Date.now() + (2 * 60 * 60 * 1000);
                        setDisableNotificationsUntil(twoHoursFromNow);
                        localStorage.setItem('tradingView_disableNotificationsUntil', twoHoursFromNow.toString());
                      } else {
                        setDisableNotificationsUntil(null);
                        localStorage.removeItem('tradingView_disableNotificationsUntil');
                      }
                    }}
                    className="w-4 h-4"
                  />
                  <span>Don't show for 2 hours</span>
                </label>
                
                <button
                  onClick={() => {
                    setShowDirectionNotification(false);
                    if (directionNotificationData.dismissKey) {
                      setNotificationsDismissed(prev => ({
                        ...prev,
                        [directionNotificationData.dismissKey]: true
                      }));
                    }
                  }}
                  className="px-4 py-1 bg-gray-700 text-white rounded hover:bg-gray-800 text-sm font-semibold"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Order Panel */}
      <OrderPanel
        symbol={selectedSymbol}
        isOpen={isOrderPanelOpen}
        onClose={() => {
          setIsOrderPanelOpen(false);
          setSelectedSymbol(null);
        }}
      />
      
      {/* Header */}
      <div className="flex justify-between items-center mb-1">
        <h1 className="text-lg font-bold text-gray-800">Quick Trade</h1>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <div className={`px-2 py-1 rounded text-xs ${wsConnected ? 'bg-green-100 text-green-800' : connectionError ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
              {wsConnected ? '🟢 Live Data Connected' : connectionError ? `❌ ${connectionError}` : `⚠️ ${wsStatus}`}
            </div>
            {!wsConnected && (
              <button
                onClick={async () => {
                  setWsStatus('Reconnecting...');
                  setConnectionError(null);
                  try {
                    await aliceBlueWS.connect();
                    if (aliceBlueWS.isConnected) {
                      setWsConnected(true);
                      setWsStatus('Connected');
                    }
                  } catch (err) {
                    console.error('Reconnect failed:', err);
                    setWsStatus('Connection Failed');
                    setConnectionError('WebSocket connection failed - Working without live data');
                  }
                }}
                className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors"
                title="Reconnect to WebSocket"
              >
                🔄 Reconnect
              </button>
            )}
          </div>
          {connectionError && (
            <div className="text-xs text-red-600 max-w-md">
              Error: {connectionError}
            </div>
          )}
        </div>
      </div>

      {/* Exchange Tabs - Horizontal */}
      <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200">
        {/* Header with Collapse Button */}
     
          <button
            onClick={() => {
              setIsExchangeTabsCollapsed(!isExchangeTabsCollapsed);
              localStorage.setItem('tradingView_exchangeTabsCollapsed', (!isExchangeTabsCollapsed).toString());
            }}
            className="flex items-center justify-between bg-blue-100 p-2 rounded border border-blue-300 mb-2"
          >
            {isExchangeTabsCollapsed ? '▼ 📋 Exchange & Symbols' : '▲ 📋 Exchange & Symbols'}
          </button>
        

        {!isExchangeTabsCollapsed && (
          <>
            <div className="flex space-x-1.5 border-b pb-1">
          {['NSE', 'NFO', 'BSE', 'BFO', 'MCX', 'CDS'].map(exchange => {
            return (
              <button
                key={exchange}
                onClick={() => {
                  setActiveTab(exchange);
                  // Auto-select first NFO symbol as sub-tab
                  if (exchange === 'NFO' && masterSymbolsByExchange['NFO']?.length > 0) {
                    const firstSymbol = masterSymbolsByExchange['NFO'][0].symbol_name;
                    setNfoSubTab(firstSymbol);
                    // Auto-set symbol filter
                    handleSymbolNameChange(firstSymbol, 'NFO');
                  }
                  // Auto-select first MCX symbol as sub-tab
                  if (exchange === 'MCX' && masterSymbolsByExchange['MCX']?.length > 0) {
                    const firstSymbol = masterSymbolsByExchange['MCX'][0].symbol_name;
                    setMcxSubTab(firstSymbol);
                    handleSymbolNameChange(firstSymbol, 'MCX');
                  }
                }}
                className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${
                  activeTab === exchange
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {exchange}
              </button>
            );
          })}
        </div>

        {/* NFO Symbol Sub-tabs - Horizontal */}
        {activeTab === 'NFO' && masterSymbolsByExchange['NFO']?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t">
            {(() => {
              // Sort symbols based on custom order
              const sortedSymbols = [...masterSymbolsByExchange['NFO']].sort((a, b) => {
                const indexA = nfoSymbolsOrder.indexOf(a.id);
                const indexB = nfoSymbolsOrder.indexOf(b.id);
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
              });

              return sortedSymbols.map((symbol) => {
                return (
                  <button
                    key={symbol.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, symbol.id)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, symbol.id)}
                    onDragEnd={handleDragEnd}
                    onClick={() => {
                      setNfoSubTab(symbol.symbol_name);
                      handleSymbolNameChange(symbol.symbol_name, 'NFO');
                      // Auto-collapse Exchange & Symbols section
                      setIsExchangeTabsCollapsed(true);
                      localStorage.setItem('tradingView_exchangeTabsCollapsed', 'true');
                      // Auto-expand Select Expiry & Strike Price section
                      setIsFilterCollapsed(false);
                    }}
                    className={`px-2 py-1 rounded font-semibold text-xs transition-colors cursor-move ${
                      nfoSubTab === symbol.symbol_name
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    } ${draggedSymbolId === symbol.id ? 'opacity-50' : ''}`}
                  >
                    {symbol.symbol_name}
                  </button>
                );
              });
            })()}
          </div>
        )}

        {/* BFO Symbol Sub-tabs - Horizontal */}
        {activeTab === 'BFO' && masterSymbolsByExchange['BFO']?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1.5 pt-1.5 border-t">
            {(() => {
              // Sort symbols based on custom order
              const sortedSymbols = [...masterSymbolsByExchange['BFO']].sort((a, b) => {
                const indexA = bfoSymbolsOrder.indexOf(a.id);
                const indexB = bfoSymbolsOrder.indexOf(b.id);
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
              });

              return sortedSymbols.map((symbol) => {
                return (
                  <button
                    key={symbol.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, symbol.id)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, symbol.id)}
                    onDragEnd={handleDragEnd}
                    onClick={() => {
                      setBfoSubTab(symbol.symbol_name);
                      handleSymbolNameChange(symbol.symbol_name, 'BFO');
                      // Auto-collapse Exchange & Symbols section
                      setIsExchangeTabsCollapsed(true);
                      localStorage.setItem('tradingView_exchangeTabsCollapsed', 'true');
                      // Auto-expand Select Expiry & Strike Price section
                      setIsFilterCollapsed(false);
                    }}
                    className={`px-2 py-1 rounded font-semibold text-xs transition-colors cursor-move ${
                      bfoSubTab === symbol.symbol_name
                        ? 'bg-orange-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    } ${draggedSymbolId === symbol.id ? 'opacity-50' : ''}`}
                  >
                    {symbol.symbol_name}
                  </button>
                );
              });
            })()}
          </div>
        )}

        {/* MCX Option Chain Controls - Symbol / Expiry / Interval / Add */}
        {activeTab === 'MCX' && (
          <div className="mt-1.5 pt-1.5 border-t">
            {mcxError && (
              <div className="mb-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">{mcxError}</div>
            )}
            <div className="flex flex-wrap gap-2 items-end">
              {/* Symbol */}
              <div>
                <label className="block text-[10px] font-medium text-gray-600 mb-1">Symbol</label>
                <select
                  value={mcxSelectedUnderlying}
                  onChange={(e) => {
                    setMcxSelectedUnderlying(e.target.value);
                    setMcxOptionChainData([]);
                    setMcxChainVisible(false);
                    setMcxSubTab(e.target.value);
                  }}
                  disabled={mcxLoading || mcxUnderlyingList.length === 0}
                  className="input text-xs py-1 px-2"
                  style={{minWidth: '130px'}}
                >
                  <option value="">Select Symbol</option>
                  {mcxUnderlyingList.map(u => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
              {/* Expiry */}
              <div>
                <label className="block text-[10px] font-medium text-gray-600 mb-1">Expiry</label>
                <select
                  value={mcxSelectedExpiry}
                  onChange={(e) => {
                    setMcxSelectedExpiry(e.target.value);
                    setMcxOptionChainData([]);
                    setMcxChainVisible(false);
                  }}
                  disabled={mcxLoading || mcxExpiryList.length === 0}
                  className="input text-xs py-1 px-2"
                  style={{minWidth: '130px'}}
                >
                  <option value="">Select Expiry</option>
                  {mcxExpiryList.map(exp => (
                    <option key={exp} value={exp}>{exp}</option>
                  ))}
                </select>
              </div>
              {/* Strike Interval */}
              <div>
                <label className="block text-[10px] font-medium text-gray-600 mb-1">Strike Interval</label>
                <select
                  value={mcxSelectedInterval}
                  onChange={(e) => setMcxSelectedInterval(e.target.value)}
                  className="input text-xs py-1 px-2"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="15">15</option>
                  <option value="20">20</option>
                  <option value="25">25</option>
                </select>
              </div>
              {/* Add Button */}
              <div>
                <button
                  onClick={() => {
                    handleAddMcxChain();
                    setIsExchangeTabsCollapsed(true);
                    localStorage.setItem('tradingView_exchangeTabsCollapsed', 'true');
                  }}
                  disabled={mcxLoading || !mcxSelectedUnderlying || !mcxSelectedExpiry}
                  className="btn btn-primary text-xs py-1 px-3 disabled:opacity-50"
                >
                  {mcxLoading ? 'Loading...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {/* Trading View Watchlist */}
      <div>
        {orderFormVisible && (
          <div id="order-form-section" className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              Place Order - {orderData.symbol?.tradingSymbol}
            </h3>
            
            <form onSubmit={handleOrderSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Transaction Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Type</label>
                <select
                  value={orderData.action}
                  onChange={(e) => setOrderData({...orderData, action: e.target.value})}
                  className="input w-full"
                >
                  <option value="BUY">Buy</option>
                  <option value="SELL">Sell</option>
                </select>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quantity</label>
                <input
                  type="number"
                  value={orderData.quantity}
                  onChange={(e) => setOrderData({...orderData, quantity: e.target.value})}
                  className="input w-full"
                  min="1"
                  required
                />
              </div>

              {/* Order Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Order Type</label>
                <select
                  value={orderData.orderType}
                  onChange={(e) => setOrderData({...orderData, orderType: e.target.value})}
                  className="input w-full"
                >
                  <option value="MARKET">Market</option>
                  <option value="LIMIT">Limit</option>
                  <option value="SL">Stop Loss</option>
                  <option value="SLM">SL Market</option>
                </select>
              </div>

              {/* Price */}
              {orderData.orderType !== 'MARKET' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
                  <input
                    type="number"
                    step="0.05"
                    value={orderData.price}
                    onChange={(e) => setOrderData({...orderData, price: e.target.value})}
                    className="input w-full"
                    required={orderData.orderType !== 'MARKET'}
                  />
                </div>
              )}

              {/* Product
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Product</label>
                <select
                  value={orderData.product}
                  onChange={(e) => setOrderData({...orderData, product: e.target.value})}
                  className="input w-full"
                >
                  <option value="MIS">MIS (Intraday)</option>
                  <option value="NRML">NRML (Normal)</option>
                  <option value="CNC">CNC (Delivery)</option>
                </select>
              </div> */}

              {/* Validity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Validity</label>
                <select
                  value={orderData.validity}
                  onChange={(e) => setOrderData({...orderData, validity: e.target.value})}
                  className="input w-full"
                >
                  <option value="DAY">Day</option>
                  <option value="IOC">IOC</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="md:col-span-3 flex space-x-4">
                <button
                  type="submit"
                  className={`btn flex-1 ${
                    orderData.action === 'BUY' ? 'btn-success' : 'btn-danger'
                  }`}
                >
                  {orderData.action === 'BUY' ? 'BUY' : 'SELL'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelOrder}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>

            {/* Symbol Info */}
            <div className="text-sm text-gray-600 bg-white p-2 rounded mt-4">
              <strong>Symbol:</strong> {orderData.symbol?.tradingSymbol} | 
              <strong> Exchange:</strong> {orderData.symbol?.exchange} | 
              <strong> LTP:</strong> {getLivePrice(orderData.symbol?.exchange, orderData.symbol?.instrumentId)?.toFixed(2) || '—'}
            </div>
          </div>
        )}

        {/* Master Symbol Selection for Active Exchange (Hide for NFO, BFO, and MCX which use sub-tabs) */}
        {activeTab !== 'NFO' && activeTab !== 'BFO' && activeTab !== 'MCX' && (
          <div className="mb-3 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-4 mb-3">
              <h4 className="font-semibold text-gray-700">Select Symbol for {activeTab}</h4>
            </div>
            
            <div className="flex space-x-4">
              {/* Master Symbol Dropdown */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Master Symbol
                </label>
                <select
                  value={selectedMasterSymbol[activeTab] || ''}
                  onChange={(e) => handleMasterSymbolChange(activeTab, e.target.value)}
                  className="input w-full"
                >
                  <option value="">Select a symbol...</option>
                  {(masterSymbolsByExchange[activeTab] || []).map((symbol) => (
                    <option key={symbol.id} value={symbol.symbol_name}>
                      {symbol.display_name} ({symbol.symbol_name})
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Manual Search Input */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Or Search Manually
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={exchangeSearchQueries[activeTab] || ''}
                    onChange={(e) => setExchangeSearchQueries(prev => ({
                      ...prev,
                      [activeTab]: e.target.value
                    }))}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch(activeTab, exchangeSearchQueries[activeTab] || '');
                      }
                    }}
                    placeholder={`Search ${activeTab} symbols...`}
                    className="input flex-1"
                  />
                  <button
                    onClick={() => handleSearch(activeTab, exchangeSearchQueries[activeTab] || '')}
                    className="btn btn-primary"
                  >
                    Search
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Advanced Options Filter (for NFO/BFO derivatives) - Merged Compact Version */}
        {(activeTab === 'NFO' || activeTab === 'BFO') && (  
          <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200">
            {/* Header Bar with Title, Indices LTP, and Collapse Button */}
            <div className="flex items-center justify-between bg-blue-100 p-2 rounded border border-blue-300 mb-2">
               <button
                onClick={() => setIsFilterCollapsed(!isFilterCollapsed)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium px-2 py-1"
              >
                {isFilterCollapsed ? '▼ 🚀 Select Expiry & Strike Price' : '▲ 🚀 Select Expiry & Strike Price'}
              </button>
              
              
              {/* Live Indices Data */}
              <div className="flex items-center gap-4">
                {Object.entries(indicesData).map(([indexName, indexInfo]) => {
                  const ltp = getLivePrice(indexInfo.exchange, indexInfo.token);
                  const changeValue = getChangeValue(indexInfo.exchange, indexInfo.token);
                  const indexKey = `${indexInfo.exchange}|${indexInfo.token}`;
                  const priceData = livePrices[indexKey];
                  
                  return (
                    <div key={indexName} className="flex flex-col items-center px-2 py-1 bg-white rounded border border-gray-200 min-w-[80px]">
                      <div className="text-[10px] font-semibold text-gray-600">{indexName}</div>
                      <div className="text-xs font-bold text-gray-800">
                        {ltp ? ltp.toFixed(2) : '—'}
                      </div>
                      {changeValue !== null && changeValue !== 0 ? (
                        <div className={`text-[9px] font-medium ${
                          changeValue >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {changeValue >= 0 ? '+' : ''}{changeValue.toFixed(2)}
                        </div>
                      ) : (
                        <div className="text-[9px] font-medium text-gray-500">
                          —
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
             
            </div>
            
            {!isFilterCollapsed && (
              <>
              {/* 50/50 Layout: Left = Filters, Right = Chain Info */}
              <div className="bg-white p-3 rounded border border-blue-200">
                <div className="grid grid-cols-2 gap-4">
                  {/* LEFT SIDE 50%: Filter Controls + Add Button */}
                  <div className="bg-gray-50 p-3 rounded border border-gray-300">
                    <div className="flex items-end gap-2 h-full">
                      {/* Symbol Name Selection - Hide when sub-tab is active */}
                      {!nfoSubTab && !bfoSubTab && !mcxSubTab && (
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Symbol
                          </label>
                          <select
                            value={symbolFilters.symbolName}
                            onChange={(e) => handleSymbolNameChange(e.target.value, activeTab)}
                            className="input w-full text-xs py-2 px-2"
                          >
                            <option value="">Select</option>
                            {(masterSymbolsByExchange[activeTab] || []).map((symbol) => (
                              <option key={symbol.id} value={symbol.symbol_name}>
                                {symbol.symbol_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Expiry Date Selection */}
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Expiry
                        </label>
                        <select
                          value={symbolFilters.expiryDate}
                          onChange={(e) => handleExpiryChange(e.target.value)}
                          className="input w-full text-xs py-2 px-2"
                          disabled={!symbolFilters.symbolName && !nfoSubTab && !bfoSubTab && !mcxSubTab}
                        >
                          <option value="">Select</option>
                          {availableExpiries.map((expiry, idx) => (
                            <option key={idx} value={expiry.date}>
                              {expiry.formatted}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Strike Selection */}
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Select Strike
                        </label>
                        <select
                          value={symbolFilters.strikePrice}
                          onChange={(e) => handleStrikePriceChange(e.target.value)}
                          className="input w-full text-xs py-2 px-2"
                          disabled={!symbolFilters.expiryDate}
                        >
                          <option value="">Select</option>
                          {availableStrikes.map((strike, idx) => (
                            <option key={idx} value={strike}>
                              {strike}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Add Button */}
                      <div className="flex-none" style={{width: '80px'}}>
                        <button
                          onClick={() => {
                            // console.log('🚀 Button clicked - current symbolFilters state:', symbolFilters);
                            handleApplyFilters();
                            // Auto-collapse filter section
                            setIsFilterCollapsed(true);
                            // Trigger sidebar collapse
                            window.dispatchEvent(new CustomEvent('collapseSidebar'));
                          }}
                          disabled={(!symbolFilters.symbolName && !nfoSubTab && !bfoSubTab && !mcxSubTab) || !symbolFilters.expiryDate || !symbolFilters.strikePrice}
                          className="btn btn-primary text-xs px-2 py-2 w-full h-[26px]"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* RIGHT SIDE 50%: Chain Info + Analyse Button */}
                  <div className="bg-blue-50 p-3 rounded border border-blue-300">
                    <div className="flex items-end gap-2 h-full">
                      {selectedChainInfo ? (
                        <>
                          <div className="flex-1">
                            <div className="text-xs text-gray-600 mb-1">Symbol</div>
                            <div className="text-sm font-bold text-blue-800 bg-white px-2 py-2 rounded border border-blue-200 h-[26px] flex items-center">
                              {selectedChainInfo.symbolName}
                            </div>
                          </div>
                          
                          <div className="flex-1">
                            <div className="text-xs text-gray-600 mb-1">Expiry Date</div>
                            <div className="text-sm font-semibold text-blue-700 bg-white px-2 py-2 rounded border border-blue-200 h-[26px] flex items-center">
                              {selectedChainInfo.expiryDate}
                            </div>
                          </div>
                          
                          <div className="flex-1">
                            <div className="text-xs text-gray-600 mb-1">Spot Price</div>
                            <input
                              type="text"
                              value={editableBaseStrike || selectedChainInfo.centerStrike}
                              onChange={(e) => {
                                // console.log('📝 Base strike input changed:', e.target.value);
                                setEditableBaseStrike(e.target.value);
                              }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  // console.log('⌨️ Enter pressed, value:', e.target.value);
                                  e.preventDefault();
                                  handleBaseStrikeChange(e.target.value);
                                }
                              }}
                              onBlur={(e) => {
                                // console.log('👁️ Input blurred, value:', e.target.value);
                                if (e.target.value && e.target.value !== selectedChainInfo.centerStrike) {
                                  handleBaseStrikeChange(e.target.value);
                                }
                              }}
                              onFocus={(e) => {
                                // console.log('🎯 Input focused');
                                e.target.select();
                              }}
                              placeholder="Strike"
                              className="w-full px-2 py-2 text-sm font-semibold text-blue-700 border border-blue-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500 h-[26px]"
                            />
                          </div>
                    </>
                  ) : (
                    <div className="text-center text-gray-500 py-2 text-xs">
                      Select symbol, expiry & strike to load chain info
                    </div>
                  )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Filtered Results - Hidden - Smart builder handles this automatically */}
              {false && filteredSymbols.length > 0 && (
                <div className="mt-2 bg-white p-2 rounded border border-blue-200">
                  <h5 className="font-medium text-gray-700 mb-2 text-xs">
                    Found {filteredSymbols.length} matching symbol(s):
                  </h5>
                  <div className="grid grid-cols-1 gap-2">
                    {filteredSymbols.map((symbol, idx) => (
                      <div 
                        key={idx} 
                        className="bg-white p-2 rounded border hover:bg-blue-50 transition-colors"
                      >
                        <div className="font-medium text-blue-600 text-xs">{symbol.trading_symbol}</div>
                        <div className="text-xs text-gray-600">
                          Strike: {symbol.parsed_strike || symbol.strike_price || '—'} | Type: {symbol.parsed_option_type || symbol.option_type || '—'}
                        </div>
                        <div className="mt-1 flex space-x-2">
                          <button
                            onClick={() => handleAddToWatchlist(symbol)}
                            className="btn btn-primary text-xs px-2 py-1"
                            disabled={watchlist.some(item => 
                              item.exchange === symbol.exchange && 
                              item.instrumentId === symbol.instrument_id
                            )}
                          >
                            {watchlist.some(item => 
                              item.exchange === symbol.exchange && 
                              item.instrumentId === symbol.instrument_id
                            ) ? 'Added' : 'Add to Watch'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </>
            )}
          </div>
        )}

        {/* Search Results for Active Exchange (Hide for NFO and MCX) */}
        {activeTab !== 'NFO' && activeTab !== 'MCX' && activeTab !== 'BFO' && (
          <div className="card">
            {/* Search Results */}
            {exchangeSearchResults[activeTab] && exchangeSearchResults[activeTab].length > 0 && (
              <div className="mt-4 max-h-64 overflow-y-auto rounded-lg shadow-sm border border-gray-200">
                <table className="table-auto w-full text-sm">
                  <thead className="bg-gradient-to-r from-blue-50 to-indigo-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Symbol</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Instrument</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exchangeSearchResults[activeTab].map((symbol, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-blue-50 transition-colors duration-150">
                        <td className="px-4 py-3 font-semibold text-gray-800">{symbol.tradingSymbol}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{symbol.instrumentType}</td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => handleAddToWatchlist(symbol)}
                            className="btn btn-primary text-xs px-2 py-1"
                            disabled={watchlist.some(item => 
                              item.exchange === symbol.exchange && 
                              item.instrumentId === symbol.instrumentId
                            )}
                          >
                            {watchlist.some(item => 
                              item.exchange === symbol.exchange && 
                              item.instrumentId === symbol.instrumentId
                            ) ? 'Added' : 'Add'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Watchlist Tables */}
        {(activeTab === 'NFO' || activeTab === 'BFO') ? (
          // Split view for NFO: CE on left, PE on right
          <div className="space-y-4">
            {/* Options Tables and Quick Order Entry Layout */}
            <div className="flex gap-3 items-start">
              {/* Collapsible Options Tables Section (CE, PE only) */}
              {!isOptionsTableCollapsed ? (
                <>
                  <div className="flex-none" style={{width: '12%'}}>
                    {renderWatchlistTable(ceOptions, '📈 Call Options', 'CE')}
                  </div>
                  <div className="flex-none flex items-start" style={{width: '12%'}}>
                    <div className="flex-1">
                      {renderWatchlistTable(peOptions, '📉 Put Options', 'PE')}
                    </div>
                    {/* Collapse Button positioned next to Put Options table */}
                    <button
                      onClick={() => {
                        setIsOptionsTableCollapsed(true);
                        localStorage.setItem('tradingView_optionsTableCollapsed', 'true');
                      }}
                      className="flex-none bg-green-300 text-white px-1 py-1 rounded-r text-xs shadow hover:bg-green-500 transition-colors ml-[-2px] mt-[50%]"
                      style={{transform: 'translateY(-50%)'}}
                      title="Collapse Call & Put Options"
                    >
                      ◀
                    </button>
                  </div>
                </>
              ) : (
                /* Expand Button when collapsed */
          <button
              onClick={() => {
                setIsOptionsTableCollapsed(false);
                localStorage.setItem('tradingView_optionsTableCollapsed', 'false');
              }}
              className="
                bg-green-300 
                text-white 
                w-8 
                h-72 
                rounded-lg 
                shadow-lg 
                hover:bg-green-500 
                transition-colors
                flex 
                items-center 
                justify-center 
                self-center
              "
              style={{ display: "flex" }}
              title="Expand Call & Put Options"
            >
              <span className="-rotate-90 whitespace-nowrap text-[15px] text-center">
                {selectedChainInfo?.symbolName || (activeTab === 'NFO' ? nfoSubTab : activeTab === 'BFO' ? bfoSubTab : mcxSubTab) || activeTab} CE - PE Strike Price Details ▶
              </span>
            </button>


                
              )}

              {/* Volume Update Table (Always Visible) */}
              <div className="flex-none" style={{width: '18%'}}>
                    <div className="bg-white rounded-lg shadow-sm border">
                        {ceOptions.length > 0 && nextExpiryDate ? (
                          <div>
                            <table className="w-full table-fixed">
                              <thead className="bg-gradient-to-r from-purple-50 to-pink-50">
                                <tr className="bg-purple-50">
                                  <th colSpan="4" className="text-center" style={{height: '32px', padding: '0'}}>
                                    <div className="flex h-full">
                                      <button
                                        onClick={handleExportBullBear}
                                        disabled={isExporting || ceOptions.length === 0}
                                        className={`flex-1 h-full px-4 text-sm font-semibold transition-colors ${
                                          isExporting || ceOptions.length === 0
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg rounded-t-lg'
                                        }`}
                                        title="Click to Update"
                                      >
                                        {isExporting ? '⏳ Exporting...' : '📊 Volume Update'}
                                      </button>
                                    </div>
                                  </th>
                                </tr>
                                <tr className="text-[11px] font-semibold text-gray-700 h-[26px]">
                                  <th className="text-center py-0 px-2 w-1/2 border-r border-gray-200" colSpan="2">🐂 Bull</th>
                                  <th className="text-center py-0 px-2 w-1/2" colSpan="2">🐻 Bear</th>
                                </tr>
                              </thead>
                              <tbody>
                            {peOptions.map((peItem, idx) => {
                              // Get corresponding CE item for this row
                              const ceItem = ceOptions[idx];
                              
                              // Extract strike prices - support both NFO and BFO formats
                              let peStrike = null, ceStrike = null;
                              
                              // For PE strike extraction
                              if (peItem.parsed_strike) {
                                peStrike = peItem.parsed_strike.toString();
                              } else {
                                // Fallback: try to parse from trading symbol
                                let match = peItem.tradingSymbol.match(/[CP](\d+)$/); // NFO format
                                if (match) {
                                  peStrike = match[1];
                                } else {
                                  match = peItem.tradingSymbol.match(/(\d{3,5})[CP]E$/); // BFO format
                                  if (match) {
                                    peStrike = match[1];
                                  }
                                }
                              }
                              
                              // For CE strike extraction
                              if (ceItem) {
                                if (ceItem.parsed_strike) {
                                  ceStrike = ceItem.parsed_strike.toString();
                                } else {
                                  // Fallback: try to parse from trading symbol
                                  let match = ceItem.tradingSymbol.match(/[CP](\d+)$/); // NFO format
                                  if (match) {
                                    ceStrike = match[1];
                                  } else {
                                    match = ceItem.tradingSymbol.match(/(\d{3,5})[CP]E$/); // BFO format
                                    if (match) {
                                      ceStrike = match[1];
                                    }
                                  }
                                }
                              }

                              if (!peStrike && !ceStrike) return null;

                              // Find next expiry symbols for PE strike
                              const nextPE = peStrike ? nextExpirySymbols.find(s => s.strike === peStrike && s.type === 'PE') : null;
                              
                              // Find next expiry symbols for CE strike
                              const nextCE = ceStrike ? nextExpirySymbols.find(s => s.strike === ceStrike && s.type === 'CE') : null;

                              // Get depth data
                              const ceKey = nextCE ? `${nextCE.exchange}|${nextCE.token}` : null;
                              const peKey = nextPE ? `${nextPE.exchange}|${nextPE.token}` : null;

                              // Debug logging for first row only
                              if (idx === 0) {
                                console.log('🔍 Debug Bulls vs Bears (first row):', {
                                  peStrike,
                                  ceStrike,
                                  nextCE,
                                  nextPE,
                                  ceKey,
                                  peKey,
                                  ceDepthData: ceKey ? depthData[ceKey] : null,
                                  peDepthData: peKey ? depthData[peKey] : null
                                });
                              }

                              // Get live WebSocket data from respective strikes
                              // Column 1: CE TBQ from Call Options strike
                              // Column 2: PE TSQ from Put Options strike  
                              // Column 3: PE TBQ from Put Options strike
                              // Column 4: CE TSQ from Call Options strike
                              const liveCeTBQ = (ceKey && depthData[ceKey]?.tbq);
                              const liveCeTSQ = (ceKey && depthData[ceKey]?.tsq);
                              const livePeTBQ = (peKey && depthData[peKey]?.tbq);
                              const livePeTSQ = (peKey && depthData[peKey]?.tsq);
                              
                              // Calculate difference if in flip mode
                              let displayCeTBQ, displayCeTSQ, displayPeTBQ, displayPeTSQ;
                              
                              if (isFlipped) {
                                if (exportedData.length > 0) {
                                  // Find exported data for CE and PE strikes
                                  const exportedCERow = ceStrike ? exportedData.find(row => row.strike_price === parseInt(ceStrike)) : null;
                                  const exportedPERow = peStrike ? exportedData.find(row => row.strike_price === parseInt(peStrike)) : null;
                                  
                                  // Calculate difference: (live - exported) / 400
                                  if (exportedCERow) {
                                    const ceTBQDiff = (typeof liveCeTBQ === 'number' ? liveCeTBQ : 0) - (exportedCERow.ce_tbq || 0);
                                    const ceTSQDiff = (typeof liveCeTSQ === 'number' ? liveCeTSQ : 0) - (exportedCERow.ce_tsq || 0);
                                    displayCeTBQ = Math.round(ceTBQDiff / 400);
                                    displayCeTSQ = Math.round(ceTSQDiff / 400);
                                  } else {
                                    displayCeTBQ = displayCeTSQ = '-';
                                  }
                                  
                                  if (exportedPERow) {
                                    const peTBQDiff = (typeof livePeTBQ === 'number' ? livePeTBQ : 0) - (exportedPERow.pe_tbq || 0);
                                    const peTSQDiff = (typeof livePeTSQ === 'number' ? livePeTSQ : 0) - (exportedPERow.pe_tsq || 0);
                                    displayPeTBQ = Math.round(peTBQDiff / 400);
                                    displayPeTSQ = Math.round(peTSQDiff / 400);
                                  } else {
                                    displayPeTBQ = displayPeTSQ = '-';
                                  }
                                } else {
                                  // No exported data yet - show placeholders instead of live depth
                                  displayCeTBQ = displayCeTSQ = displayPeTBQ = displayPeTSQ = '-';
                                }
                              } else {
                                // Live view - show actual WebSocket data
                                displayCeTBQ = typeof liveCeTBQ === 'number' ? liveCeTBQ : '-';
                                displayCeTSQ = typeof liveCeTSQ === 'number' ? liveCeTSQ : '-';
                                displayPeTBQ = typeof livePeTBQ === 'number' ? livePeTBQ : '-';
                                displayPeTSQ = typeof livePeTSQ === 'number' ? livePeTSQ : '-';
                              }
                              
                              // Helper function to get color class based on value and flip mode
                              const getColorClass = (value, baseColor) => {
                                if (isFlipped && typeof value === 'number') {
                                  // In difference mode, color based on positive/negative
                                  return value > 0 ? 'text-blue-600' : value < 0 ? 'text-red-600' : 'text-gray-600';
                                }
                                // In live mode, use base color
                                return baseColor;
                              };

                              return (
                                <tr key={idx} className="border-b border-gray-100 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 transition-all duration-150 h-[26px] animate-table-row-fade">
                                  <td className={`text-center py-0 px-2 font-semibold text-sm ${getColorClass(displayCeTBQ, 'text-blue-600')}`}>{displayCeTBQ}</td>
                                  <td className={`text-center py-0 px-2 font-semibold text-sm border-r-[5px] border-gray-150 ${getColorClass(displayPeTSQ, 'text-red-600')}`}>{displayPeTSQ}</td>
                                  <td className={`text-center py-0 px-2 font-semibold text-sm ${getColorClass(displayPeTBQ, 'text-blue-600')}`}>{displayPeTBQ}</td>
                                  <td className={`text-center py-0 px-2 font-semibold text-sm ${getColorClass(displayCeTSQ, 'text-red-600')}`}>{displayCeTSQ}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-500 text-sm">
                          {!nextExpiryDate ? 'Loading Volumes...' : 'No Volume data available'}
                        </p>
                        {nextExpiryDate && (
                          <p className="text-gray-400 text-sm mt-1">
                            Symbols: {nextExpirySymbols.length} | Depth: {Object.keys(depthData).length}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              
                          {/* Quick Order Entry and Positions - Always visible */}
              {/* Quick Order Entry and Positions - Always visible */}
<div className="flex-1">
    {/* Order Entry Panel */}
    <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-4 py-1 font-semibold text-white flex justify-between items-center rounded-t-lg">
            <span>Quick Order Entry</span>
            <div className="flex gap-2">
                {/* Analyse Button */}
                {(ceOptions.length > 0 || peOptions.length > 0) && (
                    <button 
                        className="bg-gradient-to-r from-blue-800 to-purple-800 hover:from-blue-900 hover:to-purple-900 text-white font-bold px-4 py-1 rounded shadow-lg transition-all duration-200 transform hover:scale-105 text-xs"
                        onClick={async () => {
                            try {
                                console.log('📊 Starting options analysis export...');
                                
                                const extractStrikeFromSymbol = (symbol) => {
                                    if (!symbol) return null;
                                    // NFO: NIFTY25DEC25C26000 -> 26000
                                    let match = symbol.match(/[CP](\d+)$/);
                                    if (match) return parseInt(match[1]);
                                    
                                    // BFO: SENSEX25DEC72800CE -> 72800
                                    match = symbol.match(/(\d{3,5})[CP]E$/);
                                    if (match) return parseInt(match[1]);
                                    
                                    return null;
                                };
                                
                                const extractExpiryFromSymbol = (symbol) => {
                                    if (!symbol) return null;
                                    
                                    // NFO: NIFTY02DEC25C26000 -> 2025-12-02
                                    let match = symbol.match(/(\d{2})([A-Z]{3})(\d{2})[CP]/);
                                    if (match) {
                                        const day = match[1];
                                        const monthStr = match[2];
                                        const year = '20' + match[3];
                                        
                                        const monthMap = {
                                            'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
                                            'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
                                            'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
                                        };
                                        
                                        return `${year}-${monthMap[monthStr]}-${day}`;
                                    }
                                    
                                    // BFO: SENSEX24DEC72800CE -> 2025-12-24
                                    match = symbol.match(/(\d{2})([A-Z]{3})(\d+)[CP]E$/);
                                    if (match) {
                                        const day = match[1];
                                        const monthStr = match[2];
                                        const currentYear = new Date().getFullYear();
                                        
                                        const monthMap = {
                                            'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
                                            'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
                                            'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
                                        };
                                        
                                        const month = monthMap[monthStr];
                                        if (month) {
                                            return `${currentYear}-${month}-${day}`;
                                        }
                                    }
                                    
                                    return null;
                                };
                                
                                const extractedExpiryDate = selectedChainInfo?.expiryDateRaw || (ceOptions.length > 0 ? 
                                    extractExpiryFromSymbol(ceOptions[0].tradingSymbol) : 
                                    (peOptions.length > 0 ? extractExpiryFromSymbol(peOptions[0].tradingSymbol) : ''));
                                
                                console.log('📅 Extracted expiry date:', extractedExpiryDate);
                                
                                const ceOptionsWithLTP = ceOptions.map(option => ({
                                    exchange: option.exchange || 'NFO',
                                    tradingSymbol: option.tradingSymbol,
                                    strikePrice: extractStrikeFromSymbol(option.tradingSymbol),
                                    ltp: getLivePrice(option.exchange, option.instrumentId)
                                }));
                                
                                const peOptionsWithLTP = peOptions.map(option => ({
                                    exchange: option.exchange || 'NFO',
                                    tradingSymbol: option.tradingSymbol,
                                    strikePrice: extractStrikeFromSymbol(option.tradingSymbol),
                                    ltp: getLivePrice(option.exchange, option.instrumentId)
                                }));
                                
                                const exportData = {
                                    symbolName: symbolFilters.symbolName || (activeTab === 'NFO' ? 'NIFTY' : activeTab === 'BFO' ? 'SENSEX' : activeTab === 'MCX' ? 'CRUDEOIL' : 'NIFTY'),
                                    expiryDate: extractedExpiryDate,
                                    ceOptions: ceOptionsWithLTP,
                                    peOptions: peOptionsWithLTP
                                };
                                
                                console.log('📊 Export data:', {
                                    symbolName: exportData.symbolName,
                                    expiryDate: exportData.expiryDate,
                                    peCount: exportData.peOptions.length
                                });
                                
                                const ceLTPSum = ceOptionsWithLTP.reduce((sum, opt) => sum + (opt.ltp || 0), 0);
                                const peLTPSum = peOptionsWithLTP.reduce((sum, opt) => sum + (opt.ltp || 0), 0);
                                setBaselineCESum(ceLTPSum);
                                setBaselinePESum(peLTPSum);
                                console.log('💰 Baseline sums stored - CE:', ceLTPSum.toFixed(2), 'PE:', peLTPSum.toFixed(2));
                                
                                const response = await api.post('/options-analysis/export', exportData);
                                
                                if (response.data.success) {
                                    const result = response.data.data;
                                    const clearedText = result.replacedCount > 0 ? `\\n(Cleared ${result.replacedCount} existing records from table)` : '';
                                    
                                    
                                    // Auto-collapse the filter section after successful analysis
                                    setIsFilterCollapsed(true);
                                    // Trigger sidebar collapse
                                    window.dispatchEvent(new CustomEvent('collapseSidebar'));
                                } else {
                                    alert('❌ Export failed: ' + response.data.error);
                                }
                                
                            } catch (error) {
                                console.error('❌ Export error:', error);
                                alert('❌ Export failed: ' + error.message);
                            }
                        }}
                    >
                        📊 Analyse
                    </button>
                )}
                <button
                    onClick={() => {
                        setSelectedCEStrike(null);
                        setSelectedPEStrike(null);
                        setCEQty('');
                        setPEQty('');
                        setCEEntryPrice(null);
                        setPEEntryPrice(null);
                    }}
                    className="text-xs bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                    title="Clear all selections"
                >
                    Clear
                </button>
            </div>
        </div>
        <div className="p-1">
            {/* Calculate MTM values */}
            {(() => {
                // Current live LTP sums
                const currentCESum = ceOptions.reduce((sum, option) => {
                    const ltp = getLivePrice(option.exchange, option.instrumentId);
                    return sum + (ltp || 0);
                }, 0);

                const currentPESum = peOptions.reduce((sum, option) => {
                    const ltp = getLivePrice(option.exchange, option.instrumentId);
                    return sum + (ltp || 0);
                }, 0);

                // Calculate MTM (difference from baseline)
                const ceMTM = currentCESum - baselineCESum;
                const peMTM = currentPESum - baselinePESum;

                // Define fixed column widths (12 columns total)
                const colWidths = [
                    'w-[7%]', // 1. Entry
                    'w-[7%]', // 2. LTP
                    'w-[18%]', // 3. Qty
                    'w-[7%]', // 4. Strength
                    'w-[7%]', // 5. Buy
                    'w-[7%]', // 6. Sell
                    'w-[7%]', // 7. Re Enter
                    'w-[8%]', // 8. Direction
                    'w-[11%]', // 9. Strike
                    'w-[7%]', // 10. Low
                    'w-[7%]', // 11. High
                    'w-[7%]', // 12. Alerts
                ];

                return (
                    <>
                        {/* Order Entry Table */}
                        <table className="w-full text-[10px] overflow-hidden rounded-lg">
                            <thead>
                                <tr className="font-semibold text-white text-sx h-[22px] bg-gradient-to-r from-indigo-500 to-blue-600">
                                    <th className={`${colWidths[0]} p-1 text-center rounded-l-lg`}>Entry</th>
                                    <th className={`${colWidths[1]} p-1 text-center`}>LTP</th>
                                    <th className={`${colWidths[2]} p-1 text-center`}>Qty</th>
                                    <th className={`${colWidths[3]} p-1 text-center`}>Strength</th>
                                    <th className={`${colWidths[4]} p-1 text-center`}>Buy</th>
                                    <th className={`${colWidths[5]} p-1 text-center`}>Sell</th>
                                    <th className={`${colWidths[6]} p-1 text-center`}>Re Enter</th>
                                    <th className={`${colWidths[7]} p-1 text-center`}>Direction</th>
                                    <th className={`${colWidths[8]} p-1 text-center`}>Strike</th>
                                    <th className={`${colWidths[9]} p-1 text-center`}>Low</th>
                                    <th className={`${colWidths[10]} p-1 text-center`}>High</th>
                                    <th className={`${colWidths[11]} p-1 text-center rounded-r-lg`}>Alerts</th>
                                </tr>
                            </thead>

                            <tbody>
                                {/* CE Row */}
                                <tr className="border-b h-[32px]">
                                    {/* 1. Entry */}
                                    <td className="text-gray-600 text-xs">
                                        {ceEntryPrice !== null ? ceEntryPrice.toFixed(2) : '-'}
                                    </td>

                                    {/* 2. LTP */}
                                    <td className="p-1 text-center font-bold text-sm">
                                        {selectedCEStrike ? (
                                            getLivePrice(selectedCEStrike.exchange, selectedCEStrike.instrumentId)?.toFixed(2) || '-'
                                        ) : '-'}
                                    </td>

                                    {/* 3. Qty */}
                                    <td className="p-1 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                            <button
                                                onClick={() => adjustQuantity('CE', 'decrease')}
                                                disabled={!selectedCEStrike || isDecreaseDisabled('CE')}
                                                className="bg-gradient-to-r from-gray-400 to-gray-500 text-white w-5 h-6 rounded text-xs hover:from-gray-700 hover:to-gray-800 disabled:opacity-40 disabled:cursor-not-allowed font-bold shadow-sm hover:shadow transition-all duration-200 flex items-center justify-center"
                                                title={`Decrease quantity by ${selectedCEStrike?.lotSize || 25}`}
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
                                                </svg>
                                            </button>
                                            <input
                                                type="number"
                                                value={ceQty}
                                                onChange={(e) => setCEQty(e.target.value)}
                                                onBlur={() => handleQtyBlur('CE')}
                                                className={`w-12 px-1 py-1 border rounded text-center text-[11px] font-semibold focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${(ceQty && String(ceQty).trim().length > 0 && String(ceQty).trim() !== '0') ? 'bg-yellow-100' : ''}`}
                                                placeholder={selectedCEStrike ? (selectedCEStrike.lotSize || '25') : ''}
                                                title={selectedCEStrike ? `Lot Size: ${selectedCEStrike.lotSize || 25}` : ''}
                                                min="0"
                                            />
                                            <button
                                                onClick={() => adjustQuantity('CE', 'increase')}
                                                disabled={!selectedCEStrike}
                                                className="bg-gradient-to-r from-gray-400 to-gray-500 text-white w-5 h-6 rounded text-xs hover:from-gray-700 hover:to-gray-800 disabled:opacity-40 disabled:cursor-not-allowed font-bold shadow-sm hover:shadow transition-all duration-200 flex items-center justify-center"
                                                title={`Increase quantity by ${selectedCEStrike?.lotSize || 25}`}
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                                                </svg>
                                            </button>
                                        </div>
                                    </td>

                                    {/* 3. Strength */}
                                    <td className="p-1 text-center">
                                        <div className={`font-bold text-sm ${ceMTM >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {baselineCESum > 0 ? (
                                                <div title={`MTM: ${ceMTM.toFixed(2)}`}>{ceMTM >= 0 ? '+' : '-'}{(Math.abs(ceMTM) / 10).toFixed(0)}<p3 className="text-gray-400 text-xs">%</p3></div>
                                            ) : (
                                                <div className="text-gray-400 text-xs">-</div>
                                            )}
                                        </div>
                                    </td>

                                    {/* 4. Buy (HEIGHT CHANGED TO h-8) */}
                                    <td className="p-1">
                                        <button
                                            onClick={() => handleQuickOrder(selectedCEStrike, 'BUY', 'CE')}
                                            disabled={!selectedCEStrike}
                                            className={getButtonStyle('buy').className}
                                            style={{ ...getButtonStyle('buy').style, width: '74px', height: '30px', minWidth: '74px', fontSize: '14px' }}
                                            onMouseEnter={getButtonStyle('buy').onMouseEnter}
                                            onMouseLeave={getButtonStyle('buy').onMouseLeave}
                                        >
                                            Buy
                                        </button>
                                    </td>

                                    {/* 5. Sell (HEIGHT CHANGED TO h-8) */}
                                    <td className="p-1">
                                        <button
                                            onClick={() => handleQuickOrder(selectedCEStrike, 'SELL', 'CE')}
                                            disabled={!selectedCEStrike}
                                            className={getButtonStyle('sell').className}
                                            style={{ ...getButtonStyle('sell').style, width: '74px', height: '30px', minWidth: '74px', fontSize: '14px' }}
                                            onMouseEnter={getButtonStyle('sell').onMouseEnter}
                                            onMouseLeave={getButtonStyle('sell').onMouseLeave}
                                        >
                                            Sell
                                        </button>
                                    </td>

                                    {/* 6. Re Enter */}
                                    <td className="p-1 text-center">
                                        {exportedCEStrength !== 0 && baselineCESum > 0 ? (
                                          
                                            <div className={`font-bold text-sm ${
                                                ((ceMTM / 10) - exportedCEStrength) >= 0 ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                                {((ceMTM / 10) - exportedCEStrength) >= 0 ? '+' : ''}{Math.round((ceMTM / 10) - exportedCEStrength)}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 text-xs">-</span>
                                        )}
                                    </td>

                                    {/* 7. Direction */}
                                    <td className="p-1 text-center">
                                        <div className="flex items-center justify-center">
                                            {ceDirection === 'up' && (
                                                <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M12 4l-8 8h5v8h6v-8h5z" />
                                                </svg>
                                            )}
                                            {ceDirection === 'down' && (
                                                <svg className="w-5 h-5 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M12 20l8-8h-5V4h-6v8H4z" />
                                                </svg>
                                            )}
                                            {!ceDirection && <span className="text-gray-400 text-xs">-</span>}
                                        </div>
                                    </td>

                                    {/* 8. Strike */}
                                    <td className="p-1 text-center font-bold text-sm text-blue-600">
                                        {selectedCEStrike ? `${selectedCEStrike.strike}CE` : '-'}
                                    </td>

                                    {/* 9. Low */}
                                    <td className="p-1 text-center font-semibold text-xs text-red-600">
                                        {selectedCEStrike ? (
                                            getLowPrice(selectedCEStrike.exchange, selectedCEStrike.instrumentId)?.toFixed(2) || '-'
                                        ) : '-'}
                                    </td>

                                    {/* 10. High */}
                                    <td className="p-1 text-center font-semibold text-xs text-green-600">
                                        {selectedCEStrike ? (
                                            getHighPrice(selectedCEStrike.exchange, selectedCEStrike.instrumentId)?.toFixed(2) || '-'
                                        ) : '-'}
                                    </td>

                                    {/* 11. Exit */}
                                    <td className="p-1">
                                     
                                    </td>
                                </tr>

                                {/* PE Row */}
                                <tr className="border-b h-[32px]">
                                    {/* 1. Entry */}
                                    <td className="text-gray-600 text-xs">
                                        {peEntryPrice !== null ? peEntryPrice.toFixed(2) : '-'}
                                    </td>

                                    {/* 2. LTP */}
                                    <td className="p-1 text-center font-bold text-sm">
                                        {selectedPEStrike ? (
                                            getLivePrice(selectedPEStrike.exchange, selectedPEStrike.instrumentId)?.toFixed(2) || '-'
                                        ) : '-'}
                                    </td>

                                    {/* 3. Qty */}
                                    <td className="p-1 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                            <button
                                                onClick={() => adjustQuantity('PE', 'decrease')}
                                                disabled={!selectedPEStrike || isDecreaseDisabled('PE')}
                                                className="bg-gradient-to-r from-gray-400 to-gray-500 text-white w-5 h-6 rounded text-xs hover:from-gray-700 hover:to-gray-800 disabled:opacity-40 disabled:cursor-not-allowed font-bold shadow-sm hover:shadow transition-all duration-200 flex items-center justify-center"
                                                title={`Decrease quantity by ${selectedPEStrike?.lotSize || 25}`}
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
                                                </svg>
                                            </button>

                                            <input
                                                type="number"
                                                value={peQty}
                                                onChange={(e) => setPEQty(e.target.value)}
                                                onBlur={() => handleQtyBlur('PE')}
                                                className={`w-12 px-1 py-1 border rounded text-center text-[11px] font-semibold focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${(peQty && String(peQty).trim().length > 0 && String(peQty).trim() !== '0') ? 'bg-yellow-100' : ''}`}
                                                placeholder={selectedPEStrike ? (selectedPEStrike.lotSize || '25') : ''}
                                                title={selectedPEStrike ? `Lot Size: ${selectedPEStrike.lotSize || 25}` : ''}
                                                min="0"
                                            />

                                            <button
                                                onClick={() => adjustQuantity('PE', 'increase')}
                                                disabled={!selectedPEStrike}
                                                className="bg-gradient-to-r from-gray-400 to-gray-500 text-white w-5 h-6 rounded text-xs hover:from-gray-700 hover:to-gray-800 disabled:opacity-40 disabled:cursor-not-allowed font-bold shadow-sm hover:shadow transition-all duration-200 flex items-center justify-center"
                                                title={`Increase quantity by ${selectedPEStrike?.lotSize || 25}`}
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                                                </svg>
                                            </button>
                                        </div>
                                    </td>

                                    {/* 3. Strength */}
                                    <td className="p-1 text-center">
                                        <div className={`font-bold text-sm ${peMTM >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            {baselinePESum > 0 ? (
                                                <div title={`MTM: ${peMTM.toFixed(2)}`}>{peMTM >= 0 ? '+' : '-'}{(Math.abs(peMTM) / 10).toFixed(0)}<p3 className="text-gray-400 text-xs">%</p3></div>
                                            ) : (
                                                <div className="text-gray-400 text-xs">-</div>
                                            )}
                                        </div>
                                    </td>

                                    {/* 4. Buy (HEIGHT CHANGED TO h-8) */}
                                    <td className="p-1">
                                        <button
                                            onClick={() => handleQuickOrder(selectedPEStrike, 'BUY', 'PE')}
                                            disabled={!selectedPEStrike}
                                            className={getButtonStyle('buy').className}
                                            style={{ ...getButtonStyle('buy').style, width: '74px', height: '30px', minWidth: '74px', fontSize: '14px' }}
                                            onMouseEnter={getButtonStyle('buy').onMouseEnter}
                                            onMouseLeave={getButtonStyle('buy').onMouseLeave}
                                        >
                                            Buy
                                        </button>
                                    </td>

                                    {/* 5. Sell (HEIGHT CHANGED TO h-8) */}
                                    <td className="p-1">
                                        <button
                                            onClick={() => handleQuickOrder(selectedPEStrike, 'SELL', 'PE')}
                                            disabled={!selectedPEStrike}
                                            className={getButtonStyle('sell').className}
                                            style={{ ...getButtonStyle('sell').style, width: '74px', height: '30px', minWidth: '74px', fontSize: '14px' }}
                                            onMouseEnter={getButtonStyle('sell').onMouseEnter}
                                            onMouseLeave={getButtonStyle('sell').onMouseLeave}
                                        >
                                            Sell
                                        </button>
                                    </td>

                                    {/* 6. Re Enter */}
                                    <td className="p-1 text-center">
                                        {exportedPEStrength !== 0 && baselinePESum > 0 ? (
                                            <div className={`font-bold text-sm ${
                                                ((peMTM / 10) - exportedPEStrength) >= 0 ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                                {((peMTM / 10) - exportedPEStrength) >= 0 ? '+' : ''}{Math.round((peMTM / 10) - exportedPEStrength)}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 text-xs">-</span>
                                        )}
                                    </td>

                                    {/* 7. Direction */}
                                    <td className="p-1 text-center">
                                        <div className="flex items-center justify-center">
                                            {peDirection === 'up' && (
                                                <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M12 4l-8 8h5v8h6v-8h5z" />
                                                </svg>
                                            )}
                                            {peDirection === 'down' && (
                                                <svg className="w-5 h-5 text-red-600" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M12 20l8-8h-5V4h-6v8H4z" />
                                                </svg>
                                            )}
                                            {!peDirection && <span className="text-gray-400 text-xs">-</span>}
                                        </div>
                                    </td>

                                    {/* 8. Strike */}
                                    <td className="p-1 text-center font-bold text-sm text-red-600">
                                        {selectedPEStrike ? `${selectedPEStrike.strike}PE` : '-'}
                                    </td>

                                    {/* 9. Low */}
                                    <td className="p-1 text-center font-semibold text-xs text-red-600">
                                        {selectedPEStrike ? (
                                            getLowPrice(selectedPEStrike.exchange, selectedPEStrike.instrumentId)?.toFixed(2) || '-'
                                        ) : '-'}
                                    </td>

                                    {/* 10. High */}
                                    <td className="p-1 text-center font-semibold text-xs text-green-600">
                                        {selectedPEStrike ? (
                                            getHighPrice(selectedPEStrike.exchange, selectedPEStrike.instrumentId)?.toFixed(2) || '-'
                                        ) : '-'}
                                    </td>

                                    {/* 11. Exit */}
                                    <td className="p-1">
                                   
                                    </td>
                                </tr>

                              {/* Controls Row (Aligned Under Buy & Sell Buttons) */}
                                    <tr className="bg-yellow-50 h-[26px]">

                                        {/* 1–2: empty space */}
                                        <td colSpan="4"></td>

                                      
                                        {/* 4: Order Type (LMT / MKT) exactly under Buy button */}
                                        <td className="p-1 text-center">
                                            <div className="flex justify-center gap-1">
                                                <button
                                                    onClick={() => setOrderType('LIMIT')}
                                                    className={`px-2 py-1 rounded text-[10px] font-semibold ${
                                                        orderType === 'LIMIT'
                                                            ? 'bg-blue-400 text-white'
                                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                    }`}
                                                >
                                                    LMT
                                                </button>

                                                <button
                                                    onClick={() => setOrderType('MARKET')}
                                                    className={`px-2 py-1 rounded text-[10px] font-semibold ${
                                                        orderType === 'MARKET'
                                                            ? 'bg-blue-400 text-white'
                                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                    }`}
                                                >
                                                    MKT
                                                </button>
                                            </div>
                                        </td>

                                        {/* 5: Product Type (NML / MIS) exactly under Sell button */}
                                        <td className="p-1 text-center">
                                            <div className="flex justify-center gap-1">
                                                <button
                                                    onClick={() => setProductType('NRML')}
                                                    className={`px-2 py-1 rounded text-[10px] font-semibold ${
                                                        productType === 'NRML'
                                                            ? 'bg-purple-400 text-white'
                                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                    }`}
                                                >
                                                    NML
                                                </button>

                                                <button
                                                    onClick={() => setProductType('MIS')}
                                                    className={`px-2 py-1 rounded text-[10px] font-semibold ${
                                                        productType === 'MIS'
                                                            ? 'bg-purple-400 text-white'
                                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                    }`}
                                                >
                                                    MIS
                                                </button>
                                            </div>
                                        </td>
                                          {/* 3: Re-Enter button under Qty column */}
                                        <td className="p-1 text-center">
                                            <button
                                                onClick={() => {
                                                    // Export both CE and PE strength values
                                                    if (selectedCEStrike) handleReEnter('CE');
                                                    if (selectedPEStrike) handleReEnter('PE');
                                                }}
                                                disabled={!selectedCEStrike && !selectedPEStrike}
                                                className="px-3 py-1 rounded text-[10px] font-semibold bg-gray-200 text-gray-700 hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                               
                                            >
                                                Re Enter
                                            </button>
                                        </td>


                                        {/* 6–11: empty - covers remaining columns */}
                                        <td colSpan="6"></td>
                                    </tr>

                            </tbody>
                        </table>
                    </>
                );
            })()}
        </div>
    </div>

    {/* Positions Table */}
    <div className="mt-2 bg-white rounded-lg shadow-md border border-gray-200">
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-4 py-1 font-semibold text-white flex justify-between items-center rounded-t-lg">
            <div className="flex items-center gap-3">
                <span>{isPositionsFlipped ? 'Trade Book' : 'Positions'}</span>
                <button
                    onClick={() => setIsPositionsFlipped(!isPositionsFlipped)}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 px-2 py-0.5 rounded text-xs transition-all"
                    title={isPositionsFlipped ? 'Switch to Positions' : 'Switch to Trade Book'}
                >
                    🔄 {isPositionsFlipped ? 'Positions' : 'Trades'}
                </button>
            </div>
            <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm">
              <span className="text-sm text-blue-100 font-medium">{userId}</span>
              <p1>  </p1>
                {!isPositionsFlipped && `MTM: ${calculateTotalMTM()}`}
            </span>
        </div>

        <div className="overflow-x-auto">
            <table className="w-full">
                {!isPositionsFlipped ? (
                    // Positions Table
                    <>
                        <thead className="bg-gradient-to-r indigo-50">
                            <tr className="font-semibold text-gray-700 text-sm h-[22px]">
                                <th className="py-0.5 p-2 text-left">Type</th>
                                <th className="py-0.5 p-2 text-left">Instrument</th>
                                <th className="py-0.5 p-2 text-center">Qty</th>
                                <th className="py-0.5 p-2 text-center">BAvg</th>
                                <th className="py-0.5 p-2 text-center">SAvg</th>
                                <th className="py-0.5 p-2 text-center">LTP</th>
                                <th className="py-0.5 p-2 text-center">DayP&L</th>
                                <th className="py-0.5 p-2 text-center">Re Trade</th>
                            </tr>
                        </thead>

                        <tbody className="text-xs">
                            {positions.length > 0 ? (
                        positions.map((pos, idx) => {
                            const positionRowId = `pos-${idx}`;
                            const isPositionHovered = hoveredPosition === positionRowId;
                            
                            return (
                                <tr 
                                    key={idx} 
                                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors h-[20px] relative group"
                                    onMouseEnter={() => setHoveredPosition(positionRowId)}
                                    onMouseLeave={() => setHoveredPosition(null)}
                                >
                                    <td className="py-0.5 p-2 text-left text-gray-800">{pos.type}</td>
                                    <td className="py-0.5 p-2 text-left relative">
                                        <div className="text-gray-800">{pos.instrument}</div>
                                        {/* Exit Button on Hover */}
                                        {isPositionHovered && Math.abs(parseInt(pos.qty)) > 0 && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleExitPosition(pos);
                                                }}
                                                className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-red-600 hover:bg-red-700 text-white text-[8px] rounded px-1 py-0.5 shadow-lg transition-all duration-200 z-10"
                                                title={`Exit position: ${Math.abs(parseInt(pos.qty))} qty`}
                                            >
                                                EX
                                            </button>
                                        )}
                                    </td>
                                    <td className={`py-0.5 p-2 text-center font-semibold ${pos.qty >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {pos.qty}
                                    </td>
                                    <td className="py-0.5 p-2 text-center">{pos.buyAvg}</td>
                                    <td className="py-0.5 p-2 text-center">{pos.sellAvg}</td>
                                    <td className="py-0.5 p-2 text-center">
                                        <span className={`font-bold ${
                                            pos.rawData && livePrices[`${pos.rawData.exchange}|${pos.rawData.token || pos.rawData.instrumentId}`]?.ltp 
                                            ? 'text-gray-600' 
                                            : 'text-gray-600'
                                        }`}>
                                            {pos.ltp}
                                        </span>
                                    </td>
                                    <td className={`py-0.5 p-2 text-center font-bold ${parseFloat(pos.dayPnl) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {pos.dayPnl}
                                    </td>
                                    <td className="py-0.5 p-2 text-center">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // Find lot size from watchlist based on trading symbol
                                                const tradingSymbol = pos.rawData?.tradingSymbol || pos.rawData?.tradingsymbol;
                                                const watchlistItem = watchlist.find(item => 
                                                  item.tradingSymbol === tradingSymbol && 
                                                  item.exchange === pos.rawData?.exchange
                                                );
                                                const lotSize = watchlistItem?.lotSize || pos.rawData?.lotSize || pos.rawData?.lot_size || 25;
                                                
                                                setReTradeModal({
                                                    show: true,
                                                    position: pos,
                                                    action: 'BUY',
                                                    editablePrice: parseFloat(pos.ltp) || 0,
                                                    editableQty: lotSize
                                                });
                                            }}
                                            className="bg-blue-500 hover:bg-blue-600 text-white text-[10px] font-semibold rounded px-2 py-0.5 transition-colors"
                                            title="Re-trade this instrument"
                                        >
                                            Re Trade
                                        </button>
                                    </td>
                                </tr>
                            );
                        })
                            ) : (
                                <tr>
                                    <td colSpan="8" className="p-4 text-center text-gray-500">
                                        No positions yet
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </>
                ) : (
                    // Trade Book Table
                    <>
                        <thead className="bg-gradient-to-r indigo-50">
                            <tr className="font-semibold text-gray-700 text-sm h-[22px]">
                                <th className="py-0.5 p-2 text-left">Time</th>
                                <th className="py-0.5 p-2 text-left">Exchange</th>
                                <th className="py-0.5 p-2 text-left">Trading Symbol</th>
                                <th className="py-0.5 p-2 text-center">Order Type</th>
                                <th className="py-0.5 p-2 text-center">Qty</th>
                                <th className="py-0.5 p-2 text-right">Traded Price</th>
                            </tr>
                        </thead>

                        <tbody className="text-xs">
                            {tradeBook && tradeBook.length > 0 ? (
                                tradeBook.map((trade, idx) => {
                                    const displaySymbol = trade.formattedInstrumentName || trade.tradingSymbol || '-';
                                    return (
                                        <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition-colors h-[20px]">
                                            <td className="py-0.5 p-2 text-left whitespace-nowrap text-gray-800">{trade.fillTimestamp || '-'}</td>
                                            <td className="py-0.5 p-2 text-left font-semibold text-gray-800">{trade.exchange || '-'}</td>
                                            <td className="py-0.5 p-2 text-left text-gray-800">{displaySymbol}</td>
                                            <td className={`py-0.5 p-2 text-center font-semibold ${
                                                trade.transactionType === 'BUY' ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                                {trade.transactionType || '-'}
                                            </td>
                                            <td className="py-0.5 p-2 text-center text-gray-800">{trade.filledQuantity || '-'}</td>
                                            <td className="py-0.5 p-2 text-right font-medium text-gray-800">₹{trade.tradedPrice || '0.00'}</td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="6" className="p-4 text-center text-gray-500">
                                        No trades executed today
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </>
                )}
            </table>
        </div>
    </div>
</div>

            </div>
          </div>
        ) : (
          // Regular table for other exchanges
          filteredWatchlist.length > 0 ? (
            <div className="overflow-x-auto rounded-lg shadow-md border border-gray-200">
              <table className="table-auto w-full">
                <thead className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
                  <tr>
                    <th className="py-2 px-4 text-left font-semibold">Symbol</th>
                    <th className="py-2 px-4 text-left font-semibold">Exchange</th>
                    <th className="py-2 px-4 text-left font-semibold">LTP</th>
                    <th className="py-2 px-4 text-left font-semibold">Change %</th>
                    <th className="py-2 px-4 text-left font-semibold">Volume</th>
                    <th className="py-2 px-4 text-left font-semibold">High</th>
                    <th className="py-2 px-4 text-left font-semibold">Low</th>
                    <th className="py-2 px-4 text-left font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWatchlist.map((item, idx) => {
                    const ltp = getLivePrice(item.exchange, item.instrumentId);
                    const change = getPriceChange(item.exchange, item.instrumentId);
                    const priceKey = `${item.exchange}|${item.instrumentId}`;
                    const priceData = livePrices[priceKey];
                    
                    return (
                      <tr key={idx} className="hover:bg-blue-50 transition-colors border-b border-gray-100">
                        <td className="font-semibold py-3 px-4 text-gray-800">{item.tradingSymbol}</td>
                        <td className="py-3 px-4">
                          <span className="px-2.5 py-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-md text-xs font-semibold">
                            {item.exchange}
                          </span>
                        </td>
                        <td className={`text-base font-bold py-3 px-4 ${ltp !== null ? 'text-blue-600' : 'text-gray-400'}`}>
                          {ltp !== null ? `${ltp.toFixed(2)}` : '—'}
                        </td>
                        <td className={change !== null && parseFloat(change) >= 0 ? 'text-success font-semibold' : change !== null ? 'text-danger font-semibold' : 'text-gray-400'}>
                          {change !== null ? `${parseFloat(change) > 0 ? '' : ''}${parseFloat(change).toFixed(2)}%` : '—'}
                        </td>
                        <td className="text-gray-600">
                          {priceData?.v ? parseInt(priceData.v).toLocaleString() : '—'}
                        </td>
                        <td className="text-green-600 font-medium">
                          {priceData?.h && priceData.h > 0 ? `${parseFloat(priceData.h).toFixed(2)}` : '—'}
                        </td>
                        <td className="text-red-600 font-medium">
                          {priceData?.l && priceData.l > 0 ? `${parseFloat(priceData.l).toFixed(2)}` : '—'}
                        </td>
                        <td>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleBuySell(item, 'BUY')}
                              className="btn btn-success text-sm px-3 py-1"
                            >
                              Buy
                            </button>
                            <button
                              onClick={() => handleBuySell(item, 'SELL')}
                              className="btn btn-danger text-sm px-3 py-1"
                            >
                              Sell
                            </button>
                            <button
                              onClick={() => handleRemoveFromWatchlist(item)}
                              className="btn btn-secondary text-sm px-3 py-1"
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              {watchlist.length === 0 ? (
                <p className="text-gray-500">
                  No symbols in your trading view. Search and add symbols to start trading.
                </p>
              ) : (
                <p className="text-gray-500">
                  No symbols in {activeTab} exchange. Switch to another tab or add {activeTab} symbols.
                </p>
              )}
            </div>
          )
        )}

        {/* MCX Option Chain Display */}
        {activeTab === 'MCX' && mcxChainVisible && (
          <div className="flex gap-4 mt-3">
            {/* Left: Option Chain Table (40%) */}
            <div style={{width: '40%'}} className="flex-none">
              <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <div className="bg-yellow-50 border-b px-2 py-1 flex items-center justify-between">
                  <span className="text-xs font-semibold text-yellow-800">
                    MCX: {mcxSelectedUnderlying} | {mcxSelectedExpiry}
                  </span>
                  {mcxLoading && <span className="text-[10px] text-gray-500">Refreshing...</span>}
                </div>
                {mcxError && (
                  <div className="p-2 text-xs text-red-600">{mcxError}</div>
                )}
                {mcxOptionChainData.length > 0 ? (
                  <div className="overflow-x-auto overflow-y-auto" style={{maxHeight: 'calc(100vh - 300px)'}}>
                    <table className="w-full">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="px-1 py-1 text-center border-r border-gray-400" colSpan="5">
                            <span className="text-green-600 font-bold text-[10px]">CALL</span>
                          </th>
                          <th className="px-1 py-1 text-center bg-blue-100 font-bold">
                            <span className="text-blue-600 text-[10px]">STRIKE</span>
                          </th>
                          <th className="px-1 py-1 text-center border-l border-gray-400" colSpan="5">
                            <span className="text-red-600 font-bold text-[10px]">PUT</span>
                          </th>
                        </tr>
                        <tr className="text-[9px] text-gray-500">
                          <th className="px-1 py-0.5 text-center">OI</th>
                          <th className="px-1 py-0.5 text-center">ΔOI</th>
                          <th className="px-1 py-0.5 text-center">LTP</th>
                          <th className="px-1 py-0.5 text-center">Δ</th>
                          <th className="px-1 py-0.5 text-center border-r border-gray-400">B/S</th>
                          <th className="px-1 py-0.5 text-center bg-blue-50"></th>
                          <th className="px-1 py-0.5 text-center border-l border-gray-400">B/S</th>
                          <th className="px-1 py-0.5 text-center">Δ</th>
                          <th className="px-1 py-0.5 text-center">LTP</th>
                          <th className="px-1 py-0.5 text-center">ΔOI</th>
                          <th className="px-1 py-0.5 text-center">OI</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mcxOptionChainData.map((item, index) => {
                          const strikePrice = getMcxStrikePrice(item);
                          const atmStrike = getMcxAtmStrike();
                          const isATM = strikePrice === atmStrike;
                          const ceLTP = item.CE ? (getLivePrice('MCX', item.CE.token) || parseFloat(item.CE.ltp) || 0) : 0;
                          const peLTP = item.PE ? (getLivePrice('MCX', item.PE.token) || parseFloat(item.PE.ltp) || 0) : 0;
                          const ceChange = item.CE ? (ceLTP - parseFloat(item.CE.pdc || 0)) : 0;
                          const peChange = item.PE ? (peLTP - parseFloat(item.PE.pdc || 0)) : 0;
                          return (
                            <tr key={`${item.CE?.token||''}-${item.PE?.token||''}-${index}`}
                                className={`border-t border-gray-200 hover:bg-gray-50 ${isATM ? 'bg-yellow-50' : ''}`}>
                              <td className="px-1 py-0.5 text-center text-[9px]">{mcxFormatOI(item.CE?.oi)}</td>
                              <td className={`px-1 py-0.5 text-center text-[9px] ${parseFloat(item.CE?.oi||0)-parseFloat(item.CE?.pdoi||0)>0?'text-green-600':'text-red-600'}`}>
                                {mcxFormatOI(parseFloat(item.CE?.oi||0)-parseFloat(item.CE?.pdoi||0))}
                              </td>
                              <td className="px-1 py-0.5 text-center text-[10px] font-semibold">{mcxFormatNumber(ceLTP)}</td>
                              <td className={`px-1 py-0.5 text-center text-[9px] ${ceChange>0?'text-green-600':ceChange<0?'text-red-600':''}`}>
                                {ceChange>0?'+':''}{mcxFormatNumber(ceChange)}
                              </td>
                              <td className="px-1 py-0.5 text-center border-r border-gray-400">
                                {item.CE && (
                                  <div className="flex gap-0.5 justify-center">
                                    <button onClick={() => { setSelectedSymbol({symbol:item.CE.tradingsymbol,name:item.CE.forInsName||item.CE.tradingsymbol,exchange:'MCX',instrumentId:item.CE.token,ltp:ceLTP,orderType:'BUY'}); setIsOrderPanelOpen(true); }} className="px-1 py-0.5 text-[8px] bg-green-600 text-white rounded hover:bg-green-700">B</button>
                                    <button onClick={() => { setSelectedSymbol({symbol:item.CE.tradingsymbol,name:item.CE.forInsName||item.CE.tradingsymbol,exchange:'MCX',instrumentId:item.CE.token,ltp:ceLTP,orderType:'SELL'}); setIsOrderPanelOpen(true); }} className="px-1 py-0.5 text-[8px] bg-red-600 text-white rounded hover:bg-red-700">S</button>
                                  </div>
                                )}
                              </td>
                              <td className={`px-1 py-0.5 text-center font-bold text-[10px] bg-blue-50 ${isATM?'text-yellow-600':'text-blue-600'}`}>
                                {strikePrice ? mcxFormatNumber(strikePrice, 0) : '-'}
                                {isATM && <span className="text-[8px] ml-0.5">ATM</span>}
                              </td>
                              <td className="px-1 py-0.5 text-center border-l border-gray-400">
                                {item.PE && (
                                  <div className="flex gap-0.5 justify-center">
                                    <button onClick={() => { setSelectedSymbol({symbol:item.PE.tradingsymbol,name:item.PE.forInsName||item.PE.tradingsymbol,exchange:'MCX',instrumentId:item.PE.token,ltp:peLTP,orderType:'BUY'}); setIsOrderPanelOpen(true); }} className="px-1 py-0.5 text-[8px] bg-green-600 text-white rounded hover:bg-green-700">B</button>
                                    <button onClick={() => { setSelectedSymbol({symbol:item.PE.tradingsymbol,name:item.PE.forInsName||item.PE.tradingsymbol,exchange:'MCX',instrumentId:item.PE.token,ltp:peLTP,orderType:'SELL'}); setIsOrderPanelOpen(true); }} className="px-1 py-0.5 text-[8px] bg-red-600 text-white rounded hover:bg-red-700">S</button>
                                  </div>
                                )}
                              </td>
                              <td className={`px-1 py-0.5 text-center text-[9px] ${peChange>0?'text-green-600':peChange<0?'text-red-600':''}`}>
                                {peChange>0?'+':''}{mcxFormatNumber(peChange)}
                              </td>
                              <td className="px-1 py-0.5 text-center text-[10px] font-semibold">{mcxFormatNumber(peLTP)}</td>
                              <td className={`px-1 py-0.5 text-center text-[9px] ${parseFloat(item.PE?.oi||0)-parseFloat(item.PE?.pdoi||0)>0?'text-green-600':'text-red-600'}`}>
                                {mcxFormatOI(parseFloat(item.PE?.oi||0)-parseFloat(item.PE?.pdoi||0))}
                              </td>
                              <td className="px-1 py-0.5 text-center text-[9px]">{mcxFormatOI(item.PE?.oi)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-4 text-center text-xs text-gray-500">No chain data</div>
                )}
              </div>
            </div>
            {/* Right: 60% empty space for custom tables */}
            <div className="flex-1" style={{width: '60%'}}>
              {/* Reserved for custom tables */}
            </div>
          </div>
        )}
      </div>

      {/* Advanced Confirmation Modal */}
      {confirmationModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 max-w-[280px] w-full mx-2 transform transition-all duration-300 scale-100 overflow-hidden">
            
            {/* Modal Header */}
            <div className={`px-4 py-3 ${
              confirmationModal.title.includes('BUY') 
                ? 'bg-gradient-to-r from-green-600 to-green-700' 
                : 'bg-gradient-to-r from-red-600 to-red-700'
            }`}>
              <div className="flex items-center justify-between">
                <h3 className="text-base font-extrabold text-white flex items-center gap-2 uppercase tracking-wide">
                  {confirmationModal.title.includes('BUY') ? '📈' : '📉'}
                  {confirmationModal.title.includes('BUY') ? 'Confirm Buy' : 'Confirm Sell'}
                </h3>
                <button
                  onClick={confirmationModal.onCancel}
                  className="text-white opacity-80 hover:opacity-100 transition-opacity p-1 rounded-full hover:bg-white/10"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-3 space-y-4">
              <p className="text-gray-700 text-sm font-medium text-center">{confirmationModal.message}</p>
              
           
              {/* Order Details Grid */}
              <div className="bg-gray-100 rounded-lg p-2.5 space-y-1 text-xs border border-gray-200">
                
                {/* Strike & Option Type */}
                <div className="flex justify-between items-center px-1 py-[3px]">
                  <span className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1">
                    📊 INSTRUMENT:
                  </span>
                  <span className="font-bold text-lg text-gray-900">{confirmationModal.details.strike} {confirmationModal.details.optionType}</span>
                </div>
                
                {/* LTP & Order Type */}
                <div className="flex justify-between border-t border-gray-200 pt-1 mt-1">
                  <div className="flex justify-between items-center px-1 py-[3px] flex-1">
                    <span className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1">
                      📈 LTP:
                    </span>
                    <span className="font-bold text-sm text-blue-600">{confirmationModal.details.ltp}</span>
                  </div>
                  <div className="flex justify-between items-center px-1 py-[3px] flex-1">
                    <span className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1">
                    Order:
                    </span>
                    <span className={`font-bold text-sm ${confirmationModal.details.orderType === 'MARKET'}`}>
                      {confirmationModal.details.orderType}
                    </span>
                  </div>
                </div>
                
                {/* Price Input for LIMIT orders */}
                {confirmationModal.details.orderType === 'LIMIT' && (
                  <div className="border-t border-gray-200 pt-2 mt-1">
                    <label className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1 mb-1">
                      💰 Limit Price:
                    </label>
                    <input
                      type="number"
                      step="0.05"
                      defaultValue={typeof confirmationModal.editablePrice === 'number' ? confirmationModal.editablePrice.toFixed(2) : '0.00'}
                      onChange={(e) => {
                        setConfirmationModal(prev => ({
                          ...prev,
                          editablePrice: parseFloat(e.target.value) || 0
                        }));
                      }}
                      className="w-full px-2 py-1 border border-blue-300 rounded text-center text-sm font-bold text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  </div>
                )}
                
                {/* Quantity Row with Source Tag */}
                 <div className="flex justify-between border-t border-gray-200 pt-1 mt-1">
                  <div className="flex justify-between items-center px-1 py-[3px] flex-1">
                    <span className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1">
                      QTY:</span>
                      <span className="font-bold text-sm text-blue-600">
                   {confirmationModal.details.quantity.replace('(From Quick Order Entry)', '')}</span>
                      </div>
                  <div className="flex justify-between items-center px-1 py-[3px] flex-1">
                    <span className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1">
                      Product:
                    </span>
                    <span className={`font-bold text-sm ${
                      confirmationModal.details.product === 'MIS'
                    }`}>
                      {confirmationModal.details.product}
                  
                  </span>
                  </div>
                </div>
                </div>

            </div>

            {/* Modal Footer (Actions) */}
            <div className="px-4 py-3 bg-gray-50 flex gap-2 justify-between border-t border-gray-200">
                 <button
                onClick={() => confirmationModal.onConfirm(confirmationModal.editablePrice)}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-bold text-white transition-colors ${
                  confirmationModal.title.includes('BUY')
                    ? 'bg-green-600 hover:bg-green-700 shadow-lg shadow-green-300/50'
                    : 'bg-red-600 hover:bg-red-700 shadow-lg shadow-red-300/50'
                }`}
              >
                <span className="flex items-center justify-center gap-1">
                  ✓ Confirm
                </span>
              </button>
              <button
                onClick={confirmationModal.onCancel}
                className="flex-1 px-3 py-2 bg-gray-300 text-gray-800 rounded-lg text-sm font-bold hover:bg-gray-400 transition-colors shadow-sm"
              >
                <span className="flex items-center justify-center gap-1">
                  ✕ Cancel
                </span>
              </button>
           
            </div>
          </div>
        </div>
      )}

      {/* Re Trade Modal */}
      {reTradeModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 max-w-[280px] w-full mx-2 transform transition-all duration-300 scale-100 overflow-hidden">
            
            {/* Modal Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-extrabold text-white flex items-center gap-2 uppercase tracking-wide">
                  🔄 Re Trade
                </h3>
                <button
                  onClick={() => setReTradeModal({ show: false, position: null, action: 'BUY', editablePrice: null, editableQty: null })}
                  className="text-white opacity-80 hover:opacity-100 transition-opacity p-1 rounded-full hover:bg-white/10"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-3 space-y-4">
              <span className="font-bold text-base text-gray-900">
                    {formatInstrumentName(reTradeModal.position?.instrument)}
                  </span>
              {/* Instrument Details */}
              <div className="bg-gray-100 rounded-lg p-2.5 space-y-1 text-xs border border-gray-200">
   
                {/* LTP & Qty - Editable */}
                <div className="flex justify-between border-t border-gray-200 pt-1 mt-1">
                  <div className="flex flex-col px-1 py-[3px] flex-1">
                    <span className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1 mb-1">
                      📈 LTP:
                    </span>
                    <span className="font-bold text-sm text-blue-600">
                      {reTradeModal.position?.ltp || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col px-1 py-[3px] flex-1">
                    <span className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1 mb-1">
                      QTY:
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          const tradingSymbol = reTradeModal.position?.rawData?.tradingSymbol || reTradeModal.position?.rawData?.tradingsymbol;
                          const watchlistItem = watchlist.find(item => 
                            item.tradingSymbol === tradingSymbol && 
                            item.exchange === reTradeModal.position?.rawData?.exchange
                          );
                          const lotSize = watchlistItem?.lotSize || reTradeModal.position?.rawData?.lotSize || reTradeModal.position?.rawData?.lot_size || 25;
                          const currentQty = reTradeModal.editableQty || lotSize;
                          const newQty = Math.max(lotSize, currentQty - lotSize);
                          setReTradeModal(prev => ({
                            ...prev,
                            editableQty: newQty
                          }));
                        }}
                        className="bg-gray-500 hover:bg-gray-600 text-white text-xs font-bold px-2 py-1 rounded"
                        title="Decrease by lot size"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        value={reTradeModal.editableQty || ''}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || 0;
                          setReTradeModal(prev => ({
                            ...prev,
                            editableQty: value
                          }));
                        }}
                        className="w-full px-2 py-1 border border-blue-300 rounded text-center text-xs font-bold text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        min="0"
                      />
                      <button
                        onClick={() => {
                          const tradingSymbol = reTradeModal.position?.rawData?.tradingSymbol || reTradeModal.position?.rawData?.tradingsymbol;
                          const watchlistItem = watchlist.find(item => 
                            item.tradingSymbol === tradingSymbol && 
                            item.exchange === reTradeModal.position?.rawData?.exchange
                          );
                          const lotSize = watchlistItem?.lotSize || reTradeModal.position?.rawData?.lotSize || reTradeModal.position?.rawData?.lot_size || 25;
                          const currentQty = reTradeModal.editableQty || 0;
                          const newQty = currentQty + lotSize;
                          setReTradeModal(prev => ({
                            ...prev,
                            editableQty: newQty
                          }));
                        }}
                        className="bg-gray-500 hover:bg-gray-600 text-white text-xs font-bold px-2 py-1 rounded"
                        title="Increase by lot size"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Price Input for LIMIT orders */}
                {orderType === 'LIMIT' && (
                  <div className="border-t border-gray-200 pt-2 mt-1">
                    <label className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1 mb-1">
                      💰 Limit Price:
                    </label>
                    <input
                      type="number"
                      step="0.05"
                      value={typeof reTradeModal.editablePrice === 'number' ? reTradeModal.editablePrice.toFixed(2) : '0.00'}
                      onChange={(e) => {
                        setReTradeModal(prev => ({
                          ...prev,
                          editablePrice: parseFloat(e.target.value) || 0
                        }));
                      }}
                      className="w-full px-2 py-1 border border-blue-300 rounded text-center text-sm font-bold text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  </div>
                )}

                {/* Order Type & Product */}
                <div className="flex justify-between border-t border-gray-200 pt-1 mt-1">
                  <div className="flex justify-between items-center px-1 py-[3px] flex-1">
                    <span className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1">
                      Order:
                    </span>
                    <span className="font-bold text-sm text-gray-800">
                      {orderType}
                    </span>
                  </div>
                  <div className="flex justify-between items-center px-1 py-[3px] flex-1">
                    <span className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1">
                      Product:
                    </span>
                    <span className="font-bold text-sm text-gray-800">
                      {productType}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer (Buy/Sell Actions) */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
              <div className="flex gap-2 justify-between mb-2">
                <button
                  onClick={() => handleReTrade('BUY')}
                  className="flex-1 px-3 py-2 rounded-lg text-sm font-bold text-white transition-colors bg-green-600 hover:bg-green-700 shadow-lg shadow-green-300/50"
                >
                  <span className="flex items-center justify-center gap-1">
                    📈 BUY
                  </span>
                </button>
                <button
                  onClick={() => handleReTrade('SELL')}
                  className="flex-1 px-3 py-2 rounded-lg text-sm font-bold text-white transition-colors bg-red-600 hover:bg-red-700 shadow-lg shadow-red-300/50"
                >
                  <span className="flex items-center justify-center gap-1">
                    📉 SELL
                  </span>
                </button>
              </div>
              <button
                onClick={() => setReTradeModal({ show: false, position: null, action: 'BUY', editablePrice: null, editableQty: null })}
                className="w-full px-3 py-2 rounded-lg text-sm font-bold text-gray-700 transition-colors bg-gray-300 hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quantity Popup Modal */}
      {qtyPopup.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-80 p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">
              Adjust Quantity
            </h3>
            
            {/* Display Strike and Option Info */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600">
                <p className="font-semibold text-gray-800">{qtyPopup.item?.tradingSymbol || ''}</p>
                <p className="text-xs mt-1">
                  Strike: <span className="font-semibold">{qtyPopup.item?.strike}</span> | 
                  LTP: <span className="font-semibold">₹{qtyPopup.item?.ltp?.toFixed(2) || '0.00'}</span>
                </p>
              </div>
            </div>

            {/* Quantity Controls */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-3 text-center">Quantity</label>
              <div className="flex items-stretch justify-center gap-2">
                <button
                  onClick={() => {
                    const lotSize = qtyPopup.item?.lotSize || 25;
                    const currentQty = parseInt(qtyPopup.currentQty) || 0;
                    const newQty = Math.max(0, currentQty - lotSize);
                    setQtyPopup({ ...qtyPopup, currentQty: newQty.toString() });
                  }}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-500 hover:bg-red-600 text-white font-bold text-xl transition-colors shadow-md flex-shrink-0"
                >
                  −
                </button>
                <div className="flex-1 flex flex-col justify-center">
                  <input
                    type="number"
                    value={qtyPopup.currentQty}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || parseInt(value) >= 0) {
                        setQtyPopup({ ...qtyPopup, currentQty: value });
                      }
                    }}
                    className="w-full text-center text-xl font-bold text-gray-800 border-2 border-gray-300 rounded-lg h-10 focus:border-blue-500 focus:outline-none"
                    min="0"
                  />
                </div>
                <button
                  onClick={() => {
                    const lotSize = qtyPopup.item?.lotSize || 25;
                    const currentQty = parseInt(qtyPopup.currentQty) || 0;
                    const newQty = currentQty + lotSize;
                    setQtyPopup({ ...qtyPopup, currentQty: newQty.toString() });
                  }}
                  className="w-10 h-10 flex items-center justify-center rounded-lg bg-green-500 hover:bg-green-600 text-white font-bold text-xl transition-colors shadow-md flex-shrink-0"
                >
                  +
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Lot Size: {qtyPopup.item?.lotSize || 25}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  if (qtyPopup.optionType === 'CE') {
                    setSelectedCEStrike(qtyPopup.item);
                    setCEEntryPrice(qtyPopup.item?.ltp || 0);
                    setCEQty(qtyPopup.currentQty);
                  } else if (qtyPopup.optionType === 'PE') {
                    setSelectedPEStrike(qtyPopup.item);
                    setPEEntryPrice(qtyPopup.item?.ltp || 0);
                    setPEQty(qtyPopup.currentQty);
                  }
                  setQtyPopup({ show: false, optionType: '', item: null, defaultQty: '', currentQty: '' });
                }}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-bold text-white transition-colors bg-blue-600 hover:bg-blue-700 shadow-md"
              >
                Confirm
              </button>
              <button
                onClick={() => setQtyPopup({ show: false, optionType: '', item: null, defaultQty: '', currentQty: '' })}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm font-bold text-gray-700 transition-colors bg-gray-300 hover:bg-gray-400 shadow-md"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingView;
